/**
 * Ocean Scene
 *
 * Main gameplay scene where players sail ships, engage in combat,
 * and interact with the world.
 *
 * Uses a grid-based visual system consistent with the world map.
 */

import Phaser from "phaser";
import { Socket } from "socket.io-client";
import { IslandRenderer } from "../systems/IslandRenderer";
import { PermanentLocationRenderer } from "../systems/PermanentLocationRenderer";
import {
  FishingSpotRenderer,
  FishingSpotInfo,
} from "../systems/FishingSpotRenderer";
import {
  OceanTileRenderer,
  ResourceNodeInfo,
} from "../systems/OceanTileRenderer";
import { ShipRenderer, ShipSprite } from "../systems/ShipRenderer";
import { CombatSystem, CannonEffectData } from "../systems/CombatSystem";
import { ShopUI, ShopData } from "../systems/ShopUI";
import { InputHandler } from "../systems/InputHandler";
import {
  WhirlpoolRenderer,
  WhirlpoolEntryData,
} from "../systems/WhirlpoolRenderer";
import { OceanWaterRenderer } from "../systems/OceanWaterRenderer";
import { SeaMonsterRenderer } from "../systems/SeaMonsterRenderer";
import { PlacedTileRenderer } from "../systems/PlacedTileRenderer";
import { SignatureMoveEffectsRenderer } from "../systems/SignatureMoveEffectsRenderer";
import { RiftScarManager } from "../managers/RiftScarManager";
import { InputInteractionManager } from "../managers/InputInteractionManager";
import { SocketHandlerManager } from "../managers/SocketHandlerManager";
import { CombatManager } from "../managers/CombatManager";
import { FishingManager } from "../managers/FishingManager";
import { InstanceManager } from "../managers/InstanceManager";
import { SanctumRenderer } from "../systems/SanctumRenderer";
import { TilesetManager } from "../managers/TilesetManager";
import { PixelRainRenderer } from "../systems/PixelRainRenderer";
// TODO: Integrate ASCII weather overlay and regional color palettes in OceanScene.create() and update()
// import { ASCIIWeatherOverlay, WeatherType } from '../systems/ASCIIWeatherOverlay';
// import { getRegionalPalette, applyPaletteToScene } from '../systems/RegionalColorPalettes';
import {
  TILE_SIZE,
  CHUNK_SIZE,
  UNCHARTED_WATERS_BUFFER_PX,
  WORLD_WIDTH,
  WORLD_HEIGHT,
  COLORS,
  NPC_BEHAVIOR_COLORS,
  AMBIENT_NPC_COLORS,
  IslandData,
  ShipData,
  NPCData,
  FishingSpotData,
  TradeRouteData,
} from "../types";
import { drawAggroRange } from "../utils/RadiusRenderer";
import {
  SoundManager,
  SFX_KEYS,
  initGlobalSoundManager,
  playSound,
  playSprintStartSound,
  startBackgroundMusic,
} from "../systems/SoundManager";
import {
  ResourceNodeRenderer,
  RESOURCE_NODE_SPRITES,
} from "../utils/ResourceNodeRenderer";
import {
  HITBOX_MULTIPLIER,
  AMBIENT_NPC_HITBOX_RADIUS,
  RESOURCE_NODE_HITBOX_SIZE,
  SHIPWRECK_HITBOX_WIDTH,
  SHIPWRECK_HITBOX_HEIGHT,
  DEBRIS_HITBOX_SIZE,
  LOOT_CONTAINER_HITBOX_SIZE,
  HITBOX_DEBUG_COLORS,
} from "../constants/hitbox";

// ============ INTERFACES ============
// ShipData, ShipSprite, NPC colors imported from ../types and ../systems/ShipRenderer
// Boarding UI removed
// ShopItem, ShopData imported from ../systems/ShopUI

// ============ SCENE ============

export class OceanScene extends Phaser.Scene {
  private socket: Socket | null = null;
  private localPlayerId: string = "";
  private localCharacterId: string = "";

  // Game objects
  private ships: Map<string, ShipSprite> = new Map();
  private npcs: Map<string, Phaser.GameObjects.Container> = new Map();
  private localShip: ShipSprite | null = null;
  private selectedTarget: string | null = null;

  // Optimistic movement tracking
  private optimisticMoveTarget: { x: number; y: number } | null = null;
  private lastServerUpdateTime: number = 0;

  // Systems (modular components)
  private islandRenderer!: IslandRenderer;
  private permanentLocationRenderer!: PermanentLocationRenderer;
  private fishingSpotRenderer!: FishingSpotRenderer;
  private shipRenderer!: ShipRenderer;
  private combatSystem!: CombatSystem;
  private shopUISystem!: ShopUI;
  private inputHandler!: InputHandler;
  private cannonFocusMultiplier: number = 1.0;
  private whirlpoolRenderer!: WhirlpoolRenderer;
  private oceanWaterRenderer!: OceanWaterRenderer;
  private seaMonsterRenderer!: SeaMonsterRenderer;
  private placedTileRenderer!: PlacedTileRenderer;
  private signatureMoveEffectsRenderer!: SignatureMoveEffectsRenderer;
  private oceanTileRenderer!: OceanTileRenderer;
  private riftScarManager!: RiftScarManager;
  private inputInteractionManager!: InputInteractionManager;
  private socketHandlerManager!: SocketHandlerManager;
  private combatManager!: CombatManager;
  private fishingManager!: FishingManager;
  private sanctumRenderer!: SanctumRenderer;
  private instanceManager!: InstanceManager;
  private tilesetManager!: TilesetManager;
  // private asciiWeatherOverlay!: ASCIIWeatherOverlay;
  // private currentRegion: string = '';
  private isInWhirlpoolInstance: boolean = false;
  private isInRiftInstance: boolean = false;
  private isInSanctumInstance: boolean = false;
  private currentSanctumInstanceId: string | null = null;
  private currentRiftId: string = "core";
  private inputSuppressedUntilMs: number = 0;
  private currentFishingSpotType: string = "shallow_waters";
  private currentFishingSpotId: string = "";

  // UI Elements
  private hudContainer!: Phaser.GameObjects.Container;
  private targetInfo: Phaser.GameObjects.Container | null = null;
  private moveIndicator: Phaser.GameObjects.Rectangle | null = null;
  private pathGraphics: Phaser.GameObjects.Graphics | null = null;
  private combatPathGraphics: Phaser.GameObjects.Graphics | null = null;
  private destinationIndicator:
    | Phaser.GameObjects.Container
    | Phaser.GameObjects.Rectangle
    | null = null;
  private combatDestinationIndicator:
    | Phaser.GameObjects.Container
    | Phaser.GameObjects.Rectangle
    | null = null;
  private combatTrackingLine: Phaser.GameObjects.Graphics | null = null;
  // shipTooltip is now managed by ShipRenderer
  // shopUI is now managed by ShopUI system
  private gameOverUI: Phaser.GameObjects.Container | null = null;
  private reloadBar: Phaser.GameObjects.Graphics | null = null;
  private lastFireTimestamp: number = 0;
  private collisionDebugEnabled: boolean = false;
  private collisionDebugGraphics: Phaser.GameObjects.Graphics | null = null;
  private collisionDebugTimer: Phaser.Time.TimerEvent | null = null;
  private hitboxDebugEnabled: boolean = false;
  private hitboxDebugGraphics: Phaser.GameObjects.Graphics | null = null;
  private lastPathFailedIndicatorAtMs: number = 0;
  private lastPathFailedIndicatorKey: string = "";
  private activePathFailedIndicator: {
    indicator: Phaser.GameObjects.Rectangle;
    graphics: Phaser.GameObjects.Graphics;
    text: Phaser.GameObjects.Text;
  } | null = null;
  private floatingMessageText: Phaser.GameObjects.Text | null = null;
  private floatingMessageBg: Phaser.GameObjects.Rectangle | null = null;
  private floatingMessageTween: Phaser.Tweens.Tween | null = null;
  private lastFloatingMessageAtMs: number = 0;
  private lastFloatingMessageKey: string = "";

  private lastPathFailedTimeMs: number = 0;

  // FPS counter
  private fpsText: Phaser.GameObjects.Text | null = null;

  // State
  // currentShopLocation and shopData are now managed by ShopUI system
  private isPlayerDead: boolean = false;
  // tradingPosts is now a getter that delegates to permanentLocationRenderer.getTradingPosts()

  // Fishing state
  private fishingSpots: Map<string, FishingSpotInfo> = new Map();
  private isFishing: boolean = false;
  private fishingStatusUI: Phaser.GameObjects.Container | null = null;
  private fishingMinigameActive: boolean = false;

  // Cannon focus state
  private cannonFocusActive: boolean = false;

  // Sprint state
  private isSprinting: boolean = false;

  // WASD movement state
  private wasdDirections: {
    up: boolean;
    down: boolean;
    left: boolean;
    right: boolean;
  } = {
    up: false,
    down: false,
    left: false,
    right: false,
  };
  private wasdMovementTimer: Phaser.Time.TimerEvent | null = null;
  private isWASDMoving: boolean = false;

  // Treasure system
  private treasureMarkers: Map<string, Phaser.GameObjects.Container> =
    new Map();
  private activeTreasure: {
    id: string;
    x: number;
    y: number;
    reward: number;
    expiresAt: number;
  } | null = null;

  // Loot container (wreck) system
  private lootContainerMarkers: Map<string, Phaser.GameObjects.Container> =
    new Map();

  // Message in a bottle system
  private floatingBottles: Map<string, Phaser.GameObjects.Container> =
    new Map();

  // Weather system
  private currentWeather: "clear" | "fog" | "storm" | "calm" = "clear";
  private weatherOverlay: Phaser.GameObjects.Graphics | null = null;
  private weatherParticles: Phaser.GameObjects.Particles.ParticleEmitter | null =
    null;
  private weatherTimer: Phaser.Time.TimerEvent | null = null;
  private pixelRainRenderer: PixelRainRenderer | null = null;

  // Rift Storm system
  private riftStorms: Map<
    string,
    {
      id: string;
      x: number;
      y: number;
      radius: number;
      tier: string;
      stage?: "active" | "dissipating";
      expiresAt: number;
      dissipatesAt?: number | null;
      color: string;
      graphics: Phaser.GameObjects.Graphics;
      label: Phaser.GameObjects.Text;
      clouds: Phaser.GameObjects.Graphics;
      lightning: Phaser.GameObjects.Graphics;
      portalOverlay: Phaser.GameObjects.Graphics;
      spiralGraphics: Phaser.GameObjects.Graphics;
      particles: Phaser.GameObjects.Graphics;
      lightningTimer: Phaser.Time.TimerEvent;
      portalTween: Phaser.Tweens.Tween;
      spiralTween: Phaser.Tweens.Tween;
      dissipationTween?: Phaser.Tweens.Tween;
    }
  > = new Map();
  private currentRiftStormBonus: { loot: number; xp: number } | null = null;

  private stormAmbientTimer: Phaser.Time.TimerEvent | null = null;

  private readonly CLIENT_MAX_PATH_TILES: number = 600;
  private readonly CLIENT_MAX_TARGET_DISTANCE_TILES: number = 1200;
  private dockProximityIslandId: string | null = null;
  private lastDockProximityPingAt: number = 0;
  private cameraFollowEnabled: boolean = true;

  // Shipwreck system
  private shipwrecks: Map<string, Phaser.GameObjects.Container> = new Map();
  private isSalvaging: boolean = false;
  private salvageProgress: number = 0;
  private salvageUI: Phaser.GameObjects.Container | null = null;

  // Resource node system
  private resourceNodes: Map<string, Phaser.GameObjects.Container> = new Map();
  private isHarvesting: boolean = false;
  private harvestProgress: number = 0;
  private harvestUI: Phaser.GameObjects.Container | null = null;
  private harvestTween: Phaser.Tweens.Tween | null = null;
  private currentHarvestNodeId: string | null = null;

  // Hover range visualization
  private hoverRangeGraphics: Phaser.GameObjects.Graphics | null = null;

  // Boss telegraph system
  private bossTelegraphs: Map<
    string,
    {
      graphics: Phaser.GameObjects.Graphics;
      tween: Phaser.Tweens.Tween;
    }
  > = new Map();

  // Ambient NPC system
  private ambientNPCs: Map<string, Phaser.GameObjects.Container> = new Map();
  private readonly disableAmbientNPCs: boolean = true;
  private tradeRoutes: Map<string, TradeRouteData> = new Map();
  private tradeRouteGraphics: Phaser.GameObjects.Graphics | null = null;
  private worldBoundaryGraphics: Phaser.GameObjects.Graphics | null = null;

  private lastAmbientCullingAtMs: number = 0;
  private readonly AMBIENT_CULLING_INTERVAL_MS: number = 500; // Increased from 250ms to reduce flashing
  private readonly AMBIENT_CULLING_MARGIN_PX: number = TILE_SIZE * 25; // Increased margin for hysteresis
  private readonly AMBIENT_CULLING_HYSTERESIS_PX: number = TILE_SIZE * 3; // Hysteresis to prevent flickering

  // Background tab handling for rubberbanding fix
  private isPageVisible: boolean = true;
  private ambientNPCPendingUpdates: Map<string, NPCData> = new Map(); // Queue of pending updates while visible

  // Ocean wave animation system (performance-conscious)
  private waveSprites: Phaser.GameObjects.Sprite[] = [];
  private waveTimer: number = 0;
  private readonly MAX_WAVE_SPRITES = 4; // Very small number for subtle effect

  // Cloud layer overlay
  private cloudLayer: Phaser.GameObjects.TileSprite | null = null;
  private cloudLayer2: Phaser.GameObjects.TileSprite | null = null;

  // Construction mode state
  private constructionMode: {
    active: boolean;
    selectedKitId: string | null;
    selectedKitName: string | null;
    previewGraphics: Phaser.GameObjects.Graphics | null;
    lastPreviewX: number;
    lastPreviewY: number;
    isValidPosition: boolean;
  } = {
    active: false,
    selectedKitId: null,
    selectedKitName: null,
    previewGraphics: null,
    lastPreviewX: 0,
    lastPreviewY: 0,
    isValidPosition: false,
  };

  // Signature move cast mode state
  private signatureCastMode: {
    active: boolean;
    moveId: string | null;
    moveName: string | null;
    moveColor: string | null;
    previewGraphics: Phaser.GameObjects.Graphics | null;
    lastPreviewX: number;
    lastPreviewY: number;
    aoeRadius: number;
  } = {
    active: false,
    moveId: null,
    moveName: null,
    moveColor: null,
    previewGraphics: null,
    lastPreviewX: 0,
    lastPreviewY: 0,
    aoeRadius: 0,
  };

  // Base safe-zone radius (tiles); per-island radius may be larger via claimRadius/island radius
  private readonly SAFE_ZONE_RADIUS_TILES: number = 8;

  constructor() {
    super({ key: "OceanScene" });
  }

  setSocket(socket: Socket | null): void {
    this.socket = socket;
    if (socket) {
      // Initialize SocketHandlerManager with all callbacks
      this.socketHandlerManager = new SocketHandlerManager(this, {
        // Ship handlers
        updateShips: (ships) => this.updateShips(ships),
        createShipSprite: (ship) => this.createShipSprite(ship),
        removeShip: (shipId) => this.removeShip(shipId),
        getShips: () => this.ships,
        getLocalShip: () => this.localShip,
        getLocalPlayerId: () => this.localPlayerId,
        setLocalShip: (ship) => {
          this.localShip = ship;
        },

        // Fishing spot handlers
        removeFishingSpot: (spotId) =>
          this.fishingSpotRenderer.removeSpot(spotId),
        setFishingSpotInfo: (spotType, spotId) => {
          this.currentFishingSpotType = spotType;
          this.currentFishingSpotId = spotId;
        },

        // Combat handlers
        showCannonEffect: (data) => this.combatManager?.showCannonEffect(data),
        showLootGainedEffect: (pieces, bounty, xp, newLevel) =>
          this.combatManager?.showLootGainedEffect(
            pieces,
            bounty,
            xp,
            newLevel,
          ),
        handlePlayerDeath: (killerName) => this.handlePlayerDeath(killerName),
        showShipSinkingEffect: (ship, shipName, pieces) =>
          this.combatManager?.showShipSinkingEffect(ship, shipName, pieces),
        disengageTarget: () => this.combatManager?.disengageTarget(),
        hideTargetInfo: () => this.hideTargetInfo(),
        clearPathVisualization: (isCombat) =>
          this.clearPathVisualization(isCombat),
        clearAllPathVisualizations: () => this.clearAllPathVisualizations(),
        getSelectedTarget: () =>
          this.combatManager?.getSelectedTarget() || null,
        setSelectedTarget: (target) =>
          this.combatManager?.setSelectedTarget(target),
        renderEscapeSmokeEffect: (x, y) =>
          this.combatManager?.renderEscapeSmokeEffect(x, y),

        // Path handlers
        showPathVisualization: (path, targetTile, isCombat) => {
          // Show path visualization in both ocean and sanctums
          if (path && targetTile) {
            // Convert path format from {tileX, tileY} to {x, y} for drawPath
            // Center on tile by adding TILE_SIZE/2
            const convertedPath = path.map((p) => ({
              x: p.tileX * TILE_SIZE + TILE_SIZE / 2,
              y: p.tileY * TILE_SIZE + TILE_SIZE / 2,
            }));
            this.drawPath(convertedPath, targetTile, isCombat);
          }
        },
        showPathFailedIndicator: (targetTile, reason) =>
          this.showPathFailedIndicator(targetTile, reason),
        validateServerPath: (path, targetTile) =>
          this.validateServerPath(path, targetTile),
        stopMovement: () => this.stopMovement(),
        isTileBlockedByLoadedIslands: (tileX, tileY) =>
          this.isTileBlockedByLoadedIslands(tileX, tileY),
        isDoingWASDMovement: () => this.isDoingWASDMovement(),

        // NPC handlers
        updateNPCs: (npcs) => this.updateNPCs(npcs),
        updateAmbientNPCs: (npcs) => this.updateAmbientNPCs(npcs),
        updateTradeRoutes: (routes) => this.updateTradeRoutes(routes),
        showBossTelegraph: (data) => this.showBossTelegraph(data),
        hideAmbientTradePanel: () =>
          this.events.emit("ambientNPC:hideTradePanel"),
        hideAmbientNPCTooltip: () => this.events.emit("ambientNPC:hideTooltip"),
        getAmbientNPCs: () => this.ambientNPCs,

        // Fishing handlers
        showFishingStatus: (message, isActive) =>
          this.showFishingStatus(message, isActive),
        showFishingMessage: (message, color) =>
          this.showFishingMessage(message, color),
        showFishingCatch: (fish, message, color) =>
          this.showFishingCatch(fish, message, color),
        showFishingError: (message) => this.showFishingError(message),
        setIsFishing: (isFishing) => {
          this.isFishing = isFishing;
          // Deactivate minigame when fishing stops (like combat)
          if (!isFishing && this.fishingMinigameActive) {
            this.fishingMinigameActive = false;
            this.events.emit("fishing:minigame:deactivate");
          }
        },
        startFishingMinigame: () =>
          this.fishingManager.activateFishingMinigame(),

        // Shop handlers
        displayShopUI: (shop) => this.displayShopUI(shop),
        showPurchaseFeedback: (cost) => this.showPurchaseFeedback(cost),

        // Whirlpool handlers
        getWhirlpoolRenderer: () => this.whirlpoolRenderer,
        setIsInWhirlpoolInstance: (isIn) => {
          this.isInWhirlpoolInstance = isIn;
        },
        getIslandRenderer: () => this.islandRenderer,
        getFishingSpotRenderer: () => this.fishingSpotRenderer,
        getPlacedTileRenderer: () => this.placedTileRenderer,
        getCameras: () => this.cameras,
        getRiftScarManager: () => this.riftScarManager,
        setIsInRiftInstance: (isIn) => {
          this.isInRiftInstance = isIn;
        },
        setCurrentRiftId: (riftId) => {
          this.currentRiftId = riftId;
        },
        setInRift: (isIn) => {
          this.events.emit("rift:state", { inRift: isIn });
        },
        setRiftMetadata: (metadata) => {
          this.events.emit("rift:metadata", { metadata });
        },
        setInSanctum: (isIn) => {
          this.events.emit("sanctum:state", { inSanctum: isIn });
        },
        setCurrentSanctumId: (id) => {
          this.events.emit("sanctum:id", { sanctumId: id });
        },
        setSanctumMetadata: (metadata) => {
          this.events.emit("sanctum:metadata", { metadata });
        },

        // UI handlers
        showFloatingMessage: (message, color) =>
          this.showFloatingMessage(message, color),
        showTreasureMarker: (id, x, y, reward, expiresIn) =>
          this.showTreasureMarker(id, x, y, reward, expiresIn),
        removeTreasureMarker: (id) => this.removeTreasureMarker(id),
        showTreasureCollectedNotification: (reward, loot) =>
          this.showTreasureCollectedNotification(reward, loot),
        spawnFloatingBottle: (id, x, y) => this.spawnFloatingBottle(id, x, y),
        removeFloatingBottle: (id) => this.removeFloatingBottle(id),
        showCraftingReadyToast: (recipeName, quantity) =>
          this.events.emit("crafting:ready", { recipeName, quantity }),
        setWeather: (weather, effects) =>
          this.setWeather(
            weather as "clear" | "fog" | "storm" | "calm",
            effects,
          ),
        showGlobalBroadcast: (message, type, duration) =>
          this.events.emit("global:broadcast", { message, type, duration }),
        showChatMessage: (senderId, senderName, shipColor, message, position) =>
          this.showChatMessage(
            senderId,
            senderName,
            shipColor,
            message,
            position,
          ),

        // Rift storm handlers
        spawnRiftStorm: (data) => this.spawnRiftStorm(data),
        removeRiftStorm: (id) => this.removeRiftStorm(id),
        setRiftStormDissipating: (id, endsAt) =>
          this.setRiftStormDissipating(id, endsAt),
        setCurrentRiftStormBonus: (bonus) => {
          this.currentRiftStormBonus = bonus;
        },
        startStormAmbient: () => this.startStormAmbient(),
        stopStormAmbient: () => this.stopStormAmbient(),
        getRiftStorms: () => this.riftStorms,

        // Sea monster handlers
        getSeaMonsterRenderer: () => this.seaMonsterRenderer,

        // Shipwreck handlers
        spawnShipwreck: (id, x, y, size, rotation, lootQuality) =>
          this.spawnShipwreck(id, x, y, size, rotation, lootQuality),
        showSalvageUI: (lootQuality) => this.showSalvageUI(lootQuality),
        updateSalvageUI: (progress) => this.updateSalvageUI(progress),
        hideSalvageUI: () => this.hideSalvageUI(),
        showSalvageReward: (pieces, loot, quality) =>
          this.showSalvageReward(pieces, loot, quality),
        markShipwreckDepleted: (wreckId) => this.markShipwreckDepleted(wreckId),
        removeShipwreck: (wreckId) => this.removeShipwreck(wreckId),
        setIsSalvaging: (isSalvaging) => {
          this.isSalvaging = isSalvaging;
        },

        // Resource node handlers
        spawnResourceNode: (node) => this.spawnResourceNode(node),
        removeResourceNode: (nodeId) => this.removeResourceNode(nodeId),
        hideHarvestUI: () => this.hideHarvestUI(),
        getResourceNodes: () => this.resourceNodes,
        setIsHarvesting: (isHarvesting) => {
          this.isHarvesting = isHarvesting;
        },
        setCurrentHarvestNodeId: (nodeId) => {
          this.currentHarvestNodeId = nodeId;
        },
        getCurrentHarvestNodeId: () => this.currentHarvestNodeId,

        // Loot container handlers
        spawnLootContainerMarker: (id, x, y, isPrivate) =>
          this.spawnLootContainerMarker(id, x, y, isPrivate),
        removeLootContainerMarker: (id) => this.removeLootContainerMarker(id),

        // Wildcard power handlers
        showWildcardPowerVisual: (
          casterX,
          casterY,
          targetX,
          targetY,
          color,
          aoeRadius,
          element,
        ) =>
          this.showWildcardPowerVisual(
            casterX,
            casterY,
            targetX,
            targetY,
            color,
            aoeRadius,
            element,
          ),
        getShipRenderer: () => this.shipRenderer,

        // Docking handlers
        updateIslandOwnership: (islandId, data) => {
          /* Handled by permanentLocationRenderer */
        },
        getPermanentLocationRenderer: () => this.permanentLocationRenderer,

        // State getters
        isPlayerDead: () => this.isPlayerDead,
        getSocket: () => this.socket,
        setCachedIslandCollisionCheck: (cache) => {
          this.cachedIslandCollisionCheck = cache;
        },
        setLastFireTimestamp: (timestamp) => {
          this.lastFireTimestamp = timestamp;
        },
        setIsSprinting: (isSprinting) => {
          this.isSprinting = isSprinting;
        },
        getIsSprinting: () => this.isSprinting,
      });

      // Set socket on manager to trigger socket listener setup
      this.socketHandlerManager.setSocket(socket);
    }

    this.checkDockProximity();
  }

  setLocalPlayerId(id: string): void {
    this.localPlayerId = id;
  }

  setLocalCharacterId(id: string): void {
    this.localCharacterId = id;
  }

  // ============ LIFECYCLE ============

  preload(): void {
    // Initialize and preload tilesets
    this.tilesetManager = new TilesetManager(this);
    this.tilesetManager.preloadTilesets();

    // Preload sound effects
    SoundManager.preloadSounds(this);

    // Preload NPC portrait images
    const portraitFiles = [
      // Pirates
      "pirates/pirate_1.png",
      "pirates/pirate_2.png",
      "pirates/pirate_3.png",
      "pirates/pirate_4.png",
      "pirates/pirate_5.png",
      "pirates/pirate_6.png",
      "pirates/pirate_7.png",
      "pirates/pirate_8.png",
      "pirates/pirate_9.png",
      "pirates/pirate_10.png",
      "pirates/pirate_11.png",
      "pirates/pirate_12.png",
      "pirates/pirate_13.png",
      "pirates/pirate_14.png",
      "pirates/pirate_15.png",
      "pirates/pirate_16.png",
      // Marines
      "marines/marine_1.png",
      "marines/marine_2.png",
      "marines/marine_3.png",
      "marines/marine_4.png",
      "marines/marine_5.png",
    ];

    portraitFiles.forEach((file, index) => {
      this.load.image(`portrait_${index}`, `/assets/portraits/${file}`);
    });

    // Preload ambient animal portraits (used by ambient hover tooltip)
    const ambientAnimalPortraits: Record<string, string> = {
      seagull_flock: "animals/seagull.png",
      dolphin_pod: "animals/dolphin.png",
      whale: "animals/whale.png",
      jellyfish_bloom: "animals/jellyfish.png",
      floating_barrel: "animals/barrel.png",
    };
    Object.entries(ambientAnimalPortraits).forEach(([key, file]) => {
      this.load.image(`portrait_ambient_${key}`, `/assets/portraits/${file}`);
    });

    // Preload sailor/fisherman portraits (multiple variants)
    for (let i = 1; i <= 4; i++) {
      this.load.image(
        `portrait_ambient_sailor_${i}`,
        `/assets/portraits/sailors/sailor_${i}.png`,
      );
      this.load.image(
        `portrait_ambient_fisherman_${i}`,
        `/assets/portraits/fisherman/fisherman_${i}.png`,
      );
    }

    // Preload resource node sprites
    const resourceNodeSprites = Object.keys(RESOURCE_NODE_SPRITES);
    const baseUrl = process.env.PUBLIC_URL ?? "";
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      if (file.type === "image" && resourceNodeSprites.includes(file.key)) {
        console.warn(
          `❌ Failed to load resource node sprite: ${file.key}`,
          file.src,
        );
      }
    });

    resourceNodeSprites.forEach((nodeType) => {
      const spriteConfig = RESOURCE_NODE_SPRITES[nodeType];
      if (spriteConfig) {
        this.load.image(nodeType, `${baseUrl}${spriteConfig.path}`);
      }
    });

    this.load.image(
      "message_in_a_bottle",
      `${baseUrl}/assets/tilesets/resources/message_in_a_bottle.png`,
    );

    console.log(
      `📦 Preloading ${resourceNodeSprites.length} resource node sprites`,
    );
  }

  async create(): Promise<void> {
    console.log(`🌊 OceanScene.create() called`);

    // Wait for all textures to finish loading before proceeding with island rendering
    // This ensures that island tiles use sprites instead of fallback graphics
    await new Promise<void>((resolve) => {
      if (this.load.isLoading()) {
        console.log("⏳ Waiting for asset load queue to complete...");
        this.load.once("complete", () => {
          console.log("✅ All assets loaded, proceeding with scene setup");
          resolve();
        });
      } else {
        console.log("✅ Assets already loaded, proceeding");
        resolve();
      }
    });

    // Debug: Check tileset textures after load completes
    this.tilesetManager.debugLogTextures();

    // Debug: Check resource node textures
    const resourceNodeTypes = [
      "stone_quarry",
      "shipwreck_debris",
      "sunken_rowboat",
      "coconut_palm",
      "shallow_waters",
    ];
    console.log("🔍 Checking resource node textures:");
    resourceNodeTypes.forEach((nodeType) => {
      const exists = this.textures.exists(nodeType);
      console.log(`  ${nodeType}: ${exists ? "✅" : "❌"}`);
    });
    const allKeys = this.textures.getTextureKeys();
    console.log(`Total textures loaded: ${allKeys.length}`);
    // Check if portrait textures are loaded (these should work)
    const portraitExists = this.textures.exists("portrait_0");
    console.log(`Portrait texture loaded: ${portraitExists ? "✅" : "❌"}`);
    // Show all keys that contain 'stone' or 'portrait'
    const relevantKeys = allKeys.filter(
      (k) => k.includes("stone") || k.includes("portrait"),
    );
    console.log(`Relevant textures: ${relevantKeys.join(", ")}`);

    // Initialize sound manager
    initGlobalSoundManager(this);

    startBackgroundMusic();

    // Ensure ambient storm audio stops if the scene is torn down
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.stopStormAmbient();
      if (this.riftScarManager) {
        this.riftScarManager.destroy();
      }
    });

    // Initialize placed tile renderer (no dependencies)
    this.placedTileRenderer = new PlacedTileRenderer(this);
    this.signatureMoveEffectsRenderer = new SignatureMoveEffectsRenderer(this);

    // Initialize core systems (no dependencies)
    this.combatSystem = new CombatSystem(this, {
      getShips: () => this.ships,
      getLocalShip: () => this.localShip,
      hideTargetInfo: () => this.hideTargetInfo(),
      clearAllPathVisualizations: () => this.clearAllPathVisualizations(),
      isInSafeZone: () => this.isPlayerInSafeZone(),
    });
    this.shopUISystem = new ShopUI(this);

    // Ocean background (base color only)
    this.drawOcean();

    // Initialize ocean tile renderer (uses tileset for ocean background)
    this.oceanTileRenderer = new OceanTileRenderer(this, {
      onFishSpotClick: (spot: FishingSpotInfo) =>
        this.inputInteractionManager?.handleFishingSpotClick(spot),
      onRockMineClick: (node: ResourceNodeInfo) => {
        // Start mining at decorative rock
        if (this.localShip) {
          const dx = this.localShip.x - node.worldX;
          const dy = this.localShip.y - node.worldY;
          const distPixels = Math.sqrt(dx * dx + dy * dy);
          const distTiles = distPixels / 32; // TILE_SIZE = 32

          // Move to adjacent position for mining (within 2 tiles)
          if (distTiles > 2) {
            // Move to position 2 tiles away from rock
            const angle = Math.atan2(dy, dx);
            const targetX = node.worldX + Math.cos(angle) * 64; // 2 * TILE_SIZE
            const targetY = node.worldY + Math.sin(angle) * 64;
            this.moveToPosition(targetX, targetY);
          }

          // Emit mining start
          this.socket?.emit("resource:startHarvest", { nodeId: node.id });
        }
      },
    });
    this.oceanTileRenderer.setTilesetManager(this.tilesetManager);
    // Ocean renders under shore tiles but not under interior island tiles
    this.oceanTileRenderer.setTileBlockedCallback(
      (tileX: number, tileY: number) => {
        return this.islandRenderer?.isInteriorIslandTile(tileX, tileY) || false;
      },
    );
    // Set context callback for context-aware ocean tile selection
    this.oceanTileRenderer.setContextCallback(() => {
      if (this.isInSanctumInstance) return "sanctum";
      if (this.isInRiftInstance) return "rift";
      return "core";
    });

    // Initialize animated water effect
    this.oceanWaterRenderer = new OceanWaterRenderer(this);

    // Initialize rift scar manager
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    this.riftScarManager = new RiftScarManager(this, apiUrl, "core");
    try {
      await this.riftScarManager.loadScarsForRift("core");
    } catch (error) {
      console.error(
        "[OceanScene] Failed to load rift scars for core world:",
        error,
      );
    }

    // Initialize CombatManager BEFORE renderers that need it
    this.combatManager = new CombatManager(this, {
      getSocket: () => this.socket,
      getLocalShip: () => this.localShip,
      getShips: () => this.ships,
      getNPCs: () => this.npcs,
      getLocalPlayerId: () => this.localPlayerId,
      isPlayerDead: () => this.isPlayerDead,
      isBoardingActive: () => false,
      hideTargetInfo: () => this.hideTargetInfo(),
      clearAllPathVisualizations: () => this.clearAllPathVisualizations(),
      clearPathVisualization: (isCombat) =>
        this.clearPathVisualization(isCombat),
      isInSafeZone: (x, y) => this.isInSafeZone(x, y),
    });

    // Initialize FishingManager BEFORE renderers that need it
    this.fishingManager = new FishingManager(this, {
      getLocalShip: () => this.localShip,
      getSocket: () => this.socket,
      moveToPosition: (x, y) => this.moveToPosition(x, y),
      disengageTarget: () => this.combatManager.disengageTarget(),
    });

    // Initialize InputInteractionManager BEFORE renderers that need it
    this.inputInteractionManager = new InputInteractionManager(this, {
      getLocalShip: () => this.localShip,
      getLocalPlayerId: () => this.localPlayerId,
      getSocket: () => this.socket,
      getIslandAtPosition: (x, y) => this.getIslandAtPosition(x, y),
      getRiftScarAtPosition: (x, y) => null,
      canDockAtIsland: (island) => this.canDockAtIsland(island),
      isPlayerDocked: () => this.localShip?.shipData.combatState === "docked",
      getDockedIslandId: () => this.localShip?.shipData.dockedAt,
      moveToPosition: (x, y) => this.moveToPosition(x, y),
      disengageTarget: () => this.combatManager.disengageTarget(),
      dockAtIsland: (islandId) => this.dockAtIsland(islandId),
      undockFromIsland: () => this.undockFromIsland(),
      startFishing: (spot) => this.fishingManager.startFishing(spot),
      showFloatingMessage: (msg, color) => this.showFloatingMessage(msg, color),
    });

    // Initialize InstanceManager
    this.instanceManager = new InstanceManager(this, {
      getSocket: () => this.socket,
      getLocalShip: () => this.localShip,
      getIslandRenderer: () => this.islandRenderer,
      getFishingSpotRenderer: () => this.fishingSpotRenderer,
      getPlacedTileRenderer: () => this.placedTileRenderer,
      getWhirlpoolRenderer: () => this.whirlpoolRenderer,
      getRiftScarManager: () => this.riftScarManager,
      getPermanentLocationRenderer: () => this.permanentLocationRenderer,
      getCameras: () => this.cameras,
      showFloatingMessage: (msg, color) => this.showFloatingMessage(msg, color),
      suppressInput: (durationMs) => {
        this.inputSuppressedUntilMs = Date.now() + durationMs;
      },
    });

    // NOW initialize renderers that depend on managers
    this.fishingSpotRenderer = new FishingSpotRenderer(this, {
      onFishingSpotClick: (spot: FishingSpotInfo) =>
        this.inputInteractionManager?.handleFishingSpotClick(spot),
      onFishingSpotRightClick: (
        spot: FishingSpotInfo,
        screenX: number,
        screenY: number,
      ) => {
        this.inputInteractionManager?.handleFishingSpotRightClick(
          spot,
          screenX,
          screenY,
        );
      },
    });

    this.permanentLocationRenderer = new PermanentLocationRenderer(this, {
      onTradingPostClick: (
        island: IslandData,
        worldX: number,
        worldY: number,
      ) =>
        this.inputInteractionManager?.handleTradingPostClick(
          island,
          worldX,
          worldY,
        ),
      onShipyardClick: (island: IslandData, worldX: number, worldY: number) =>
        this.inputInteractionManager?.handleShipyardClick(
          island,
          worldX,
          worldY,
        ),
      onHarborOfficeClick: (
        island: IslandData,
        worldX: number,
        worldY: number,
      ) =>
        this.inputInteractionManager?.handleHarborOfficeClick(
          island,
          worldX,
          worldY,
        ),
      onTavernClick: (island: IslandData, worldX: number, worldY: number) =>
        this.inputInteractionManager?.handleTavernClick(island, worldX, worldY),
    });

    this.islandRenderer = new IslandRenderer(this, {
      onFishingSpotsLoaded: (spots: FishingSpotData[], chunkId: string) => {
        this.fishingSpotRenderer.renderFishingSpots(spots, chunkId);
      },
      onIslandRightClick: (
        island: IslandData,
        screenX: number,
        screenY: number,
      ) => {
        this.inputInteractionManager?.handleIslandRightClick(
          island,
          screenX,
          screenY,
        );
      },
      onPermanentLocationLoaded: (island: IslandData) => {
        // Pass permanent locations to PermanentLocationRenderer
        this.permanentLocationRenderer.renderLocation(island);
      },
      onChunkUnloaded: (chunkId: string) => {
        // Coordinate unloading with PermanentLocationRenderer
        this.permanentLocationRenderer.unloadChunk(chunkId);
        // Also unload placed tiles for this chunk
        this.placedTileRenderer.unloadChunk(chunkId);
      },
      onPlacedTilesLoaded: (tiles: any[], chunkId: string) => {
        // Render placed tiles from tile editor
        this.placedTileRenderer.loadChunk(chunkId, tiles);
      },
      getLocalPlayerId: () => this.localPlayerId,
      getCurrentRiftId: () => this.currentRiftId,
    });

    // Pass TilesetManager to IslandRenderer for sprite-based tile rendering
    this.islandRenderer.setTilesetManager(this.tilesetManager);

    this.shipRenderer = new ShipRenderer(this, {
      onShipClick: (shipId: string) => this.combatManager?.engageTarget(shipId),
      onShipHover: (ship: ShipSprite) => {
        this.shipRenderer.showShipTooltip(ship);
        this.combatManager?.showHoverRange(ship);
      },
      onShipHoverEnd: () => {
        this.shipRenderer.hideShipTooltip();
        this.combatManager?.hideHoverRange();
      },
      getLocalPlayerId: () => this.localPlayerId,
      getSelectedTarget: () => this.combatManager?.getSelectedTarget() || null,
      getLocalShip: () => this.localShip,
    });

    // Cannon focus minigame is handled by React component (CannonFocusOverlay in GameCanvas)
    // Socket events (cannonFocus:activate, cannonFocus:deactivate, cannonFocus:targetUpdate)
    // are emitted here and listened to by the React component

    // Fishing minigame is now handled by React component (FishingMinigame in GameCanvas)
    // Socket events (fishing:minigame:activate, fishing:minigame:deactivate)
    // are emitted here and listened to by the React component

    this.whirlpoolRenderer = new WhirlpoolRenderer(this, {
      onWhirlpoolClick: (entry: WhirlpoolEntryData) =>
        this.inputInteractionManager?.handleWhirlpoolClick(entry),
      onWhirlpoolRightClick: (
        entry: WhirlpoolEntryData,
        screenX: number,
        screenY: number,
      ) => {
        this.inputInteractionManager?.handleWhirlpoolRightClick(
          entry,
          screenX,
          screenY,
        );
      },
      onWhirlpoolHover: (
        entry: WhirlpoolEntryData,
        screenX: number,
        screenY: number,
      ) => {
        this.whirlpoolRenderer.showTooltip(entry, screenX, screenY);
      },
      onWhirlpoolHoverEnd: () => {
        this.whirlpoolRenderer.hideTooltip();
      },
      onWhirlpoolResourceClick: (
        resourceId: string,
        worldX: number,
        worldY: number,
      ) => {
        if (!this.socket) return;
        if (!this.localShip) return;
        const dist = Math.hypot(
          this.localShip.x - worldX,
          this.localShip.y - worldY,
        );
        const interactRange = TILE_SIZE * 5;
        if (dist <= interactRange) {
          this.socket.emit("whirlpool:harvestResource", { resourceId });
          return;
        }

        this.moveToPosition(worldX, worldY);
      },
      onWhirlpoolExitClick: (worldX: number, worldY: number) => {
        this.inputInteractionManager?.handleWhirlpoolExitClick(
          worldX,
          worldY,
          this.isInWhirlpoolInstance,
        );
      },
    });

    this.inputHandler = new InputHandler(this, {
      onEscape: () => {
        this.combatManager.disengageTarget();
        this.stopFishingOnMove();
      },
      onSpace: () => this.stopMovement(),
      onFocusToggle: () => this.combatManager.toggleCannonFocus(),
      onFishingToggle: () => this.toggleFishingMinigame(),
      onSprintStart: () => this.startSprint(),
      onSprintEnd: () => this.stopSprint(),
      onWildcardPower: () => this.activateSignatureMove(),
      onWASDMove: (
        direction: "up" | "down" | "left" | "right",
        isPressed: boolean,
      ) => this.handleWASDMovement(direction, isPressed),
      onClickShip: (shipId: string) => this.combatManager.engageTarget(shipId),
      onClickEmpty: (worldX: number, worldY: number) =>
        this.moveToPosition(worldX, worldY),
      onRightClickShip: (
        shipId: string,
        screenX: number,
        screenY: number,
        shipData,
      ) => {
        console.log(
          `🎯 OceanScene: Emitting ship:contextmenu event for ${shipId}`,
        );
        this.events.emit("ship:contextmenu", {
          shipId,
          screenX,
          screenY,
          shipData,
        });
      },
      onRightClickEmpty: (
        worldX: number,
        worldY: number,
        screenX: number,
        screenY: number,
      ) => {
        console.log(`🎯 OceanScene: Emitting world:contextmenu event`);
        this.events.emit("world:contextmenu", {
          worldX,
          worldY,
          screenX,
          screenY,
        });
      },
      onRightClickIsland: (
        worldX: number,
        worldY: number,
        screenX: number,
        screenY: number,
      ) => {
        const island = this.getIslandAtPosition(worldX, worldY);
        if (island) {
          this.inputInteractionManager?.handleIslandRightClick(
            island,
            screenX,
            screenY,
          );
        }
      },
      onRightClickRiftScar: (
        worldX: number,
        worldY: number,
        screenX: number,
        screenY: number,
      ) => {
        const scar = this.getRiftScarAtPosition(worldX, worldY);
        if (scar) {
          this.inputInteractionManager?.handleRiftScarRightClick(
            scar,
            screenX,
            screenY,
          );
        }
      },
      getShipAtPosition: (x: number, y: number) => this.getShipAtPosition(x, y),
      getLocalPlayerId: () => this.localPlayerId,
      isInputBlocked: () =>
        this.isPlayerDead || Date.now() < this.inputSuppressedUntilMs,
      isFishing: () => this.isFishing,
      isFishingMinigameActive: () => this.fishingMinigameActive,
      isCannonFocusAvailable: () => this.combatManager.isCannonFocusAvailable(),
      isCannonFocusActive: () => this.cannonFocusActive,
      hasIslandAt: (worldX: number, worldY: number) =>
        this.hasIslandAtPosition(worldX, worldY),
      hasRiftScarAt: (worldX: number, worldY: number) =>
        this.hasRiftScarAtPosition(worldX, worldY),
      // Construction mode callbacks
      isConstructionMode: () => this.constructionMode.active,
      onConstructionClick: (worldX: number, worldY: number) =>
        this.handleConstructionClick(worldX, worldY),
      onConstructionMove: (worldX: number, worldY: number) =>
        this.updateConstructionPreview(worldX, worldY),
      onConstructionExit: () => this.exitConstructionMode(),
      // Signature cast mode callbacks
      isSignatureCastMode: () => this.isSignatureCastModeActive(),
      onSignatureCastClick: (worldX: number, worldY: number) =>
        this.handleSignatureCastClick(worldX, worldY),
      onSignatureCastMove: (worldX: number, worldY: number) =>
        this.updateSignatureCastPreview(worldX, worldY),
      onSignatureCastExit: () => this.exitSignatureCastMode(),
      showNothingInterestingHappens: () =>
        this.showFloatingMessage("Nothing interesting happens", "#aaaaaa"),
    });

    // Initialize sanctum renderer
    this.sanctumRenderer = new SanctumRenderer(this, {
      onEnemyClick: (enemyId: string) => {
        console.log(`🏛️ Enemy clicked: ${enemyId}`);
        // TODO: Implement sanctum combat
      },
      onContainerClick: (containerId: string) => {
        console.log(`🏛️ Container clicked: ${containerId}`);
        // Find container data to get position
        const container = this.findSanctumContainer(containerId);
        if (container) {
          console.log(
            `🏛️ Found container at (${container.worldX}, ${container.worldY}), moving player there`,
          );
          // Move to container first, then gather on arrival (like resource nodes)
          this.moveToPosition(container.worldX, container.worldY);
          // Store pending gather action
          this.pendingSanctumGather = { containerId, type: "container" };
          console.log(`🏛️ Pending gather set for container ${containerId}`);
        } else {
          console.warn(`🏛️ Container ${containerId} not found!`);
        }
      },
      onContainerRightClick: (
        containerId: string,
        screenX: number,
        screenY: number,
      ) => {
        console.log(`🏛️ Container right-clicked: ${containerId}`);
        const container = this.findSanctumContainer(containerId);
        if (container) {
          // Emit proper context menu event that GameCanvas can handle
          this.events.emit("sanctum:container:contextmenu", {
            containerId,
            containerData: container,
            screenX,
            screenY,
          });
        }
      },
      onTileClick: (tileX: number, tileY: number) => {
        console.log(`🏛️ Tile clicked: ${tileX}, ${tileY}`);
        // TODO: Implement tile-based movement in sanctum
      },
      onTerminalClick: (roomId: string) => {
        console.log(`🏛️ Terminal clicked in room: ${roomId}`);
        // Request terminal data from server
        this.socket?.emit("sanctum:request_terminal", {
          instanceId: this.getSanctumInstanceId(),
          roomId,
        });
        // Emit event to open terminal UI in React
        this.events.emit("sanctum:openTerminal");
      },
      onExitClick: () => {
        console.log(`🏛️ Exit portal clicked - leaving sanctum`);
        // Emit socket event to exit sanctum
        if (this.socket && this.currentSanctumInstanceId) {
          this.socket.emit("sanctum:exit", {
            instanceId: this.currentSanctumInstanceId,
          });
        }
      },
    });
    this.sanctumRenderer.initialize();

    // Listen for sanctum movement events (from loot containers, etc)
    this.game.events.on("sanctum:move_to", (data: { x: number; y: number }) => {
      console.log(`🏛️ Sanctum move_to event received: (${data.x}, ${data.y})`);
      this.moveToPosition(data.x, data.y);
    });

    // Initialize sea monster renderer
    this.seaMonsterRenderer = new SeaMonsterRenderer(this);
    this.seaMonsterRenderer.start();

    // Handle sanctum exit request from game scene (exit portal clicked)
    this.events.on("sanctum:exitRequest", () => {
      console.log(
        "🏛️ [OceanScene] Exit request received from sanctum renderer",
      );
      if (this.socket && this.currentSanctumInstanceId) {
        this.socket.emit("sanctum:exit", {
          instanceId: this.currentSanctumInstanceId,
        });
      }
    });

    // Handle rift scar clicks
    this.events.on("rift-scar-clicked", async (scarData: any) => {
      console.log("[OCEAN SCENE] Rift scar clicked:", scarData.name);
      if (this.localShip && this.riftScarManager && this.localCharacterId) {
        const result = await this.riftScarManager.traverseScar(
          this.localCharacterId,
          scarData,
        );
        if (result) {
          console.log(
            "[OCEAN SCENE] Successfully traversed to:",
            result.destinationRift,
          );
        }
      }
    });

    // Handle rift exited event (from admin teleport or normal exit)
    this.events.on("rift:exited", () => {
      console.log(
        "🌀 [OCEAN SCENE] rift:exited event received - setting isInRiftInstance = false",
      );
      this.isInRiftInstance = false;
      this.currentRiftId = "core";
    });

    // Handle harvest started event
    this.events.on(
      "harvest:started",
      (data: {
        nodeId: string;
        harvestTime: number;
        nodeName?: string;
        requiredSkill?: string;
        requiredLevel?: number;
      }) => {
        console.log(
          `⛏️ Starting harvest of ${data.nodeName || "resource"} (${data.harvestTime}s)`,
        );
        this.showHarvestUI(data.harvestTime);
      },
    );

    // Handle harvest completed event
    this.events.on(
      "harvest:complete",
      (data: {
        nodeId: string;
        drops?: any[];
        xpGained?: number;
        bonusApplied?: string;
        skillType?: string;
      }) => {
        console.log(
          `⛏️ Harvest complete! Got ${data.drops?.length || 0} items and ${data.xpGained || 0} XP`,
        );
        this.hideHarvestUI();

        // Show harvest result message if there are drops
        if (data.drops && data.drops.length > 0) {
          const dropNames = data.drops
            .map((d) => d.item?.name || d.itemId)
            .join(", ");
          const xpText = data.xpGained ? ` (+${data.xpGained} XP)` : "";
          const bonusText = data.bonusApplied ? ` [${data.bonusApplied}]` : "";
          this.showFloatingMessage(
            `Obtained: ${dropNames}${xpText}${bonusText}`,
            "#2ecc71",
          );
        }
      },
    );

    // Handle harvest cancelled event
    this.events.on("harvest:cancelled", (data: { nodeId: string }) => {
      console.log(`⛏️ Harvest cancelled for node ${data.nodeId}`);
      this.hideHarvestUI();
    });

    // Handle rift traversal success - enter/exit rift instance
    this.events.on("rift-traversal-success", async (result: any) => {
      console.log("[OCEAN SCENE] Handling rift traversal success:", result);

      if (this.localShip && this.socket) {
        // Check if returning to core world (exiting rift)
        if (result.destinationRift === "core") {
          this.events.emit("rift:exited", {
            exitPosition: { x: result.spawnX, y: result.spawnY },
          });
          return;
        }

        // Entering a rift - COMPLETELY CLEAR CORE WORLD
        this.isInRiftInstance = true;
        this.currentRiftId = result.destinationRift;

        // Suppress input briefly on transition completion
        this.inputSuppressedUntilMs = Date.now() + 350;

        console.log("[RIFT] Clearing all core world content...");

        // CRITICAL: Clear chunk cache to prevent core world data from persisting
        const CacheManager = require("../../utils/CacheManager").default;
        CacheManager.clearChunks();
        console.log("[RIFT] Chunk cache cleared");

        // DESTROY all core world islands (not just hide)
        this.islandRenderer.clearIslands();

        // DESTROY all permanent locations (trading posts, harbor offices, etc)
        this.permanentLocationRenderer.getLocations().forEach((entry) => {
          entry.graphics.destroy();
          entry.hitArea?.destroy();
          entry.label?.destroy();
          entry.typeLabel?.destroy();
          entry.safeLabel?.destroy();
          entry.safeZoneGraphics?.destroy();
        });
        this.permanentLocationRenderer.getLocations().clear();

        // REMOVE all NPC ships from core world (not just hide)
        this.ships.forEach((ship) => {
          if (ship.shipId && ship.shipId !== this.localShip?.shipId) {
            this.shipRenderer.removeShip(ship.shipId);
          }
        });

        // REMOVE all NPCs (they only exist in core world)
        this.npcs.forEach((npc) => {
          npc.destroy();
        });
        this.npcs.clear();

        // REMOVE all ambient NPCs (they only exist in core world)
        this.ambientNPCs.forEach((npc) => {
          npc.destroy();
        });
        this.ambientNPCs.clear();

        // Clear fishing spots
        this.fishingSpotRenderer.clearSpots();

        // Clear whirlpools (destroy containers directly)
        this.whirlpoolRenderer.destroy();

        // Clear rift scars from core world
        if (this.riftScarManager) {
          this.riftScarManager.clear();
        }

        // Update local ship position
        this.localShip.x = result.spawnX;
        this.localShip.y = result.spawnY;

        // Notify server of new position and rift after traversal
        this.socket.emit("ship:updatePosition", {
          x: result.spawnX,
          y: result.spawnY,
          velocityX: 0,
          velocityY: 0,
          rotation: this.localShip.rotation,
        });

        // Update ship's riftId on server for proper filtering
        this.socket.emit("ship:setRiftId", { riftId: result.destinationRift });

        console.log(
          `[RIFT] Loading fresh rift content for rift: ${this.currentRiftId}`,
        );
        console.log(
          `[RIFT] Spawn position: (${result.spawnX}, ${result.spawnY})`,
        );

        // CRITICAL: Load islands FIRST before centering camera
        // This ensures chunks are loaded at the spawn position
        if (this.islandRenderer) {
          // Temporarily center on spawn position for loading
          this.cameras.main.centerOn(result.spawnX, result.spawnY);
          const worldView = this.cameras.main.worldView;
          console.log(
            `[RIFT] Loading visible islands for rift ${this.currentRiftId} at spawn position`,
          );
          await this.islandRenderer.loadVisibleIslands(worldView, {
            paddingPx: TILE_SIZE * 40,
          });
          console.log(`[RIFT] Finished loading visible islands`);
        }

        // Reinitialize whirlpool renderer for rift context
        this.whirlpoolRenderer = new WhirlpoolRenderer(this, {
          onWhirlpoolClick: (entry: WhirlpoolEntryData) =>
            this.inputInteractionManager?.handleWhirlpoolClick(entry),
          onWhirlpoolRightClick: (
            entry: WhirlpoolEntryData,
            screenX: number,
            screenY: number,
          ) => {
            this.events.emit("whirlpool:contextmenu", {
              whirlpoolId: entry.id,
              whirlpoolPosition: { x: entry.worldX, y: entry.worldY },
              whirlpoolClass: entry.class,
              screenX,
              screenY,
            });
          },
          onWhirlpoolHover: (
            entry: WhirlpoolEntryData,
            screenX: number,
            screenY: number,
          ) => {
            this.whirlpoolRenderer.showTooltip(entry, screenX, screenY);
          },
          onWhirlpoolHoverEnd: () => {
            this.whirlpoolRenderer.hideTooltip();
          },
          onWhirlpoolResourceClick: (
            resourceId: string,
            worldX: number,
            worldY: number,
          ) => {
            if (!this.socket) return;
            if (!this.localShip) return;
            const dist = Math.hypot(
              this.localShip.x - worldX,
              this.localShip.y - worldY,
            );
            if (dist > 150) return;
            this.moveToPosition(worldX, worldY);
          },
          onWhirlpoolExitClick: (worldX: number, worldY: number) => {
            this.inputInteractionManager?.handleWhirlpoolExitClick(
              worldX,
              worldY,
              this.isInWhirlpoolInstance,
            );
          },
        });

        // Load rift scars for this rift
        if (this.riftScarManager) {
          await this.riftScarManager.loadScarsForRift(result.destinationRift);
        }

        // Request fresh whirlpool data for rift (if any)
        this.socket?.emit("whirlpool:getAll");

        // CRITICAL: Request fresh ship/NPC data for the new rift
        console.log(
          "[RIFT] Requesting fresh ship data for rift:",
          result.destinationRift,
        );
        this.socket?.emit("ship:requestAll");

        // Emit event for React UI
        this.events.emit("rift:entered", {
          riftId: result.destinationRift,
          position: { x: result.spawnX, y: result.spawnY },
        });

        // Show notification
        this.showFloatingMessage(
          `Entered ${result.destinationRift}`,
          "#00ffff",
        );
      }
    });

    // Handle rift exit - return to core world
    this.events.on(
      "rift:exited",
      async (data?: { exitPosition?: { x: number; y: number } }) => {
        console.log("[OCEAN SCENE] Exiting rift, returning to core world");

        if (this.localShip && this.socket) {
          const exitPosition = data?.exitPosition ?? {
            x: this.localShip.x,
            y: this.localShip.y,
          };
          console.log("[RIFT EXIT] Clearing rift content...");

          // CRITICAL: Clear chunk cache to remove rift data
          const CacheManager = require("../../utils/CacheManager").default;
          CacheManager.clearChunks();
          console.log("[RIFT EXIT] Chunk cache cleared");

          // Clear all rift content
          this.islandRenderer.clearIslands();
          this.fishingSpotRenderer.clearSpots();
          this.whirlpoolRenderer.destroy();

          if (this.riftScarManager) {
            this.riftScarManager.clear();
          }

          // Update state
          this.isInRiftInstance = false;
          this.currentRiftId = "core";

          // Update position
          this.localShip.x = exitPosition.x;
          this.localShip.y = exitPosition.y;

          // Notify server
          this.socket.emit("ship:updatePosition", {
            x: exitPosition.x,
            y: exitPosition.y,
            velocityX: 0,
            velocityY: 0,
            rotation: this.localShip.rotation,
          });

          // Reset ship's riftId on server
          this.socket.emit("ship:setRiftId", { riftId: "core" });

          // Center camera
          this.cameras.main.centerOn(exitPosition.x, exitPosition.y);

          console.log("[RIFT EXIT] Loading fresh core world content...");

          // Reinitialize whirlpool renderer for core world
          this.whirlpoolRenderer = new WhirlpoolRenderer(this, {
            onWhirlpoolClick: (entry: WhirlpoolEntryData) =>
              this.inputInteractionManager?.handleWhirlpoolClick(entry),
            onWhirlpoolRightClick: (
              entry: WhirlpoolEntryData,
              screenX: number,
              screenY: number,
            ) => {
              this.events.emit("whirlpool:contextmenu", {
                whirlpoolId: entry.id,
                whirlpoolPosition: { x: entry.worldX, y: entry.worldY },
                screenX,
                screenY,
              });
            },
            onWhirlpoolHover: (
              entry: WhirlpoolEntryData,
              screenX: number,
              screenY: number,
            ) => {
              this.whirlpoolRenderer.showTooltip(entry, screenX, screenY);
            },
            onWhirlpoolHoverEnd: () => {
              this.whirlpoolRenderer.hideTooltip();
            },
            onWhirlpoolResourceClick: (
              resourceId: string,
              worldX: number,
              worldY: number,
            ) => {
              if (!this.socket) return;
              if (!this.localShip) return;
              const dist = Math.hypot(
                this.localShip.x - worldX,
                this.localShip.y - worldY,
              );
              if (dist > 150) return;
              this.moveToPosition(worldX, worldY);
            },
            onWhirlpoolExitClick: (worldX: number, worldY: number) => {
              this.inputInteractionManager?.handleWhirlpoolExitClick(
                worldX,
                worldY,
                this.isInWhirlpoolInstance,
              );
            },
          });

          // Reload core world islands (fresh from server)
          const worldView = this.cameras.main.worldView;
          this.islandRenderer.loadVisibleIslands(worldView, {
            paddingPx: TILE_SIZE * 40,
          });

          // Reload permanent locations, fishing spots, NPCs, etc
          this.socket?.emit("fishing:getAllSpots");
          this.socket?.emit("whirlpool:getAll");

          // Reload rift scars for core world
          if (this.riftScarManager) {
            this.riftScarManager.loadScarsForRift("core");
          }

          // CRITICAL: Request fresh ship/NPC data for core world
          console.log("[RIFT EXIT] Requesting fresh ship data for core world");
          this.socket?.emit("ship:requestAll");

          this.showFloatingMessage("Returned to the Grand Latitude", "#00ffff");
        }
      },
    );

    // Set up camera - world is 8000x8000
    this.cameras.main.setBounds(
      -UNCHARTED_WATERS_BUFFER_PX,
      -UNCHARTED_WATERS_BUFFER_PX,
      WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX * 2,
      WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX * 2,
    );

    // Don't set zoom here - let GameCanvas apply persisted zoom via callback
    // this.cameras.main.setZoom(1);

    // Center camera on world center initially (will follow player once spawned)
    this.cameras.main.centerOn(WORLD_WIDTH / 2, WORLD_HEIGHT / 2);

    // Input
    this.inputHandler.setup();

    // HUD
    this.createHUD();

    // Instructions
    this.createInstructions();

    // Create cloud overlay layer (subtle drifting clouds)
    this.createCloudLayer();

    // Listen for game resize events to update screen-space overlays
    this.scale.on(Phaser.Scale.Events.RESIZE, () => {
      this.updateWeatherOverlaySize();
    });

    // Create FPS counter in top-left corner
    this.fpsText = this.add.text(10, 10, "FPS: 60", {
      fontSize: "14px",
      color: "#00ff00",
      fontFamily: "monospace",
      backgroundColor: "#000000aa",
      padding: { x: 6, y: 4 },
    });
    this.fpsText.setScrollFactor(0); // Fixed to camera
    this.fpsText.setDepth(10000); // Above everything
  }

  /**
   * Check if there is an island under the given world coordinates.
   * Used by InputHandler so that right-clicks on islands do not
   * fall through as "empty ocean" clicks.
   */
  private hasIslandAtPosition(worldX: number, worldY: number): boolean {
    return this.getIslandAtPosition(worldX, worldY) !== null;
  }

  /**
   * Check if there is a rift scar under the given world coordinates.
   */
  private hasRiftScarAtPosition(worldX: number, worldY: number): boolean {
    return this.getRiftScarAtPosition(worldX, worldY) !== null;
  }

  /**
   * Get the rift scar at the given world coordinates (if any).
   */
  private getRiftScarAtPosition(worldX: number, worldY: number): any | null {
    if (!this.riftScarManager) return null;

    const scar = this.riftScarManager.getNearestScar();
    if (!scar) return null;

    const distance = Phaser.Math.Distance.Between(
      worldX,
      worldY,
      scar.x,
      scar.y,
    );
    // Check if within interaction radius (75px as set in RiftScar entity)
    if (distance < 75) {
      return scar.getScarData();
    }

    return null;
  }

  private worldToTileCoord(world: number): number {
    return Math.floor(world / TILE_SIZE);
  }

  private isTileInWorldBounds(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileY < 0) return false;
    const maxTileX: number = Math.floor((WORLD_WIDTH - 1) / TILE_SIZE);
    const maxTileY: number = Math.floor((WORLD_HEIGHT - 1) / TILE_SIZE);
    return tileX <= maxTileX && tileY <= maxTileY;
  }

  private isTileInCameraView(tileX: number, tileY: number): boolean {
    const worldView = this.cameras?.main?.worldView;
    if (!worldView) return true;
    const centerX: number = tileX * TILE_SIZE + TILE_SIZE / 2;
    const centerY: number = tileY * TILE_SIZE + TILE_SIZE / 2;
    return Phaser.Geom.Rectangle.Contains(worldView, centerX, centerY);
  }

  private isTileBlockedByLoadedIslands(tileX: number, tileY: number): boolean {
    if (!this.islandRenderer) return false;
    const islands = this.islandRenderer.getIslands();
    if (!islands || islands.size === 0) return false;

    const centerX: number = tileX * TILE_SIZE + TILE_SIZE / 2;
    const centerY: number = tileY * TILE_SIZE + TILE_SIZE / 2;

    const entries = Array.from(islands.values());
    for (let i = 0; i < entries.length; i++) {
      const island = entries[i].islandData;
      if (island.isPermanent && this.isServiceLocation(island)) {
        continue;
      }
      if (island.shape && island.shape.length >= 3) {
        const worldPoly = island.shape.map((p) => ({
          x: island.x + p.x,
          y: island.y + p.y,
        }));
        if (this.pointInPolygon(centerX, centerY, worldPoly)) {
          return true;
        }
      } else {
        const radiusTiles: number = Math.max(
          2,
          Math.round(island.radius / TILE_SIZE),
        );
        const islandTileX: number = Math.floor(island.x / TILE_SIZE);
        const islandTileY: number = Math.floor(island.y / TILE_SIZE);
        const dx: number = Math.abs(tileX - islandTileX);
        const dy: number = Math.abs(tileY - islandTileY);
        if (dx <= radiusTiles && dy <= radiusTiles) {
          return true;
        }
      }
    }

    return false;
  }

  private isServiceLocation(island: {
    id?: string;
    permanentName?: string | null;
  }): boolean {
    const islandId: string = String(island.id ?? "").toLowerCase();
    const permanentName: string = String(
      island.permanentName ?? "",
    ).toLowerCase();
    const normalized = `${islandId} ${permanentName}`;
    return (
      normalized.includes("trading_post") ||
      normalized.includes("trading post") ||
      normalized.includes("shipyard") ||
      normalized.includes("harbor_office") ||
      normalized.includes("harbor office") ||
      normalized.includes("tavern")
    );
  }

  private cachedIslandCollisionCheck: {
    tileX: number;
    tileY: number;
    blocked: boolean;
  } | null = null;

  private validateMoveTarget(
    worldX: number,
    worldY: number,
  ):
    | { ok: true }
    | { ok: false; reason: string; tileX: number; tileY: number } {
    const tileX: number = this.worldToTileCoord(worldX);
    const tileY: number = this.worldToTileCoord(worldY);

    if (!this.isTileInWorldBounds(tileX, tileY)) {
      return { ok: false, reason: "out_of_bounds", tileX, tileY };
    }

    // Skip ocean island collision checks when in sanctum - use sanctum collision map instead
    if (!this.isInSanctumInstance) {
      if (
        this.cachedIslandCollisionCheck?.tileX === tileX &&
        this.cachedIslandCollisionCheck?.tileY === tileY
      ) {
        if (this.cachedIslandCollisionCheck.blocked) {
          return { ok: false, reason: "blocked", tileX, tileY };
        }
      } else {
        const blocked = this.isTileBlockedByLoadedIslands(tileX, tileY);
        this.cachedIslandCollisionCheck = { tileX, tileY, blocked };
        if (blocked) {
          return { ok: false, reason: "blocked", tileX, tileY };
        }
      }

      // Also check placed tiles (construction, structures, etc.)
      const worldX = tileX * TILE_SIZE;
      const worldY = tileY * TILE_SIZE;
      if (
        this.placedTileRenderer &&
        (this.placedTileRenderer as any).isTileBlockedByPlacedTile
      ) {
        const blockedByPlacedTile = (
          this.placedTileRenderer as any
        ).isTileBlockedByPlacedTile(worldX, worldY);
        if (blockedByPlacedTile) {
          return { ok: false, reason: "blocked_by_placed_tile", tileX, tileY };
        }
      }

      if (this.oceanTileRenderer?.isOceanRockAtTile(tileX, tileY)) {
        return { ok: false, reason: "blocked_by_ocean_rock", tileX, tileY };
      }
    } else {
      // In sanctum: Check sanctum collision map (walls block movement)
      const sanctumCollisionMap = this.sanctumRenderer?.getCollisionMap();
      if (sanctumCollisionMap) {
        const collisionValue = sanctumCollisionMap.get(`${tileX},${tileY}`);
        // If tile is not in collision map at all, it's void (outside dungeon)
        if (collisionValue === undefined) {
          return { ok: false, reason: "sanctum_void", tileX, tileY };
        }
        // If tile is explicitly blocked (wall)
        if (collisionValue === true) {
          return { ok: false, reason: "sanctum_wall", tileX, tileY };
        }
      }
    }

    if (this.localShip) {
      const fromTileX: number = this.worldToTileCoord(this.localShip.x);
      const fromTileY: number = this.worldToTileCoord(this.localShip.y);
      const distTiles: number =
        Math.abs(tileX - fromTileX) + Math.abs(tileY - fromTileY);
      if (distTiles > this.CLIENT_MAX_TARGET_DISTANCE_TILES) {
        return { ok: false, reason: "too_far", tileX, tileY };
      }
    }

    return { ok: true };
  }

  private validateServerPath(
    path: Array<{ tileX: number; tileY: number }>,
    targetTile: { tileX: number; tileY: number } | null,
  ): boolean {
    if (!path || path.length === 0) return true;
    if (path.length > this.CLIENT_MAX_PATH_TILES) return false;
    let reportedMismatch: boolean = false;

    for (let i = 0; i < path.length; i++) {
      const step = path[i];
      if (!this.isTileInWorldBounds(step.tileX, step.tileY)) return false;

      if (this.isInSanctumInstance) {
        const sanctumCollisionMap = this.sanctumRenderer?.getCollisionMap();
        if (sanctumCollisionMap) {
          const collisionValue = sanctumCollisionMap.get(
            `${step.tileX},${step.tileY}`,
          );
          if (collisionValue === undefined || collisionValue === true) {
            return false;
          }
        }
      } else if (
        this.isTileInCameraView(step.tileX, step.tileY) &&
        this.isTileBlockedByLoadedIslands(step.tileX, step.tileY)
      ) {
        const isTargetStep =
          !!targetTile &&
          targetTile.tileX === step.tileX &&
          targetTile.tileY === step.tileY;
        if (isTargetStep) {
          return false;
        }
        if (!reportedMismatch) {
          this.socket?.emit("collision:mismatch", {
            tileX: step.tileX,
            tileY: step.tileY,
            clientBlocked: true,
          });
          reportedMismatch = true;
        }
      } else if (
        !this.isInSanctumInstance &&
        this.isTileInCameraView(step.tileX, step.tileY)
      ) {
        if (this.oceanTileRenderer?.isOceanRockAtTile(step.tileX, step.tileY)) {
          const isTargetStep =
            !!targetTile &&
            targetTile.tileX === step.tileX &&
            targetTile.tileY === step.tileY;
          if (isTargetStep) {
            return false;
          }
          if (!reportedMismatch) {
            this.socket?.emit("collision:mismatch", {
              tileX: step.tileX,
              tileY: step.tileY,
              clientBlocked: true,
            });
            reportedMismatch = true;
          }
        }

        // Also check placed tiles
        const worldX = step.tileX * TILE_SIZE;
        const worldY = step.tileY * TILE_SIZE;
        if (
          this.placedTileRenderer &&
          (this.placedTileRenderer as any).isTileBlockedByPlacedTile
        ) {
          if (
            (this.placedTileRenderer as any).isTileBlockedByPlacedTile(
              worldX,
              worldY,
            )
          ) {
            const isTargetStep =
              !!targetTile &&
              targetTile.tileX === step.tileX &&
              targetTile.tileY === step.tileY;
            if (isTargetStep) {
              return false;
            }
            if (!reportedMismatch) {
              this.socket?.emit("collision:mismatch", {
                tileX: step.tileX,
                tileY: step.tileY,
                clientBlocked: true,
              });
              reportedMismatch = true;
            }
          }
        }
      }

      if (i > 0) {
        const prev = path[i - 1];
        const dx: number = Math.abs(step.tileX - prev.tileX);
        const dy: number = Math.abs(step.tileY - prev.tileY);
        if (dx + dy > 2) {
          return false;
        }
      }
    }

    if (targetTile) {
      if (!this.isTileInWorldBounds(targetTile.tileX, targetTile.tileY))
        return false;
    }

    return true;
  }

  /**
   * Enter sanctum instance - hide ocean, show dungeon
   */
  public enterSanctum(
    instanceId: string,
    sanctumNumber?: number,
    sanctumType?: "survival" | "preservation" | "experiment",
    sanctumGrade?: "A" | "B" | "C" | "D",
  ): void {
    console.log(`🏛️ Entering sanctum instance: ${instanceId}`);

    // Set sanctum flag BEFORE processing ships so they render with correct visuals
    this.isInSanctumInstance = true;
    this.currentSanctumInstanceId = instanceId;

    // Stop all ship movement tweens for instant positioning
    this.shipRenderer?.getShips().forEach((ship: ShipSprite) => {
      if (ship.movementTween) {
        ship.movementTween.stop();
        ship.movementTween = undefined;
      }
      if (ship.bobbingTween) {
        ship.bobbingTween.stop();
        ship.bobbingTween = undefined;
      }
    });

    // Emit sanctum entered event with metadata (sanctumNumber is optional)
    if (sanctumType && sanctumGrade) {
      console.log(
        `🏛️ Emitting sanctum:entered event with type: ${sanctumType}, grade: ${sanctumGrade}`,
      );
      this.events.emit("sanctum:entered", {
        sanctumId: instanceId,
        number: sanctumNumber,
        type: sanctumType,
        grade: sanctumGrade,
      });
    } else {
      console.log(
        `⚠️ Not emitting sanctum:entered - missing metadata (type: ${sanctumType}, grade: ${sanctumGrade})`,
      );
    }

    // Store player's ocean position before entering sanctum
    if (this.localShip) {
      (this.localShip as any).oceanX = this.localShip.x;
      (this.localShip as any).oceanY = this.localShip.y;
      console.log(
        `🏛️ Stored ocean position: (${this.localShip.x}, ${this.localShip.y})`,
      );
    }

    // Remove camera bounds so it can follow player to sanctum coordinates
    if (this.cameras?.main) {
      this.cameras.main.removeBounds();
      console.log("🏛️ Camera bounds removed for sanctum");
    }

    // Hide ocean water grid and set background to dark ocean color (not pure black)
    this.oceanWaterRenderer?.setEnabled(false);
    this.cameras.main.setBackgroundColor("#0a1628");

    // Hide all ships
    this.shipRenderer
      ?.getShips()
      .forEach((ship: ShipSprite) => ship.setVisible(false));

    // Hide islands
    this.islandRenderer?.getIslands().forEach((island: any) => {
      island.graphics.setVisible(false);
      island.label?.setVisible(false);
      island.typeLabel?.setVisible(false);
      island.territoryLabel?.setVisible(false);
    });

    // Hide permanent locations (trading posts, harbor offices, taverns)
    this.permanentLocationRenderer?.getLocations().forEach((location: any) => {
      location.graphics?.setVisible(false);
      location.label?.setVisible(false);
      location.hitArea?.setVisible(false);
      location.serviceLabels?.forEach((label: any) => label.setVisible(false));
      location.safeLabel?.setVisible(false);
      location.safeZoneGraphics?.setVisible(false);
    });

    // Hide fishing spots
    this.fishingSpotRenderer?.getSpots().forEach((spot: any) => {
      if (spot.graphics) spot.graphics.setVisible(false);
      if (spot.label) spot.label.setVisible(false);
    });

    // Hide resource nodes
    this.resourceNodes.forEach((node: any) => {
      if (node.graphics) node.graphics.setVisible(false);
      if (node.hitArea) node.hitArea.setVisible(false);
      if (node.label) node.label.setVisible(false);
    });

    // Hide ambient NPCs (sailors, merchants, etc.)
    this.ambientNPCs.forEach((npc: any) => {
      if (npc.sprite) npc.sprite.setVisible(false);
      if (npc.label) npc.label.setVisible(false);
    });

    // Hide ALL other ships (including NPC ships/sailors) and disable their combat
    this.shipRenderer?.getShips().forEach((ship: ShipSprite) => {
      ship.setVisible(false);
      // Disable combat for all ocean ships
      if (ship !== this.localShip) {
        ship.setActive(false);
      }
    });

    // Hide rift storms
    if (this.riftStorms) {
      this.riftStorms.forEach((storm: any) => {
        if (storm.graphics) storm.graphics.setVisible(false);
        if (storm.label) storm.label.setVisible(false);
      });
    }

    // Hide world edge border
    if (this.worldBoundaryGraphics) {
      this.worldBoundaryGraphics.setVisible(false);
    }

    // Hide whirlpools - set renderer to inactive mode
    if (this.whirlpoolRenderer) {
      (this.whirlpoolRenderer as any).whirlpools?.forEach((wp: any) => {
        if (wp.graphics) wp.graphics.setVisible(false);
        if (wp.label) wp.label.setVisible(false);
      });
    }

    // Hide placed tiles (shipwrecks, clutter, etc.)
    if (this.placedTileRenderer) {
      (this.placedTileRenderer as any).placedTiles?.forEach((tiles: any[]) => {
        tiles.forEach((tile: any) => {
          if (tile.sprite) tile.sprite.setVisible(false);
          if (tile.label) tile.label.setVisible(false);
        });
      });
    }

    // Hide shipwrecks explicitly
    this.shipwrecks.forEach((wreck: any) => {
      wreck.setVisible(false);
    });

    // Hide sea monsters
    if (this.seaMonsterRenderer) {
      (this.seaMonsterRenderer as any).monsters?.forEach((monster: any) => {
        if (monster.container) monster.container.setVisible(false);
        if (monster.sprite) monster.sprite.setVisible(false);
      });
    }

    // Hide rift scars
    if (this.riftScarManager) {
      (this.riftScarManager as any).scars?.forEach((scar: any) => {
        if (scar.container) scar.container.setVisible(false);
        if (scar.graphics) scar.graphics.setVisible(false);
      });
    }

    // Hide cloud layers
    if (this.cloudLayer) this.cloudLayer.setVisible(false);
    if (this.cloudLayer2) this.cloudLayer2.setVisible(false);

    // Hide path graphics
    if (this.pathGraphics) this.pathGraphics.setVisible(false);
    if (this.combatPathGraphics) this.combatPathGraphics.setVisible(false);

    // Disable ocean water renderer and hide it completely
    if (this.oceanWaterRenderer) {
      this.oceanWaterRenderer.setEnabled(false);
      // Stop the animation timer to prevent any updates
      (this.oceanWaterRenderer as any).animationTimer?.destroy();
      (this.oceanWaterRenderer as any).animationTimer = null;
    }

    // Hide the grid graphics that shows tile outlines
    if (this.gridGraphics) {
      this.gridGraphics.clear();
      this.gridGraphics.setVisible(false);
    }

    // Recreate all ships with sanctum visuals (squares/circles instead of ship visuals)
    const shipsToRecreate = Array.from(
      this.shipRenderer?.getShips().values() || [],
    );
    shipsToRecreate.forEach((ship: ShipSprite) => {
      if (ship.shipId) {
        this.shipRenderer?.removeShip(ship.shipId);
      }
    });

    // Re-create ships with sanctum visuals
    shipsToRecreate.forEach((ship: ShipSprite) => {
      this.shipRenderer?.createShipSprite(ship.shipData);
    });

    // Activate sanctum renderer
    this.sanctumRenderer?.activate();

    // Make local ship visible in sanctum and set high depth to render above dungeon
    if (this.localShip) {
      this.localShip.setVisible(true);
      this.localShip.setDepth(1000); // Render above dungeon tiles
      this.localShip.setAlpha(1); // Ensure fully visible
      console.log(
        `🏛️ Local ship depth set to 1000, visible: ${this.localShip.visible}, alpha: ${this.localShip.alpha}`,
      );
    } else {
      console.warn("🏛️ WARNING: localShip is null when entering sanctum!");
    }

    // Camera will be positioned when room data arrives
    console.log(
      `🏛️ Sanctum renderer activated, waiting for room data to position camera`,
    );
  }

  /**
   * Exit sanctum instance - show ocean, hide dungeon
   * @param oceanReturnPosition Optional server-provided return position (preferred)
   */
  public exitSanctum(oceanReturnPosition?: { x: number; y: number }): void {
    console.log(`🏛️ Exiting sanctum instance`);

    this.isInSanctumInstance = false;
    this.currentSanctumInstanceId = null;

    // Emit sanctum exited event
    this.events.emit("sanctum:exited");

    // Restore ocean background color
    this.cameras.main.setBackgroundColor("#0a1628");

    // Show ocean water grid and update visible area
    this.oceanWaterRenderer?.setEnabled(true);
    if (this.oceanWaterRenderer) {
      this.oceanWaterRenderer.updateVisibleArea();
    }

    // Restore player's ocean position
    // Priority: 1) Server-provided return position, 2) Stored ocean position, 3) Default
    if (this.localShip) {
      let returnX: number;
      let returnY: number;

      if (oceanReturnPosition) {
        // Use server-provided return position (most reliable)
        returnX = oceanReturnPosition.x;
        returnY = oceanReturnPosition.y;
        console.log(
          `🏛️ Using server return position: (${returnX}, ${returnY})`,
        );
      } else if ((this.localShip as any).oceanX !== undefined) {
        // Fallback to stored ocean position
        returnX = (this.localShip as any).oceanX;
        returnY = (this.localShip as any).oceanY;
        console.log(`🏛️ Using stored ocean position: (${returnX}, ${returnY})`);
      } else {
        // Last resort: default position
        returnX = 4000;
        returnY = 4000;
        console.log(`🏛️ Using default position: (${returnX}, ${returnY})`);
      }

      this.localShip.x = returnX;
      this.localShip.y = returnY;

      // Clean up stored position
      delete (this.localShip as any).oceanX;
      delete (this.localShip as any).oceanY;

      console.log(`🏛️ Player teleported to ocean at (${returnX}, ${returnY})`);
    }

    // Recreate all ships with ocean visuals (ship visuals instead of squares/circles)
    const shipsToRecreate = Array.from(
      this.shipRenderer?.getShips().values() || [],
    );
    shipsToRecreate.forEach((ship: ShipSprite) => {
      if (ship.shipId) {
        this.shipRenderer?.removeShip(ship.shipId);
      }
    });

    // Re-create ships with ocean visuals
    shipsToRecreate.forEach((ship: ShipSprite) => {
      this.shipRenderer?.createShipSprite(ship.shipData);
    });

    // Show all ships
    this.shipRenderer
      ?.getShips()
      .forEach((ship: ShipSprite) => ship.setVisible(true));

    // Show islands
    this.islandRenderer?.getIslands().forEach((island: any) => {
      island.graphics.setVisible(true);
      island.label?.setVisible(true);
      island.typeLabel?.setVisible(true);
      island.territoryLabel?.setVisible(true);
    });

    // Show permanent locations
    this.permanentLocationRenderer?.getLocations().forEach((location: any) => {
      location.graphics?.setVisible(true);
      location.label?.setVisible(true);
      location.hitArea?.setVisible(true);
      location.serviceLabels?.forEach((label: any) => label.setVisible(true));
      location.safeLabel?.setVisible(true);
      location.safeZoneGraphics?.setVisible(true);
    });

    // Show fishing spots
    this.fishingSpotRenderer?.getSpots().forEach((spot: any) => {
      if (spot.graphics) spot.graphics.setVisible(true);
      if (spot.label) spot.label.setVisible(true);
    });

    // Show resource nodes
    this.resourceNodes.forEach((node: any) => {
      if (node.graphics) node.graphics.setVisible(true);
      if (node.hitArea) node.hitArea.setVisible(true);
      if (node.label) node.label.setVisible(true);
    });

    // Show ambient NPCs
    this.ambientNPCs.forEach((npc: any) => {
      if (npc.sprite) npc.sprite.setVisible(true);
      if (npc.label) npc.label.setVisible(true);
    });

    // Show rift storms
    if (this.riftStorms) {
      this.riftStorms.forEach((storm: any) => {
        if (storm.graphics) storm.graphics.setVisible(true);
        if (storm.label) storm.label.setVisible(true);
      });
    }

    // Show whirlpools
    if (this.whirlpoolRenderer) {
      (this.whirlpoolRenderer as any).whirlpools?.forEach((wp: any) => {
        if (wp.graphics) wp.graphics.setVisible(true);
        if (wp.label) wp.label.setVisible(true);
      });
    }

    // Show world edge border
    if (this.worldBoundaryGraphics) {
      this.worldBoundaryGraphics.setVisible(true);
    }

    // Show placed tiles
    if (this.placedTileRenderer) {
      (this.placedTileRenderer as any).placedTiles?.forEach((tiles: any[]) => {
        tiles.forEach((tile: any) => {
          if (tile.sprite) tile.sprite.setVisible(true);
          if (tile.label) tile.label.setVisible(true);
        });
      });
    }

    // Show shipwrecks explicitly
    this.shipwrecks.forEach((wreck: any) => {
      wreck.setVisible(true);
    });

    // Deactivate sanctum renderer and fog of war
    this.sanctumRenderer?.deactivate();

    // Disable fog of war system (sanctum-specific)
    if (this.sanctumRenderer && (this.sanctumRenderer as any).fogOfWarSystem) {
      (this.sanctumRenderer as any).fogOfWarSystem.setEnabled(false);
    }

    // Re-enable ocean grid graphics
    if (this.gridGraphics) {
      this.gridGraphics.setVisible(true);
    }

    // Force re-enable ocean water renderer and update it
    if (this.oceanWaterRenderer) {
      (this.oceanWaterRenderer as any).setEnabled(true);
      (this.oceanWaterRenderer as any).updateVisibleArea();
      console.log("🏛️ Ocean water renderer re-enabled");
    }

    // Re-enable camera following
    if (this.localShip) {
      this.cameras.main.startFollow(this.localShip, true, 0.1, 0.1);
      // Center camera on player immediately
      this.cameras.main.centerOn(this.localShip.x, this.localShip.y);
    }

    // Force update visible grid and redraw everything
    this.updateVisibleGrid();

    console.log(`🏛️ Sanctum exit complete - ocean visuals restored`);
  }

  /**
   * Render sanctum room (legacy single-room method)
   */
  public renderSanctumRoom(room: any): void {
    if (!this.isInSanctumInstance) {
      console.warn("🏛️ Cannot render room - not in sanctum instance");
      return;
    }

    console.log(`🏛️ Rendering sanctum room: ${room.id} (${room.type})`);
    console.log(
      `🏛️ Room position: (${room.tileX}, ${room.tileY}), size: ${room.width}x${room.height}`,
    );
    this.sanctumRenderer?.renderRoom(room);

    // Move player ship to sanctum room center
    const roomCenterX = (room.tileX + room.width / 2) * TILE_SIZE;
    const roomCenterY = (room.tileY + room.height / 2) * TILE_SIZE;

    if (this.localShip) {
      console.log(
        `🏛️ Moving player to sanctum room at (${roomCenterX}, ${roomCenterY})`,
      );

      // Stop any ship movement (player becomes on-foot in sanctum)
      (this.localShip as any).velocityX = 0;
      (this.localShip as any).velocityY = 0;

      // Set position
      this.localShip.x = roomCenterX;
      this.localShip.y = roomCenterY;

      // Stop camera following and center on room
      this.cameras.main.stopFollow();
      this.cameras.main.centerOn(roomCenterX, roomCenterY);
    } else {
      console.warn("🏛️ No local ship found, centering camera on room");
      this.cameras.main.centerOn(roomCenterX, roomCenterY);
    }
  }

  private drawStormOverlay(width: number, height: number): void {
    if (!this.weatherOverlay) return;

    // Dark storm overlay (no lightning flashes for epilepsy safety)
    this.weatherOverlay.fillStyle(0x1a1a2e, 0.4);
    this.weatherOverlay.fillRect(0, 0, width, height);
  }

  private stopWeatherTimer(): void {
    if (this.weatherTimer) {
      this.weatherTimer.destroy();
      this.weatherTimer = null;
    }
  }

  /**
   * Render sanctum dungeon with type-specific styling
   */
  public renderSanctumDungeon(
    layout: any,
    sanctumType?: "survival" | "experiment" | "preservation",
  ): void {
    // Ensure we're in sanctum mode (handles race condition where dungeon_layout arrives before instance_created)
    if (!this.isInSanctumInstance && layout.instanceId) {
      console.log("🏛️ Not in sanctum mode yet, entering now...");
      this.enterSanctum(layout.instanceId);
    }

    if (!this.isInSanctumInstance) {
      console.warn("🏛️ Cannot render dungeon - not in sanctum instance");
      return;
    }

    if (!this.sanctumRenderer) {
      console.error("🏛️ SanctumRenderer not initialized");
      return;
    }

    console.log(
      `🏛️ Rendering full sanctum dungeon: ${layout.rooms.length} rooms, ${layout.corridors.length} corridors`,
    );

    // Set sanctum type to apply color palette and atmosphere
    if (sanctumType) {
      this.sanctumRenderer.setSanctumType(sanctumType);
    }

    // Render the dungeon with grade indicator
    this.sanctumRenderer.renderDungeon(
      layout,
      this.currentSanctumGrade as "A" | "B" | "C" | "D" | undefined,
    );

    console.log(
      `🏛️ Rendering sanctum dungeon with type: ${sanctumType || "unknown"}`,
    );
  }

  /**
   * Store current sanctum grade for rendering
   */
  private currentSanctumGrade: "A" | "B" | "C" | "D" | null = null;

  /**
   * Set current sanctum grade
   */
  setSanctumGrade(grade: "A" | "B" | "C" | "D"): void {
    this.currentSanctumGrade = grade;
  }

  /**
   * Update enemy in sanctum
   */
  public updateSanctumEnemy(
    enemyId: string,
    hp: number,
    defeated: boolean,
  ): void {
    this.sanctumRenderer?.updateEnemy(enemyId, hp, defeated);
  }

  /**
   * Update container in sanctum
   */
  public updateSanctumContainer(containerId: string, looted: boolean): void {
    this.sanctumRenderer?.updateContainer(containerId, looted);
  }

  /**
   * Get current sanctum instance ID
   */
  public getSanctumInstanceId(): string | null {
    return this.currentSanctumInstanceId;
  }

  /**
   * Find sanctum container by ID from current dungeon layout
   */
  private findSanctumContainer(containerId: string): {
    worldX: number;
    worldY: number;
    containerId: string;
    rarity: string;
  } | null {
    const currentRoom = this.sanctumRenderer?.getCurrentRoom();
    if (!currentRoom) {
      // Try to find in full dungeon layout if available
      const dungeonLayout = (this.sanctumRenderer as any)?.currentDungeonLayout;
      if (dungeonLayout) {
        for (const room of dungeonLayout.rooms) {
          const container = room.lootContainers?.find(
            (c: any) => c.id === containerId,
          );
          if (container) {
            return {
              worldX:
                (room.tileX + container.tileX) * TILE_SIZE + TILE_SIZE / 2,
              worldY:
                (room.tileY + container.tileY) * TILE_SIZE + TILE_SIZE / 2,
              containerId: container.id,
              rarity: container.rarity,
            };
          }
        }
      }
      return null;
    }

    const container = currentRoom.lootContainers?.find(
      (c: any) => c.id === containerId,
    );
    if (!container) return null;

    return {
      worldX: (currentRoom.tileX + container.tileX) * TILE_SIZE + TILE_SIZE / 2,
      worldY: (currentRoom.tileY + container.tileY) * TILE_SIZE + TILE_SIZE / 2,
      containerId: container.id,
      rarity: container.rarity,
    };
  }

  /**
   * Check if in sanctum
   */
  public isInSanctum(): boolean {
    return this.isInSanctumInstance;
  }

  /**
   * Get island at position (for right-click context menu)
   * Returns null if no island is found.
   * Checks both regular islands and permanent locations.
   */
  private getIslandAtPosition(
    worldX: number,
    worldY: number,
  ): IslandData | null {
    // Check regular islands first
    if (this.islandRenderer) {
      const islands = this.islandRenderer.getIslands();
      if (islands && islands.size > 0) {
        const entries = Array.from(islands.values());
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          const dx = worldX - entry.x;
          const dy = worldY - entry.y;
          const radius: number = entry.islandData.radius + TILE_SIZE; // small padding
          if (dx * dx + dy * dy <= radius * radius) {
            return entry.islandData;
          }
        }
      }
    }

    // Check permanent locations
    if (this.permanentLocationRenderer) {
      const locations = this.permanentLocationRenderer.getLocations();
      if (locations && locations.size > 0) {
        const entries = Array.from(locations.values());
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          const dx = worldX - entry.x;
          const dy = worldY - entry.y;
          const radius: number = entry.islandData.radius + TILE_SIZE; // small padding
          if (dx * dx + dy * dy <= radius * radius) {
            return entry.islandData;
          }
        }
      }
    }

    return null;
  }

  private pointInPolygon(
    px: number,
    py: number,
    poly: Array<{ x: number; y: number }>,
  ): boolean {
    let inside: boolean = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi: number = poly[i].x;
      const yi: number = poly[i].y;
      const xj: number = poly[j].x;
      const yj: number = poly[j].y;
      const dyDiff: number = yj - yi;
      // Skip horizontal edges
      if (Math.abs(dyDiff) < 1e-6) continue;
      const intersect: boolean =
        (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / dyDiff + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Create subtle drifting cloud overlay
   */
  private createCloudLayer(): void {
    // Create procedural blocky cloud texture that matches grid aesthetic
    const cloudGraphics = this.make.graphics({ x: 0, y: 0 });
    const tileSize: number = TILE_SIZE; // 16px grid base
    const textureSize: number = 256;
    cloudGraphics.clear();

    // Dark base sky tile so clouds feel subtle
    cloudGraphics.fillStyle(COLORS.deepOcean, 1);
    cloudGraphics.fillRect(0, 0, textureSize, textureSize);

    // Draw chunky square "cloud" patches snapped to the grid
    cloudGraphics.fillStyle(0xffffff, 0.05);
    for (let i = 0; i < 60; i++) {
      const gridX: number = Math.floor(
        Math.random() * (textureSize / tileSize),
      );
      const gridY: number = Math.floor(
        Math.random() * (textureSize / tileSize),
      );
      const patchW: number = (1 + Math.floor(Math.random() * 3)) * tileSize;
      const patchH: number = (1 + Math.floor(Math.random() * 2)) * tileSize;
      const x: number = gridX * tileSize;
      const y: number = gridY * tileSize;
      cloudGraphics.fillRect(x, y, patchW, patchH);
    }

    // Slight highlight squares for extra texture
    cloudGraphics.fillStyle(0xffffff, 0.08);
    for (let i = 0; i < 25; i++) {
      const gridX: number = Math.floor(
        Math.random() * (textureSize / tileSize),
      );
      const gridY: number = Math.floor(
        Math.random() * (textureSize / tileSize),
      );
      const x: number = gridX * tileSize;
      const y: number = gridY * tileSize;
      cloudGraphics.fillRect(x, y, tileSize, tileSize);
    }

    cloudGraphics.generateTexture("cloud_tile", textureSize, textureSize);
    cloudGraphics.destroy();

    // Create two large parallax layers that live in world space above the ocean
    const layerWidth: number = WORLD_WIDTH * 1.5;
    const layerHeight: number = WORLD_HEIGHT * 1.5;
    const centerX: number = WORLD_WIDTH / 2;
    const centerY: number = WORLD_HEIGHT / 2;

    // Farther, slower layer
    this.cloudLayer = this.add.tileSprite(
      centerX,
      centerY,
      layerWidth,
      layerHeight,
      "cloud_tile",
    );
    this.cloudLayer.setScrollFactor(0.5, 0.5); // Parallax relative to world
    this.cloudLayer.setDepth(-8); // Above ocean background, below grid/ships
    this.cloudLayer.setAlpha(0.35);

    // Closer, slightly faster layer
    this.cloudLayer2 = this.add.tileSprite(
      centerX,
      centerY,
      layerWidth,
      layerHeight,
      "cloud_tile",
    );
    this.cloudLayer2.setScrollFactor(0.7, 0.7);
    this.cloudLayer2.setDepth(-7);
    this.cloudLayer2.setAlpha(0.28);

    // ============ BACKGROUND TAB VISIBILITY HANDLING ============
    // Listen for visibility changes to prevent rubberbanding when window is backgrounded
    // When tab comes back into focus, Phaser tweens would normally "catch up" by interpolating
    // through accumulated frames, causing NPCs to rubberbands. This handler prevents that.
    const handleVisibilityChange = () => {
      const wasVisible = this.isPageVisible;
      this.isPageVisible = !document.hidden;

      if (!wasVisible && this.isPageVisible) {
        // Page just became visible after being hidden
        // Process any queued ambient NPC updates in a single batch with immediate positioning
        console.log("🎬 Page became visible - processing queued NPC updates");
        if (this.ambientNPCPendingUpdates.size > 0) {
          const updates = Array.from(this.ambientNPCPendingUpdates.values());
          this.ambientNPCPendingUpdates.clear();

          // Stop all existing tweens to prevent catch-up interpolation
          updates.forEach((data) => {
            const npc = this.ambientNPCs.get(data.id);
            if (npc) {
              const existingTween = (npc as any).movementTween as
                | Phaser.Tweens.Tween
                | undefined;
              if (existingTween) {
                existingTween.stop();
              }

              // Update position immediately without tweening to avoid catch-up motion
              npc.setPosition(data.x, data.y);
              (npc as any).npcData = data;
              this.updateAmbientNPCVisuals(npc, data);
            }
          });

          console.log(
            `✅ Processed ${updates.length} queued NPC updates with instant positioning`,
          );
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup listener on scene shutdown
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });
  }

  /**
   * Spawn a wave animation at a random visible location
   */
  private spawnWaveEffect(): void {
    // Don't spawn if we have too many
    if (this.waveSprites.length >= this.MAX_WAVE_SPRITES) return;

    const camera = this.cameras.main;
    const marginTiles: number = 4;
    const margin: number = marginTiles * TILE_SIZE;

    // Random position within visible area, then snap to grid center
    const rawX: number =
      camera.scrollX + margin + Math.random() * (camera.width - margin * 2);
    const rawY: number =
      camera.scrollY + margin + Math.random() * (camera.height - margin * 2);
    const tileX: number = Math.floor(rawX / TILE_SIZE);
    const tileY: number = Math.floor(rawY / TILE_SIZE);
    const x: number = tileX * TILE_SIZE + TILE_SIZE / 2;
    const y: number = tileY * TILE_SIZE + TILE_SIZE / 2;

    // Create a tiny pixel-art wave pattern confined to a single tile
    const wave = this.add.graphics();
    wave.setDepth(-4); // Just above ocean background
    wave.setPosition(x, y);

    const half: number = TILE_SIZE / 2;
    const quarter: number = TILE_SIZE / 4;
    const color: number = 0x4a90a4;
    const alpha: number = 0.45;

    // Choose a simple variant so waves do not all look identical
    const variant: number = Math.floor(Math.random() * 3);
    wave.lineStyle(1, color, alpha);
    wave.fillStyle(color, alpha * 0.7);

    if (variant === 0) {
      // Three short horizontal crests
      wave.strokeLineShape(
        new Phaser.Geom.Line(-half + 2, -quarter, half - 2, -quarter),
      );
      wave.strokeLineShape(new Phaser.Geom.Line(-half + 4, 0, half - 4, 0));
      wave.strokeLineShape(
        new Phaser.Geom.Line(-half + 2, quarter, half - 2, quarter),
      );
    } else if (variant === 1) {
      // Staggered blocks suggesting a choppy tile
      wave.fillRect(-half + 2, -quarter - 1, quarter, 2);
      wave.fillRect(-quarter / 2, 0 - 1, quarter, 2);
      wave.fillRect(0, quarter - 1, quarter + 2, 2);
    } else {
      // Small square ripples towards the top of the tile
      wave.strokeRect(-quarter, -quarter, quarter * 2, quarter);
      wave.strokeRect(-quarter / 2, -quarter + 2, quarter, quarter / 2);
    }

    // Subtle fade-out, no expanding shapes to avoid flashes
    this.tweens.add({
      targets: wave,
      alpha: 0,
      duration: 900,
      ease: "Sine.easeOut",
      onComplete: () => {
        wave.destroy();
        const idx = this.waveSprites.indexOf(
          wave as unknown as Phaser.GameObjects.Sprite,
        );
        if (idx > -1) this.waveSprites.splice(idx, 1);
      },
    });

    this.waveSprites.push(wave as unknown as Phaser.GameObjects.Sprite);
  }

  /**
   * Spawn an explosion effect at a world position (for tile demolition)
   * Terraria-style debris and dust effect
   */
  public spawnExplosionEffect(
    worldX: number,
    worldY: number,
    materialColor: number = 0xff6600,
  ): void {
    const explosionSize = 64;

    // Create main explosion graphics object
    const explosion = this.add.graphics();
    explosion.setDepth(50); // Above tiles, below ships

    // Explosion particle colors
    const darkColor = 0x2a2a2a; // Dark gray for smoke

    // Main explosion burst
    const center = explosionSize / 2;
    explosion.fillStyle(0xff8800, 1); // Orange burst
    explosion.fillCircle(0, 0, center);

    // Bright inner core
    explosion.fillStyle(0xffff00, 0.8);
    explosion.fillCircle(0, 0, center * 0.6);

    // Debris chunks (small squares flying out)
    explosion.fillStyle(materialColor, 0.9);
    const numDebris = 8;
    for (let i = 0; i < numDebris; i++) {
      const angle = (i / numDebris) * Math.PI * 2;
      const distance = center * 0.8;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      explosion.fillRect(dx - 4, dy - 4, 8, 8);
    }

    // Dust cloud rings
    explosion.lineStyle(1, darkColor, 0.5);
    explosion.strokeCircle(0, 0, center * 0.5);
    explosion.strokeCircle(0, 0, center * 0.75);

    explosion.setPosition(worldX, worldY);
    explosion.setScale(1, 1);
    explosion.setAlpha(1);

    // Expanding ring animation (fast burst outward)
    this.tweens.add({
      targets: explosion,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 350,
      ease: "Quad.easeOut",
      onComplete: () => {
        explosion.destroy();
      },
    });

    // Dust puff effects at debris positions
    for (let i = 0; i < 4; i++) {
      const puffX = worldX + (Math.random() - 0.5) * explosionSize * 1.5;
      const puffY = worldY + (Math.random() - 0.5) * explosionSize * 1.5;

      const puff = this.add.graphics();
      puff.setDepth(45);
      puff.fillStyle(darkColor, 0.6);
      puff.fillCircle(0, 0, 8);
      puff.setPosition(puffX, puffY);

      this.tweens.add({
        targets: puff,
        scaleX: 2.5,
        scaleY: 2.5,
        alpha: 0,
        duration: 600,
        ease: "Sine.easeOut",
        onComplete: () => puff.destroy(),
      });
    }
  }

  /**
   * Get a bright version of a material color for explosion effects
   */
  private brightColorForMaterial(materialColor: number): number {
    switch (materialColor) {
      case 0xd2691e: // Wood - bright orange
        return 0xffa500;
      case 0x808080: // Stone - bright gray
        return 0xd3d3d3;
      case 0xa9a9a9: // Metal - bright silver
        return 0xefefef;
      default:
        return 0xffff00;
    }
  }

  public setCollisionDebugEnabled(enabled: boolean): void {
    this.collisionDebugEnabled = enabled;
    if (enabled) {
      if (!this.collisionDebugGraphics) {
        this.collisionDebugGraphics = this.add.graphics();
        this.collisionDebugGraphics.setDepth(1000); // Render above world layers
      }
      this.collisionDebugGraphics.setVisible(true);
      // Kick off an immediate draw so the user sees feedback without waiting for the timer tick
      void this.refreshCollisionDebugOverlay();
      if (!this.collisionDebugTimer) {
        this.collisionDebugTimer = this.time.addEvent({
          delay: 500,
          loop: true,
          callback: () => {
            void this.refreshCollisionDebugOverlay();
          },
        });
      }
    } else {
      if (this.collisionDebugTimer) {
        this.collisionDebugTimer.remove();
        this.collisionDebugTimer = null;
      }
      if (this.collisionDebugGraphics) {
        this.collisionDebugGraphics.clear();
        this.collisionDebugGraphics.destroy();
        this.collisionDebugGraphics = null;
      }
    }
  }

  public setHitboxDebugEnabled(enabled: boolean): void {
    this.hitboxDebugEnabled = enabled;
    if (enabled) {
      if (!this.hitboxDebugGraphics) {
        this.hitboxDebugGraphics = this.add.graphics();
        this.hitboxDebugGraphics.setDepth(1000); // Above everything
      }
      this.hitboxDebugGraphics.setVisible(true);
      this.refreshHitboxDebugOverlay();
    } else {
      if (this.hitboxDebugGraphics) {
        this.hitboxDebugGraphics.clear();
        this.hitboxDebugGraphics.setVisible(false);
      }
    }
  }

  private refreshHitboxDebugOverlay(): void {
    if (!this.hitboxDebugEnabled || !this.hitboxDebugGraphics) return;

    this.hitboxDebugGraphics.clear();

    // Draw ship hitboxes
    this.ships.forEach((ship) => {
      const shipData = ship.shipData;
      const sizeMultiplier = shipData.shipType
        ? shipData.shipType === "galleon"
          ? 1.0
          : shipData.shipType === "brigantine"
            ? 0.8
            : 0.6
        : 0.6;
      const hitboxSize = TILE_SIZE * sizeMultiplier * HITBOX_MULTIPLIER;

      this.hitboxDebugGraphics!.lineStyle(2, HITBOX_DEBUG_COLORS.ship, 0.8);
      this.hitboxDebugGraphics!.strokeRect(
        ship.x - hitboxSize / 2,
        ship.y - hitboxSize / 2,
        hitboxSize,
        hitboxSize,
      );
    });

    // Draw NPC hitboxes
    this.npcs.forEach((npc) => {
      const npcData = (npc as any).npcData as NPCData | undefined;
      if (!npcData) return;

      const isAmbient = npcData.ambientCategory !== undefined;
      const color = isAmbient
        ? HITBOX_DEBUG_COLORS.ambientNpc
        : HITBOX_DEBUG_COLORS.npc;
      const radius = isAmbient
        ? AMBIENT_NPC_HITBOX_RADIUS
        : (TILE_SIZE * HITBOX_MULTIPLIER) / 2;

      this.hitboxDebugGraphics!.lineStyle(2, color, 0.8);
      this.hitboxDebugGraphics!.strokeCircle(npc.x, npc.y, radius);
    });

    // Draw resource node hitboxes
    this.resourceNodes.forEach((node) => {
      this.hitboxDebugGraphics!.lineStyle(
        2,
        HITBOX_DEBUG_COLORS.resourceNode,
        0.8,
      );
      this.hitboxDebugGraphics!.strokeRect(
        node.x - RESOURCE_NODE_HITBOX_SIZE / 2,
        node.y - RESOURCE_NODE_HITBOX_SIZE / 2,
        RESOURCE_NODE_HITBOX_SIZE,
        RESOURCE_NODE_HITBOX_SIZE,
      );
    });

    // Draw shipwreck hitboxes
    this.shipwrecks.forEach((wreck) => {
      this.hitboxDebugGraphics!.lineStyle(
        2,
        HITBOX_DEBUG_COLORS.shipwreck,
        0.8,
      );
      this.hitboxDebugGraphics!.strokeRect(
        wreck.x - SHIPWRECK_HITBOX_WIDTH / 2,
        wreck.y - 10 - SHIPWRECK_HITBOX_HEIGHT / 2,
        SHIPWRECK_HITBOX_WIDTH,
        SHIPWRECK_HITBOX_HEIGHT,
      );
    });

    // Draw floating bottle hitboxes (debris)
    this.floatingBottles.forEach((bottle: Phaser.GameObjects.Container) => {
      this.hitboxDebugGraphics!.lineStyle(2, HITBOX_DEBUG_COLORS.debris, 0.8);
      this.hitboxDebugGraphics!.strokeRect(
        bottle.x - DEBRIS_HITBOX_SIZE / 2,
        bottle.y - DEBRIS_HITBOX_SIZE / 2,
        DEBRIS_HITBOX_SIZE,
        DEBRIS_HITBOX_SIZE,
      );
    });

    // Draw loot container hitboxes
    this.lootContainerMarkers.forEach(
      (container: Phaser.GameObjects.Container) => {
        this.hitboxDebugGraphics!.lineStyle(
          2,
          HITBOX_DEBUG_COLORS.lootContainer,
          0.8,
        );
        this.hitboxDebugGraphics!.strokeRect(
          container.x - LOOT_CONTAINER_HITBOX_SIZE / 2,
          container.y - LOOT_CONTAINER_HITBOX_SIZE / 2,
          LOOT_CONTAINER_HITBOX_SIZE,
          LOOT_CONTAINER_HITBOX_SIZE,
        );
      },
    );
  }

  update(time: number, delta: number): void {
    // Update FPS counter (every 100ms to reduce overhead)
    if (this.fpsText && time % 100 === 0) {
      const fps = Math.round(this.game.loop.actualFps);
      this.fpsText.setText(`FPS: ${fps}`);
    }

    if (this.cameraFollowEnabled && this.localShip) {
      this.cameras.main.centerOn(this.localShip.x, this.localShip.y);
    }

    // Update pixel rain renderer for storms
    if (this.pixelRainRenderer) {
      this.pixelRainRenderer.update(delta);
    }

    // Update ocean tile renderer using world view bounds (handles zoom correctly)
    const camera = this.cameras.main;
    const view = camera.worldView;
    this.oceanTileRenderer.update(
      view.x,
      view.y,
      view.width,
      view.height,
      delta,
    );

    // Skip whirlpool renderer updates when in sanctum
    if (this.whirlpoolRenderer && !this.isInSanctumInstance) {
      this.whirlpoolRenderer.update(delta);
    }

    // Update rift scars (every 100ms to reduce overhead)
    if (this.localShip && time % 100 === 0) {
      this.riftScarManager.update(
        time,
        delta,
        this.localShip.x,
        this.localShip.y,
      );
    }

    // Check for pending fishing spot arrival (when player arrives)
    const pendingFishingSpot =
      this.inputInteractionManager?.getPendingFishingSpot();
    if (pendingFishingSpot && this.localShip && !this.isInSanctumInstance) {
      const velocityX =
        (this.localShip as any).velocityX ||
        this.localShip.shipData?.velocityX ||
        0;
      const velocityY =
        (this.localShip as any).velocityY ||
        this.localShip.shipData?.velocityY ||
        0;
      const isStationary =
        Math.abs(velocityX) < 0.01 && Math.abs(velocityY) < 0.01;

      if (isStationary) {
        const spotWorldX = pendingFishingSpot.tileX * TILE_SIZE + TILE_SIZE / 2;
        const spotWorldY = pendingFishingSpot.tileY * TILE_SIZE + TILE_SIZE / 2;
        const dist = Math.hypot(
          this.localShip.x - spotWorldX,
          this.localShip.y - spotWorldY,
        );

        if (dist < TILE_SIZE * 1.5) {
          this.fishingManager.startFishing(pendingFishingSpot);
          this.inputInteractionManager?.clearPendingFishingSpot();
        }
      }
    }

    // Check for pending sanctum container gathering (when player arrives)
    if (
      this.pendingSanctumGather &&
      this.localShip &&
      this.isInSanctumInstance
    ) {
      const velocityX =
        (this.localShip as any).velocityX ||
        this.localShip.shipData?.velocityX ||
        0;
      const velocityY =
        (this.localShip as any).velocityY ||
        this.localShip.shipData?.velocityY ||
        0;
      const isStationary =
        Math.abs(velocityX) < 0.01 && Math.abs(velocityY) < 0.01;

      if (isStationary) {
        const container = this.findSanctumContainer(
          this.pendingSanctumGather.containerId,
        );
        if (container) {
          const dist = Math.hypot(
            this.localShip.x - container.worldX,
            this.localShip.y - container.worldY,
          );
          if (dist < TILE_SIZE * 2) {
            this.socket?.emit("sanctum:loot_container", {
              instanceId: this.getSanctumInstanceId(),
              containerId: this.pendingSanctumGather.containerId,
              playerX: this.localShip.x,
              playerY: this.localShip.y,
            });
            this.pendingSanctumGather = null;
          }
        }
      }
    }

    // Cloud parallax movement based on camera position for proper parallax effect
    if (this.cloudLayer) {
      // Slower layer moves at 50% of camera speed (matching scrollFactor 0.5)
      this.cloudLayer.tilePositionX = this.cameras.main.scrollX * 0.5;
      this.cloudLayer.tilePositionY = this.cameras.main.scrollY * 0.5;
    }
    if (this.cloudLayer2) {
      // Faster layer moves at 70% of camera speed (matching scrollFactor 0.7)
      this.cloudLayer2.tilePositionX = this.cameras.main.scrollX * 0.7;
      this.cloudLayer2.tilePositionY = this.cameras.main.scrollY * 0.7;
    }

    // Spawn occasional subtle wave effects (slow, ambient ocean motion)
    this.waveTimer += delta;
    if (this.waveTimer > 6000 && Math.random() < 0.2) {
      this.spawnWaveEffect();
      this.waveTimer = 0;
    }

    // Update combat tracking line (direct line to target)
    if (this.combatManager) {
      this.combatManager.updateCombatTrackingLine();
      // Keep focus prompt/availability in sync with real combat eligibility
      this.combatManager.syncCannonFocusEligibility();
      // Update target HP in cannon focus overlay
      this.combatManager.updateTargetInfo();
    }

    // Update HUD (every 50ms to reduce overhead while maintaining responsiveness)
    if (time % 50 === 0) {
      this.updateHUD();
      this.updateReloadIndicator();
    }

    // Update hitbox debug overlay (every 100ms to reduce overhead)
    if (this.hitboxDebugEnabled && time % 100 === 0) {
      this.refreshHitboxDebugOverlay();
    }

    // Update fog of war in sanctum (every 100ms to reduce overhead)
    if (this.isInSanctumInstance && this.localShip && time % 100 === 0) {
      this.sanctumRenderer?.updateFogOfWar(this.localShip.x, this.localShip.y);
    }

    // Update visible grid overlay (every 100ms - skip in sanctum)
    if (!this.isInSanctumInstance && time % 100 === 0) {
      this.updateVisibleGrid();
    }

    // Update ambient NPC culling (every 250ms to reduce overhead)
    if (
      !this.isInSanctumInstance &&
      !this.isInRiftInstance &&
      time % 250 === 0
    ) {
      this.updateAmbientNPCCulling();
    }

    // Load nearby islands via IslandRenderer system (every 100ms)
    // Skip island loading for whirlpool and sanctum instances (they have their own content)
    if (
      this.localShip &&
      !this.isInWhirlpoolInstance &&
      !this.isInSanctumInstance &&
      time % 100 === 0
    ) {
      const worldView = this.cameras.main.worldView;
      this.islandRenderer.loadVisibleIslands(worldView, {
        paddingPx: TILE_SIZE * 40,
      });
    }
  }

  private async refreshCollisionDebugOverlay(): Promise<void> {
    if (
      !this.collisionDebugEnabled ||
      !this.localShip ||
      !this.collisionDebugGraphics
    ) {
      return;
    }

    const tileX = Math.floor(this.localShip.x / TILE_SIZE);
    const tileY = Math.floor(this.localShip.y / TILE_SIZE);
    const radiusTiles = 200;

    const apiUrl: string =
      process.env.REACT_APP_API_URL || "http://localhost:3001";
    try {
      // Try dev endpoint first, fall back to prod if not available
      let response: Response = await fetch(
        `${apiUrl}/api/admin/dev/collision-area?x=${tileX}&y=${tileY}&radius=${radiusTiles}`,
      );

      // If dev endpoint fails, try prod endpoint
      if (!response.ok) {
        response = await fetch(
          `${apiUrl}/api/admin/prod/collision-area?x=${tileX}&y=${tileY}&radius=${radiusTiles}`,
        );
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        console.error(
          `Failed to refresh collision debug overlay: HTTP ${response.status} ${text.slice(0, 200)}`,
        );
        return;
      }
      const data: unknown = await response.json();
      const tiles: Array<{
        tileX: number;
        tileY: number;
        reason?: string | null;
      }> =
        typeof data === "object" &&
        data !== null &&
        "tiles" in (data as Record<string, unknown>)
          ? ((data as Record<string, unknown>).tiles as Array<{
              tileX: number;
              tileY: number;
              reason?: string | null;
            }>)
          : [];

      if (!this.collisionDebugEnabled || !this.collisionDebugGraphics) {
        return;
      }

      this.collisionDebugGraphics.clear();
      tiles.forEach((tile) => {
        const worldX = tile.tileX * TILE_SIZE;
        const worldY = tile.tileY * TILE_SIZE;
        const reason = tile.reason === "entity" ? "entity" : "island";
        const color = reason === "entity" ? 0x00ff00 : 0xff0000;
        this.collisionDebugGraphics!.lineStyle(1, color, 0.8);
        this.collisionDebugGraphics!.fillStyle(color, 0.35);
        this.collisionDebugGraphics!.fillRect(
          worldX,
          worldY,
          TILE_SIZE,
          TILE_SIZE,
        );
        this.collisionDebugGraphics!.strokeRect(
          worldX,
          worldY,
          TILE_SIZE,
          TILE_SIZE,
        );
      });
    } catch (error) {
      console.error("Failed to refresh collision debug overlay:", error);
    }
  }

  // ============ SETUP ============

  // Grid graphics - redrawn each frame for visible area only
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private lastGridCenterX: number = -1;
  private lastGridCenterY: number = -1;

  private drawOcean(): void {
    // Simple solid background - match ocean tile color for seamless appearance
    // Ocean tiles are light blue, so use a matching background color
    const oceanTileColor = 0x1e5a8e; // Light blue to match ocean tile sprites

    const bg = this.add.rectangle(
      WORLD_WIDTH / 2,
      WORLD_HEIGHT / 2,
      WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX * 2,
      WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX * 2,
      oceanTileColor,
    );
    bg.setDepth(-10);

    const innerBg = this.add.rectangle(
      WORLD_WIDTH / 2,
      WORLD_HEIGHT / 2,
      WORLD_WIDTH,
      WORLD_HEIGHT,
      oceanTileColor,
    );
    innerBg.setDepth(-9);

    // Create grid graphics object for tile grid overlay
    this.gridGraphics = this.add.graphics();
    this.gridGraphics.setDepth(100); // Always render above all island tiles and props

    // Add darker blue ocean overlay for depth effect
    // Layer sits above ocean animation (-8) and decor (-6) but below ships/islands (0)
    const oceanOverlay = this.add.rectangle(
      0,
      0,
      WORLD_WIDTH * 2, // Cover entire world including buffer
      WORLD_HEIGHT * 2,
      COLORS.oceanOverlay,
      0.25, // Low opacity for subtle darkening effect
    );
    oceanOverlay.setOrigin(0, 0);
    oceanOverlay.setDepth(-7); // Above ocean tiles (-8), below decor (-6)
    oceanOverlay.setScrollFactor(1, 1);

    // Draw world boundary - red border to show limits
    this.drawWorldBoundary();
  }

  /**
   * Draw world boundary indicators
   */
  private drawWorldBoundary(): void {
    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.setDepth(0);
    this.worldBoundaryGraphics = boundaryGraphics;

    // Thick red border around world edge
    const borderWidth = 4;
    boundaryGraphics.lineStyle(borderWidth, 0xe74c3c, 1);
    boundaryGraphics.strokeRect(
      borderWidth / 2,
      borderWidth / 2,
      WORLD_WIDTH - borderWidth,
      WORLD_HEIGHT - borderWidth,
    );

    // Add warning stripes along edges (every 100px)
    const stripeSize = 16;
    boundaryGraphics.fillStyle(0xe74c3c, 0.3);

    // Top edge stripes
    for (let x = 0; x < WORLD_WIDTH; x += stripeSize * 2) {
      boundaryGraphics.fillRect(x, 0, stripeSize, stripeSize);
    }
    // Bottom edge stripes
    for (let x = stripeSize; x < WORLD_WIDTH; x += stripeSize * 2) {
      boundaryGraphics.fillRect(
        x,
        WORLD_HEIGHT - stripeSize,
        stripeSize,
        stripeSize,
      );
    }
    // Left edge stripes
    for (let y = stripeSize; y < WORLD_HEIGHT; y += stripeSize * 2) {
      boundaryGraphics.fillRect(0, y, stripeSize, stripeSize);
    }
    // Right edge stripes
    for (let y = 0; y < WORLD_HEIGHT; y += stripeSize * 2) {
      boundaryGraphics.fillRect(
        WORLD_WIDTH - stripeSize,
        y,
        stripeSize,
        stripeSize,
      );
    }

    const labelAlpha: number = 0.18;
    const topLabel = this.add.text(
      WORLD_WIDTH / 2,
      -UNCHARTED_WATERS_BUFFER_PX / 2,
      "UNCHARTED WATERS",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "18px",
        color: "#7a8aa0",
        fontStyle: "bold",
      },
    );
    topLabel.setOrigin(0.5);
    topLabel.setAlpha(labelAlpha);
    topLabel.setDepth(-8);

    const bottomLabel = this.add.text(
      WORLD_WIDTH / 2,
      WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX / 2,
      "UNCHARTED WATERS",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "18px",
        color: "#7a8aa0",
        fontStyle: "bold",
      },
    );
    bottomLabel.setOrigin(0.5);
    bottomLabel.setAlpha(labelAlpha);
    bottomLabel.setDepth(-8);

    const leftLabel = this.add.text(
      -UNCHARTED_WATERS_BUFFER_PX / 2,
      WORLD_HEIGHT / 2,
      "UNCHARTED WATERS",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "18px",
        color: "#7a8aa0",
        fontStyle: "bold",
      },
    );
    leftLabel.setOrigin(0.5);
    leftLabel.setRotation(-Math.PI / 2);
    leftLabel.setAlpha(labelAlpha);
    leftLabel.setDepth(-8);

    const rightLabel = this.add.text(
      WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX / 2,
      WORLD_HEIGHT / 2,
      "UNCHARTED WATERS",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "18px",
        color: "#7a8aa0",
        fontStyle: "bold",
      },
    );
    rightLabel.setOrigin(0.5);
    rightLabel.setRotation(Math.PI / 2);
    rightLabel.setAlpha(labelAlpha);
    rightLabel.setDepth(-8);
  }

  /**
   * Create animated waterfall effect around world borders
   */
  private createWaterfallEffect(): void {
    const waterfallHeight = 64;
    const particleSize = 4;
    const particlesPerEdge = 40;

    // Top edge waterfall (flowing down)
    for (let i = 0; i < particlesPerEdge; i++) {
      const x = (i / particlesPerEdge) * WORLD_WIDTH;
      const delay = (i % 8) * 50;
      this.createWaterfallParticle(
        x,
        -waterfallHeight,
        0,
        1,
        delay,
        particleSize,
      );
    }

    // Bottom edge waterfall (flowing down)
    for (let i = 0; i < particlesPerEdge; i++) {
      const x = (i / particlesPerEdge) * WORLD_WIDTH;
      const delay = (i % 8) * 50;
      this.createWaterfallParticle(x, WORLD_HEIGHT, 0, 1, delay, particleSize);
    }

    // Left edge waterfall (flowing right)
    for (let i = 0; i < particlesPerEdge; i++) {
      const y = (i / particlesPerEdge) * WORLD_HEIGHT;
      const delay = (i % 8) * 50;
      this.createWaterfallParticle(
        -waterfallHeight,
        y,
        1,
        0,
        delay,
        particleSize,
      );
    }

    // Right edge waterfall (flowing left)
    for (let i = 0; i < particlesPerEdge; i++) {
      const y = (i / particlesPerEdge) * WORLD_HEIGHT;
      const delay = (i % 8) * 50;
      this.createWaterfallParticle(
        WORLD_WIDTH + waterfallHeight,
        y,
        -1,
        0,
        delay,
        particleSize,
      );
    }
  }

  /**
   * Create a single animated waterfall particle
   */
  private createWaterfallParticle(
    x: number,
    y: number,
    vx: number,
    vy: number,
    delay: number,
    size: number,
  ): void {
    const particle = this.add.rectangle(x, y, size, size, 0x4a90e2, 0.6);
    particle.setDepth(-5);

    const duration = 2000;
    const distance = 80;

    this.tweens.add({
      targets: particle,
      x: x + vx * distance,
      y: y + vy * distance,
      alpha: 0,
      duration,
      delay,
      ease: "Linear",
      repeat: -1,
      repeatDelay: 0,
    });
  }

  /**
   * Draw grid lines only for visible area - called in update()
   */
  private updateVisibleGrid(): void {
    const camera = this.cameras.main;
    const centerX = Math.floor(camera.scrollX / 100) * 100;
    const centerY = Math.floor(camera.scrollY / 100) * 100;

    // Only redraw if camera moved significantly
    if (centerX === this.lastGridCenterX && centerY === this.lastGridCenterY) {
      return;
    }

    this.lastGridCenterX = centerX;
    this.lastGridCenterY = centerY;

    // Clear previous grid
    this.gridGraphics.clear();

    // Calculate visible bounds with padding
    const padding = 100;
    const left = Math.max(0, camera.scrollX - padding);
    const top = Math.max(0, camera.scrollY - padding);
    const right = Math.min(
      WORLD_WIDTH,
      camera.scrollX + camera.width + padding,
    );
    const bottom = Math.min(
      WORLD_HEIGHT,
      camera.scrollY + camera.height + padding,
    );

    // Draw grid at exact TILE_SIZE (16px) - true square grid
    const gridSize = TILE_SIZE; // 16px - exactly 1 tile

    // Snap to grid
    const startX = Math.floor(left / gridSize) * gridSize;
    const startY = Math.floor(top / gridSize) * gridSize;

    // Draw tile grid lines - every 16px (1 tile)
    this.gridGraphics.lineStyle(1, COLORS.grid, 0.08); // Very subtle grid overlay

    for (let x = startX; x <= right; x += gridSize) {
      this.gridGraphics.moveTo(x, top);
      this.gridGraphics.lineTo(x, bottom);
    }

    for (let y = startY; y <= bottom; y += gridSize) {
      this.gridGraphics.moveTo(left, y);
      this.gridGraphics.lineTo(right, y);
    }

    this.gridGraphics.strokePath();

    // Draw major grid lines (every 4 tiles = 64px) - more visible
    const majorGridSize = TILE_SIZE * 4;
    const majorStartX = Math.floor(left / majorGridSize) * majorGridSize;
    const majorStartY = Math.floor(top / majorGridSize) * majorGridSize;

    this.gridGraphics.lineStyle(1, COLORS.gridLight, 0.12); // Subtle major grid overlay

    for (let x = majorStartX; x <= right; x += majorGridSize) {
      this.gridGraphics.moveTo(x, top);
      this.gridGraphics.lineTo(x, bottom);
    }

    for (let y = majorStartY; y <= bottom; y += majorGridSize) {
      this.gridGraphics.moveTo(left, y);
      this.gridGraphics.lineTo(right, y);
    }

    this.gridGraphics.strokePath();
  }

  // ============ ISLAND RENDERING ============
  // Island rendering is now handled by IslandRenderer system (../systems/IslandRenderer.ts)
  // Permanent locations (trading posts, harbor offices, etc.) are handled by PermanentLocationRenderer

  /**
   * Get trading posts from the PermanentLocationRenderer for arrival detection
   */
  private get tradingPosts(): Map<
    string,
    { x: number; y: number; name: string }
  > {
    return this.permanentLocationRenderer?.getTradingPosts() || new Map();
  }

  /**
   * Check if player can dock at an island (within 3 tiles + island radius)
   */
  private canDockAtIsland(island: IslandData): boolean {
    if (!this.localShip) return false;

    const dx = this.localShip.x - island.x;
    const dy = this.localShip.y - island.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDockDistance = island.radius + 3 * TILE_SIZE;

    return dist <= maxDockDistance;
  }

  /**
   * Emit proximity prompt when within docking range of any island
   */
  private checkDockProximity(): void {
    if (!this.localShip) return;
    // Do not show dock prompt if the local ship is missing from the authoritative ship map (prevents unusable prompts)
    const hasAuthoritativeShip: boolean = !!(
      this.localShip.shipData?.id && this.ships.has(this.localShip.shipData.id)
    );
    if (!hasAuthoritativeShip) {
      if (this.dockProximityIslandId) {
        this.dockProximityIslandId = null;
        this.events.emit("dock:proximityClear");
      }
      return;
    }
    if (this.localShip.shipData.combatState === "docked") {
      if (this.dockProximityIslandId) {
        this.dockProximityIslandId = null;
        this.events.emit("dock:proximityClear");
      }
      return;
    }
    if (!this.islandRenderer) return;

    const shipX = this.localShip.x;
    const shipY = this.localShip.y;
    let nearestId: string | null = null;
    let nearestName: string | null = null;
    let nearestDist: number = Infinity;

    this.islandRenderer.getIslands().forEach((entry) => {
      const island = entry.islandData;
      const dx = shipX - island.x;
      const dy = shipY - island.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const maxDockDistance = island.radius + 3 * TILE_SIZE;
      if (dist <= maxDockDistance && dist < nearestDist) {
        nearestId = island.id;
        nearestName =
          island.customName ||
          island.permanentName ||
          (island as any).name ||
          "Island";
        nearestDist = dist;
      }
    });

    const now: number = Date.now();
    const shouldEmitProximity: boolean =
      !!nearestId &&
      (this.dockProximityIslandId !== nearestId ||
        now - this.lastDockProximityPingAt > 1200);

    if (nearestId && shouldEmitProximity) {
      this.dockProximityIslandId = nearestId;
      this.lastDockProximityPingAt = now;
      this.events.emit("dock:proximity", {
        islandId: nearestId,
        islandName: nearestName || "Island",
      });
    } else if (!nearestId && this.dockProximityIslandId) {
      this.dockProximityIslandId = null;
      this.events.emit("dock:proximityClear");
    }
  }

  /**
   * Dock at an island
   */
  public dockAtIsland(islandId: string): void {
    if (!this.socket) return;
    if (this.localShip?.shipData.combatState === "docked") return;
    this.socket.emit("ship:dock", { islandId });
  }

  /**
   * Undock from current island
   */
  undockFromIsland(): void {
    if (!this.socket) return;
    this.socket.emit("ship:undock");
  }

  /**
   * Check if player is currently docked
   */
  isPlayerDocked(): boolean {
    return this.localShip?.shipData.combatState === "docked";
  }

  /**
   * Get the island the player is docked at
   */
  getDockedIslandId(): string | undefined {
    return this.localShip?.shipData.dockedAt;
  }

  private pendingFishingSpot: FishingSpotInfo | null = null;
  private pendingSanctumGather: {
    containerId: string;
    type: "container";
  } | null = null;

  /**
   * Start fishing at a spot
   */
  public startFishing(spot: FishingSpotInfo): void {
    console.log(
      `🎣 Starting to fish at ${spot.type} spot (${spot.tileX}, ${spot.tileY}) [id: ${spot.id}]`,
    );

    // Show immediate visual feedback
    this.showFishingCastingIndicator(spot);

    this.socket?.emit("fishing:start", { spotId: spot.id });
  }

  /**
   * Show casting indicator when clicking a fishing spot
   */
  private showFishingCastingIndicator(spot: FishingSpotInfo): void {
    // Create a visual indicator at the spot
    const indicator = this.add.container(spot.worldX, spot.worldY);
    indicator.setDepth(150);

    // Ripple effect
    const ripple = this.add.graphics();
    ripple.lineStyle(2, 0x5dade2, 1);
    ripple.strokeCircle(0, 0, 8);
    indicator.add(ripple);

    // Animate ripple expanding
    this.tweens.add({
      targets: ripple,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 600,
      ease: "Cubic.easeOut",
      onComplete: () => indicator.destroy(),
    });

    // Show "Casting..." text above player
    if (this.localShip) {
      const castText = this.add.text(
        this.localShip.x,
        this.localShip.y - 30,
        "Casting...",
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "10px",
          color: "#5dade2",
          backgroundColor: "#00000088",
          padding: { x: 4, y: 2 },
        },
      );
      castText.setOrigin(0.5);
      castText.setDepth(150);

      // Fade out after a short time
      this.tweens.add({
        targets: castText,
        alpha: 0,
        y: castText.y - 10,
        duration: 1000,
        delay: 500,
        onComplete: () => castText.destroy(),
      });
    }
  }

  private drawRegionLabels(): void {
    // Region labels - positioned at fixed world coordinates
    // World is 8000x8000 with Four Reaches in corners, Grand Latitude horizontal bands
    const labels = [
      // Four Reaches (corners) - doubled coordinates for 8000x8000 world
      { text: "EASTERN REACH", x: 1400, y: 1400, color: "#3498db" },
      { text: "WESTERN REACH", x: 6600, y: 1400, color: "#9b59b6" },
      { text: "SOUTHERN REACH", x: 1400, y: 6600, color: "#e67e22" },
      { text: "NORTHERN REACH", x: 6600, y: 6600, color: "#1abc9c" },
      // Grand Latitude (center horizontal bands)
      { text: "PARADISE", x: 4000, y: 3200, color: "#e74c3c" },
      { text: "ARCADIA", x: 4000, y: 4800, color: "#c0392b" },
    ];

    labels.forEach((label) => {
      const text = this.add.text(label.x, label.y, label.text, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "14px",
        color: label.color,
        fontStyle: "bold",
      });
      text.setOrigin(0.5);
      text.setAlpha(0.3);
      text.setDepth(0);
    });
  }

  // Input handling is now managed by InputHandler system (../systems/InputHandler.ts)

  public moveToPosition(x: number, y: number): void {
    // Don't stop fishing or disengage combat in sanctums - different context
    if (!this.isInSanctumInstance) {
      // Stop fishing when moving
      this.stopFishingOnMove();

      // Disengage combat target when manually moving - allows free movement during combat
      if (this.combatManager.getSelectedTarget()) {
        console.log(`🚶 Player moving during combat - disengaging target`);
        this.combatManager.disengageTarget();
      }
    }

    const validation = this.validateMoveTarget(x, y);
    if (!validation.ok) {
      this.showPathFailedIndicator(
        { tileX: (validation as any).tileX, tileY: (validation as any).tileY },
        (validation as any).reason,
      );
      return;
    }

    console.log(
      `🖱️ moveToPosition called: (${x}, ${y}), socket connected: ${this.socket?.connected}`,
    );

    // Store optimistic move target to smooth out movement while waiting for server
    this.optimisticMoveTarget = { x, y };

    if (this.socket) {
      this.socket.emit("shipMoveTo", { x, y });
      console.log(`📤 Emitted shipMoveTo to server`);
    } else {
      console.log(`❌ No socket connection!`);
    }
  }

  private stopMovement(): void {
    this.socket?.emit("shipStop");
    this.clearPathVisualization();
  }

  /**
   * Handle WASD directional movement (direct movement without pathfinding validation)
   * Uses 2-tile lookahead for smooth movement prediction
   */
  private handleWASDMovement(
    direction: "up" | "down" | "left" | "right",
    isPressed: boolean,
  ): void {
    // Update direction state
    if (direction === "up") this.wasdDirections.up = isPressed;
    if (direction === "down") this.wasdDirections.down = isPressed;
    if (direction === "left") this.wasdDirections.left = isPressed;
    if (direction === "right") this.wasdDirections.right = isPressed;

    // Stop any existing WASD movement timer
    if (this.wasdMovementTimer) {
      this.wasdMovementTimer.destroy();
      this.wasdMovementTimer = null;
    }

    // If any direction is pressed, start continuous movement
    const anyDirectionPressed =
      this.wasdDirections.up ||
      this.wasdDirections.down ||
      this.wasdDirections.left ||
      this.wasdDirections.right;

    if (anyDirectionPressed && this.localShip) {
      // Start continuous movement with 2-tile lookahead for smooth prediction
      const moveInterval = () => {
        if (!this.localShip) return;

        // Check if any direction is still pressed
        const stillPressed =
          this.wasdDirections.up ||
          this.wasdDirections.down ||
          this.wasdDirections.left ||
          this.wasdDirections.right;

        if (!stillPressed) {
          return; // Stop if no keys pressed
        }

        // Calculate movement direction based on pressed keys
        let moveX = 0;
        let moveY = 0;
        const moveDistance = TILE_SIZE; // Move 1 tile per update

        if (this.wasdDirections.up) moveY -= moveDistance;
        if (this.wasdDirections.down) moveY += moveDistance;
        if (this.wasdDirections.left) moveX -= moveDistance;
        if (this.wasdDirections.right) moveX += moveDistance;

        // Calculate target position 2 tiles ahead for smooth prediction
        // This makes movement feel continuous like click-based movement
        const targetX = this.localShip.x + moveX * 2;
        const targetY = this.localShip.y + moveY * 2;

        // Move directly without pathfinding validation (no "blocked by no path" visual)
        this.moveDirectly(targetX, targetY);

        // Schedule next movement - moderate interval (75ms) to avoid server overload
        this.wasdMovementTimer = this.time.delayedCall(75, moveInterval);
      };

      // Start the movement loop with moderate interval
      this.wasdMovementTimer = this.time.delayedCall(75, moveInterval);
    }
  }

  /**
   * Move directly without pathfinding validation (used for WASD movement)
   */
  private moveDirectly(x: number, y: number): void {
    if (!this.isInSanctumInstance) {
      this.stopFishingOnMove();
      if (this.combatManager.getSelectedTarget()) {
        this.combatManager.disengageTarget();
      }
    }

    // Clear path visualization for WASD movement (no green square)
    this.clearPathVisualization(false);

    if (this.socket) {
      this.isWASDMoving = true;
      this.socket.emit("shipMoveTo", { x, y });
      // Reset flag after a longer delay to allow the server response to be processed
      // Only reset if no keys are currently pressed (proper cancellation)
      this.time.delayedCall(200, () => {
        const anyKeyPressed =
          this.wasdDirections.up ||
          this.wasdDirections.down ||
          this.wasdDirections.left ||
          this.wasdDirections.right;
        if (!anyKeyPressed) {
          this.isWASDMoving = false;
        }
      });
    }
  }

  /**
   * Check if currently doing WASD movement
   */
  public isDoingWASDMovement(): boolean {
    return this.isWASDMoving;
  }

  /**
   * Draw path visualization for player movement
   */
  private drawPath(
    path: { x: number; y: number }[],
    targetTile: { tileX: number; tileY: number },
    isCombatPath: boolean = false,
  ): void {
    console.log(
      `📍 Path visualization: ${path?.length || 0} tiles, target: ${targetTile ? `(${targetTile.tileX}, ${targetTile.tileY})` : "none"}, combat: ${isCombatPath}, sanctum: ${this.isInSanctumInstance}`,
    );

    const isCombat = isCombatPath;

    // Choose which graphics objects to use based on combat mode
    if (isCombat) {
      // Clear existing combat path graphics
      if (this.combatPathGraphics) {
        this.combatPathGraphics.destroy();
        this.combatPathGraphics = null;
      }
      if (this.combatDestinationIndicator) {
        this.combatDestinationIndicator.destroy();
        this.combatDestinationIndicator = null;
      }
    } else {
      // Clear existing movement path graphics
      if (this.pathGraphics) {
        this.pathGraphics.destroy();
        this.pathGraphics = null;
      }
      if (this.destinationIndicator) {
        this.destinationIndicator.destroy();
        this.destinationIndicator = null;
      }
    }

    // If no path, we've reached destination - clear visualization
    if (!path || path.length === 0) {
      return;
    }

    // Colors: green for ocean movement, blue for sanctum movement, red for combat
    const lineColor = isCombat
      ? 0xff0000
      : this.isInSanctumInstance
        ? 0x00aaff
        : 0x00ff00;
    const fillColor = isCombat
      ? 0xff0000
      : this.isInSanctumInstance
        ? 0x00aaff
        : 0x00ff00;

    // Create path graphics
    const graphics = this.add.graphics();
    graphics.setDepth(50); // Higher depth to ensure visibility

    // Draw path line
    graphics.lineStyle(3, lineColor, 0.8);

    // Start visualization one tile ahead of the player so it doesn't cover the ship sprite
    if (this.localShip) {
      const firstTile = path[0];
      const startX = firstTile.x;
      const startY = firstTile.y;

      graphics.moveTo(startX, startY);

      // Draw line through each path tile starting from the first waypoint
      for (const tile of path) {
        graphics.lineTo(tile.x, tile.y);
      }

      graphics.strokePath();

      // Draw small squares at each waypoint
      graphics.fillStyle(fillColor, 0.5);
      for (const tile of path) {
        graphics.fillRect(tile.x - 4, tile.y - 4, 8, 8);
      }

      console.log(
        `📍 Drew ${isCombat ? "combat" : "movement"} path starting at first waypoint (${startX}, ${startY})`,
      );
    } else {
      console.log(`❌ No localShip for path visualization`);
    }

    // Store reference
    if (isCombat) {
      this.combatPathGraphics = graphics;
    } else {
      this.pathGraphics = graphics;
    }

    // Show destination indicator (subtle square outline only)
    if (targetTile) {
      const destX = targetTile.tileX * TILE_SIZE + TILE_SIZE / 2;
      const destY = targetTile.tileY * TILE_SIZE + TILE_SIZE / 2;

      const indicator = this.add.rectangle(
        destX,
        destY,
        TILE_SIZE,
        TILE_SIZE,
        0x000000,
        0,
      );
      indicator.setStrokeStyle(2, lineColor);
      indicator.setDepth(5);

      if (isCombat) {
        this.combatDestinationIndicator = indicator;
      } else {
        this.destinationIndicator = indicator;
      }
    }
  }

  /**
   * Clear path visualization when movement stops
   * @param combatOnly - if true, only clears combat path; if false, clears movement path
   */
  private clearPathVisualization(combatOnly: boolean = false): void {
    if (combatOnly) {
      if (this.combatPathGraphics) {
        this.combatPathGraphics.destroy();
        this.combatPathGraphics = null;
      }
      if (this.combatDestinationIndicator) {
        this.combatDestinationIndicator.destroy();
        this.combatDestinationIndicator = null;
      }
    } else {
      if (this.pathGraphics) {
        this.pathGraphics.destroy();
        this.pathGraphics = null;
      }
      if (this.destinationIndicator) {
        this.destinationIndicator.destroy();
        this.destinationIndicator = null;
      }
    }
  }

  /**
   * Clear all path visualizations
   */
  private clearAllPathVisualizations(): void {
    this.clearPathVisualization(false);
    this.clearPathVisualization(true);
  }

  /**
   * Show visual indicator when pathfinding fails (blocked tile)
   */
  private showPathFailedIndicator(
    targetTile: { tileX: number; tileY: number },
    reason?: string,
  ): void {
    const nowMs: number = Date.now();
    if (nowMs - this.lastPathFailedTimeMs < 150) {
      return; // rate limit to avoid spamming indicators
    }
    this.lastPathFailedTimeMs = nowMs;

    const worldX = targetTile.tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = targetTile.tileY * TILE_SIZE + TILE_SIZE / 2;

    // Red X indicator
    const indicator = this.add.rectangle(
      worldX,
      worldY,
      TILE_SIZE,
      TILE_SIZE,
      0xff0000,
      0.6,
    );
    indicator.setStrokeStyle(3, 0xff0000);
    indicator.setDepth(100);

    // X mark using two lines
    const graphics = this.add.graphics();
    graphics.setDepth(101);
    graphics.lineStyle(3, 0xffffff, 1);
    graphics.moveTo(worldX + 6, worldY - 6);
    graphics.lineTo(worldX - 6, worldY + 6);
    graphics.moveTo(worldX - 6, worldY - 6);
    graphics.lineTo(worldX + 6, worldY + 6);
    graphics.strokePath();

    // "Blocked" text - contextual messages based on block reason
    let label = "BLOCKED";
    if (reason) {
      switch (reason) {
        case "entity":
          label = "BLOCKED BY ENTITY";
          break;
        case "island":
          label = "BLOCKED BY LAND";
          break;
        case "placed_tile":
          label = "BLOCKED BY OBSTACLE";
          break;
        case "sanctum_wall":
          label = "BLOCKED BY WALL";
          break;
        case "sanctum_void":
          label = "BLOCKED BY VOID";
          break;
        default:
          // Convert snake_case to "BLOCKED BY READABLE TEXT"
          // Strip "blocked_by_" prefix if present
          let reasonText = reason.startsWith("blocked_by_")
            ? reason.slice("blocked_by_".length)
            : reason;

          const readable = reasonText
            .split("_")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
          label = `BLOCKED BY ${readable}`;
      }
    }
    const text = this.add.text(worldX, worldY - TILE_SIZE, label, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "10px",
      color: "#ffffff",
    });
    text.setOrigin(0.5);
    text.setDepth(102);

    this.activePathFailedIndicator = { indicator, graphics, text };

    // Shake animation
    this.tweens.add({
      targets: [indicator, graphics],
      x: "+=3",
      yoyo: true,
      repeat: 3,
      duration: 50,
    });

    // Fade out and destroy
    this.tweens.add({
      targets: [indicator, graphics, text],
      alpha: 0,
      duration: 800,
      delay: 400,
      onComplete: () => {
        indicator.destroy();
        graphics.destroy();
        text.destroy();

        if (this.activePathFailedIndicator?.indicator === indicator) {
          this.activePathFailedIndicator = null;
        }
      },
    });
  }

  private getShipAtPosition(x: number, y: number): ShipSprite | null {
    let closest: ShipSprite | null = null;
    let closestDist = TILE_SIZE; // Click tolerance = 1 tile

    this.ships.forEach((ship) => {
      const dist = Phaser.Math.Distance.Between(x, y, ship.x, ship.y);
      if (dist < closestDist) {
        closest = ship;
        closestDist = dist;
      }
    });

    return closest;
  }

  // setupSocketListeners() removed - now handled by SocketHandlerManager

  private getLootContainerAtPosition(
    worldX: number,
    worldY: number,
  ): { id: string; x: number; y: number } | null {
    // Use the same general hitbox size as the marker interaction area
    const tolerance = LOOT_CONTAINER_HITBOX_SIZE / 2;
    let found: { id: string; x: number; y: number } | null = null;

    // Check ocean loot containers
    this.lootContainerMarkers.forEach((marker, id) => {
      if (found) return;
      const dx = marker.x - worldX;
      const dy = marker.y - worldY;
      if (dx * dx + dy * dy <= tolerance * tolerance) {
        found = { id, x: marker.x, y: marker.y };
      }
    });

    // Also check sanctum containers if in sanctum
    if (!found && this.sanctumRenderer) {
      const sanctumContainers = this.sanctumRenderer.getContainers();
      sanctumContainers.forEach((container, id) => {
        if (found) return;
        const dx = container.x - worldX;
        const dy = container.y - worldY;
        if (dx * dx + dy * dy <= tolerance * tolerance) {
          found = { id, x: container.x, y: container.y };
        }
      });
    }

    return found;
  }

  // ============ SHIP RENDERING ============
  // Ship rendering is now handled by ShipRenderer system (../systems/ShipRenderer.ts)

  /**
   * Create a ship sprite - delegates to ShipRenderer
   */
  private createShipSprite(data: ShipData): ShipSprite | null {
    // Guard against shipRenderer not being initialized yet
    if (!this.shipRenderer) {
      console.warn(
        `⚠️ ShipRenderer not initialized yet, queuing ship spawn for ${data.name}`,
      );
      // Queue the ship spawn for after initialization
      setTimeout(() => this.createShipSprite(data), 100);
      return null;
    }

    const ship = this.shipRenderer.createShipSprite(data);
    this.ships.set(data.id, ship);

    if (data.ownerId === this.localPlayerId) {
      this.localShip = ship;
      this.cameras.main.centerOn(data.x, data.y);
      console.log(`📷 Camera centered on local ship at (${data.x}, ${data.y})`);
    }

    return ship;
  }

  /**
   * Update ships from server data - delegates to ShipRenderer
   */
  private updateShips(shipsData: ShipData[]): void {
    // Guard against shipRenderer not being initialized yet
    if (!this.shipRenderer) {
      return;
    }

    // Filter ships based on sanctum instance
    let filteredShips = shipsData;
    if (this.isInSanctumInstance) {
      // In sanctum: show local player ship + ships in the same sanctum instance
      filteredShips = shipsData.filter((ship) => {
        // Always show local player's ship
        if (this.localShip && ship.id === this.localShip.shipId) {
          return true;
        }
        // Show other ships in the same sanctum instance
        return ship.sanctumInstanceId === this.currentSanctumInstanceId;
      });
      console.log(
        `🏛️ Filtered ships for sanctum ${this.currentSanctumInstanceId}: ${filteredShips.length}/${shipsData.length}`,
      );
    } else {
      // In ocean: only show ships that are NOT in any sanctum
      filteredShips = shipsData.filter((ship) => !ship.sanctumInstanceId);
    }

    this.shipRenderer.updateShips(filteredShips, () =>
      this.updatePathAsShipMoves(),
    );

    // Sync local references
    this.ships = this.shipRenderer.getShips();

    // In sanctum, preserve local ship reference (don't let it get cleared)
    if (this.isInSanctumInstance && this.localShip) {
      // Keep existing local ship reference
    } else {
      this.localShip = this.shipRenderer.getLocalShip();
    }

    // Clear optimistic move target once we get a server update (trust server position)
    this.optimisticMoveTarget = null;
    this.lastServerUpdateTime = Date.now();

    // Sync sprint state from server
    if (this.localShip && this.localShip.shipData) {
      // Sync client sprint state with server state
      const wasSprinting = this.isSprinting;
      if (typeof this.localShip.shipData.isSprinting === "boolean") {
        this.isSprinting = this.localShip.shipData.isSprinting;
        // Play sound when sprint actually starts (server confirmed)
        if (this.isSprinting && !wasSprinting) {
          playSprintStartSound();
          console.log("🏃 [SPRINT] Server confirmed sprint started");
        }
      }

      // Emit SP update to React components
      this.game.events.emit("ship:sp", {
        current: this.localShip.shipData.currentSP ?? 100,
        max: this.localShip.shipData.maxSP ?? 100,
      });
    }

    // Update security tier based on local ship position
    if (this.localShip) {
      // Use shipData position for accurate world coordinates (not sprite visual position)
      const shipData = this.localShip.shipData;
      const x = shipData?.x ?? this.localShip.x;
      const y = shipData?.y ?? this.localShip.y;
      const tier = this.calculateSecurityTierInternal(x, y);
      this.game.events.emit("security:tier", tier);
    }

    if (this.selectedTarget) {
      const targetShip = this.ships.get(this.selectedTarget);
      if (targetShip) {
        this.events.emit("cannonFocus:targetUpdate", {
          name: targetShip.shipData.name,
          hp: targetShip.shipData.hullHp,
          maxHp: targetShip.shipData.maxHullHp,
          faction: targetShip.shipData.faction || "neutral",
          level: targetShip.shipData.level || 1,
        });
      } else {
        // Target disappeared (e.g. sunk); keep UI consistent.
        this.events.emit("cannonFocus:targetUpdate", {
          name: null,
          hp: 0,
          maxHp: 0,
          faction: "neutral",
          level: 1,
        });
      }
    }
  }

  /**
   * Calculate security tier based on position (private version for internal use)
   */
  private calculateSecurityTierInternal(
    x: number,
    y: number,
  ): "safe_zone" | "contested" | "open_waters" {
    return this.calculateSecurityTier(x, y);
  }

  /**
   * Check if position is in contested zone (near player-claimed territory or Navy-protected regions)
   */
  private isInContestedZone(x: number, y: number): boolean {
    // Check if in Navy-protected region (Western Reach has Navy presence)
    if (this.isInNavyProtectedRegion(x, y)) {
      return true;
    }

    // Get all islands from renderer
    const islands = this.islandRenderer?.getIslands?.();
    if (!islands) return false;

    const radiusPx: number = 40 * TILE_SIZE; // Check within 40 tiles of claimed islands
    const radiusSq: number = radiusPx * radiusPx;

    for (const entry of Array.from(islands.values())) {
      const island = entry.islandData;
      // Only count player-claimed islands (not permanent locations)
      if (island.isPermanent || !island.ownerId) {
        continue;
      }
      const dx = island.x - x;
      const dy = island.y - y;
      if (dx * dx + dy * dy <= radiusSq) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if position is in a Navy-protected region
   * Western Reach has Navy protection
   */
  private isInNavyProtectedRegion(x: number, y: number): boolean {
    // Convert world position to chunk coordinates
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);

    // Western Reach chunks: [[0,3],[1,3],[2,3],[0,4],[1,4],[2,4]]
    const isWesternReach =
      (chunkX === 0 || chunkX === 1 || chunkX === 2) &&
      (chunkY === 3 || chunkY === 4);

    return isWesternReach;
  }

  /**
   * Update path visualization as ship moves - check if reached destination
   */
  private updatePathAsShipMoves(): void {
    // If the local ship starts moving while still marked as docked, the docked UI should close.
    // This can happen if movement resumes before an explicit undock event reaches the client.
    if (
      this.localShip?.shipData.combatState === "docked" &&
      (Math.abs(this.localShip.shipData.velocityX) > 0.001 ||
        Math.abs(this.localShip.shipData.velocityY) > 0.001)
    ) {
      this.localShip.shipData.dockedAt = undefined;
      this.localShip.shipData.dockedPosition = undefined;
      this.localShip.shipData.combatState = "sailing";
      this.events.emit("player:undocked");
    }

    if (this.destinationIndicator && this.localShip) {
      const destX = this.destinationIndicator.x;
      const destY = this.destinationIndicator.y;
      const dist = Phaser.Math.Distance.Between(
        this.localShip.x,
        this.localShip.y,
        destX,
        destY,
      );

      if (dist < TILE_SIZE) {
        this.clearPathVisualization();
      }
    }
  }

  /**
   * Remove a ship - delegates to ShipRenderer with combat cleanup callback
   */
  private removeShip(shipId: string): void {
    // Guard against shipRenderer not being initialized yet
    if (!this.shipRenderer) {
      return;
    }

    this.shipRenderer.removeShip(shipId, () => {
      console.log(`🎯 Target destroyed - clearing combat visuals`);
      this.selectedTarget = null;
      this.hideTargetInfo();

      // Dismiss cannon focus minigame when target is destroyed
      this.events.emit("cannonFocus:deactivate");

      if (this.combatTrackingLine) {
        this.combatTrackingLine.clear();
      }

      this.clearPathVisualization(true);
    });

    // Sync local references
    this.ships = this.shipRenderer.getShips();
    this.localShip = this.shipRenderer.getLocalShip();
  }

  // ============ PLAYER DEATH & RESPAWN ============

  /**
   * Handle player death - show game over UI
   */
  private handlePlayerDeath(killerName: string): void {
    console.log(`💀 handlePlayerDeath called - killer: ${killerName}`);
    this.isPlayerDead = true;

    // Remove the local ship sprite immediately
    if (this.localShip) {
      const shipId = this.localShip.shipId;
      if (shipId) {
        console.log(`💀 Removing dead ship sprite: ${shipId}`);
        this.shipRenderer.removeShip(shipId);
        this.ships.delete(shipId);
      }
    }
    this.localShip = null;

    // Deactivate cannon focus minigame
    this.events.emit("cannonFocus:deactivate");

    // Clear all path visualizations
    this.clearAllPathVisualizations();

    // Clear combat tracking line
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }

    // Clear hover range
    this.combatManager.hideHoverRange();

    // Clear target info
    this.hideTargetInfo();
    this.selectedTarget = null;

    // Show game over UI
    console.log(`💀 Showing game over UI...`);
    this.showGameOverUI(killerName);
  }

  /**
   * Clear the local player's ship from the scene (used on disconnect to prevent ghost players)
   */
  public clearLocalShip(): void {
    if (this.localShip) {
      const shipId = this.localShip.shipId;
      if (shipId) {
        console.log(
          `👻 Clearing local ship to prevent ghost player: ${shipId}`,
        );
        this.shipRenderer.removeShip(shipId);
        this.ships.delete(shipId);
      }
      this.localShip = null;
    }
  }

  /**
   * Show game over UI with respawn button
   */
  private showGameOverUI(killerName: string): void {
    // Remove existing game over UI if any
    if (this.gameOverUI) {
      this.gameOverUI.destroy();
      this.gameOverUI = null;
    }

    const { width, height } = this.cameras.main;

    // Create container for game over UI (fixed to screen)
    this.gameOverUI = this.add.container(0, 0);
    this.gameOverUI.setScrollFactor(0);
    this.gameOverUI.setDepth(2000);

    // Dark overlay - make it interactive to block clicks through
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x000000,
      0.85,
    );
    overlay.setInteractive(); // Block clicks from passing through
    overlay.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        localX: number,
        localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation(); // Prevent click from reaching game world
      },
    );
    this.gameOverUI.add(overlay);

    // Game Over title
    const title = this.add.text(width / 2, height / 2 - 100, "GAME OVER", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "48px",
      color: "#e74c3c",
      fontStyle: "bold",
    });
    title.setOrigin(0.5);
    this.gameOverUI.add(title);

    // Death message
    const deathMsg = this.add.text(
      width / 2,
      height / 2 - 30,
      `Your ship was sunk by ${killerName}`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "18px",
        color: "#ffffff",
      },
    );
    deathMsg.setOrigin(0.5);
    this.gameOverUI.add(deathMsg);

    // Full loot warning
    const lootWarning = this.add.text(
      width / 2,
      height / 2 + 10,
      "All loot has been lost!",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "14px",
        color: "#f39c12",
      },
    );
    lootWarning.setOrigin(0.5);
    this.gameOverUI.add(lootWarning);

    // Respawn button - create separately with higher depth
    const btnWidth = 200;
    const btnHeight = 50;
    const btnY = height / 2 + 80;

    const respawnBtn = this.add.rectangle(
      width / 2,
      btnY,
      btnWidth,
      btnHeight,
      0x27ae60,
    );
    respawnBtn.setStrokeStyle(3, 0x2ecc71);
    respawnBtn.setDepth(2001); // Higher than container to ensure clickability
    respawnBtn.setScrollFactor(0);
    respawnBtn.setInteractive({ useHandCursor: true });
    this.gameOverUI.add(respawnBtn);

    const respawnText = this.add.text(width / 2, btnY, "RESPAWN", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "20px",
      color: "#ffffff",
      fontStyle: "bold",
    });
    respawnText.setOrigin(0.5);
    respawnText.setDepth(2002);
    respawnText.setScrollFactor(0);
    this.gameOverUI.add(respawnText);

    // Track if respawn is already requested to prevent double-clicks
    let respawnRequested = false;

    // Hover effects
    respawnBtn.on("pointerover", () => {
      if (!respawnRequested) {
        respawnBtn.setFillStyle(0x2ecc71);
        respawnBtn.setScale(1.05);
        respawnText.setScale(1.05);
      }
    });

    respawnBtn.on("pointerout", () => {
      if (!respawnRequested) {
        respawnBtn.setFillStyle(0x27ae60);
        respawnBtn.setScale(1);
        respawnText.setScale(1);
      }
    });

    // Click handler - use pointerdown for immediate response
    respawnBtn.on(
      "pointerdown",
      (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        if (!respawnRequested) {
          respawnRequested = true;
          // Visual feedback
          respawnBtn.setFillStyle(0x1e8449);
          respawnText.setText("⏳ RESPAWNING...");
          this.requestRespawn();
        }
      },
    );
  }

  /**
   * Request respawn from server
   */
  private requestRespawn(): void {
    console.log("🔄 Requesting respawn...");
    this.socket?.emit("player:respawn");

    // Hide game over UI
    if (this.gameOverUI) {
      this.gameOverUI.destroy();
      this.gameOverUI = null;
    }

    this.isPlayerDead = false;
  }

  // ============ INPUT HANDLING ============

  // ============ COMBAT ============
  // Combat logic is now handled by CombatManager

  private showCannonEffect(data: CannonEffectData): void {
    // Guard against combatSystem not being initialized yet
    if (!this.combatSystem) {
      return;
    }
    this.combatSystem.showCannonEffect(data);
  }

  private showShipSinkingEffect(
    ship: ShipSprite,
    shipName: string,
    pieces: number,
  ): void {
    // Guard against combatSystem not being initialized yet
    if (!this.combatSystem) {
      return;
    }
    this.combatSystem.showShipSinkingEffect(ship, shipName, pieces);
  }

  private showLootGainedEffect(
    pieces: number,
    bounty: number,
    xp: number = 0,
    newLevel?: number,
  ): void {
    // Guard against combatSystem not being initialized yet
    if (!this.combatSystem) {
      return;
    }
    this.combatSystem.showLootGainedEffect(pieces, bounty, xp, newLevel);
  }

  private isShipInSafeZone(
    ship: Phaser.GameObjects.Container & { shipData?: ShipData },
  ): boolean {
    const shipData = (ship as any).shipData as ShipData | undefined;
    if (!shipData) return false;
    return this.isInSafeZone(shipData.x, shipData.y);
  }

  private isInSafeZone(x: number, y: number): boolean {
    // Match server: Trading Post / Shipyard / Harbor Office safe zones within 8 tiles.
    // We derive centers from currently loaded islands and permanent locations.

    // Server rule: safe zones are fixed 8 tiles around permanent safe islands (trading post, shipyard, harbor office, tavern).
    const radiusPx: number = this.SAFE_ZONE_RADIUS_TILES * TILE_SIZE;
    const radiusSq: number = radiusPx * radiusPx;

    // Check regular islands
    const islands = this.islandRenderer?.getIslands?.();
    if (islands) {
      for (const entry of Array.from(islands.values())) {
        const island = entry.islandData;
        if (!this.isSafeZoneIsland(island.id, island.permanentName)) {
          continue;
        }

        // Use island.x/y from islandData, fallback to entry.x/y
        const islandX = island.x ?? entry.x;
        const islandY = island.y ?? entry.y;
        const dx = islandX - x;
        const dy = islandY - y;
        const distSq = dx * dx + dy * dy;

        if (distSq <= radiusSq) {
          return true;
        }
      }
    }

    // Check permanent locations (trading posts, shipyards, harbor offices, taverns)
    const locations = this.permanentLocationRenderer?.getLocations?.();
    if (locations) {
      for (const entry of Array.from(locations.values())) {
        const island = entry.islandData;
        if (!this.isSafeZoneIsland(island.id, island.permanentName)) {
          continue;
        }

        // Use entry.x/y for permanent locations
        const dx = entry.x - x;
        const dy = entry.y - y;
        const distSq = dx * dx + dy * dy;

        if (distSq <= radiusSq) {
          return true;
        }
      }
    }

    return false;
  }

  private isSafeZoneIsland(
    islandId: string,
    permanentName: string | null,
  ): boolean {
    const id = islandId.toLowerCase();
    if (
      id.includes("trading_post") ||
      id.includes("shipyard") ||
      id.includes("harbor_office") ||
      id.includes("tavern")
    ) {
      return true;
    }

    const name = (permanentName || "").toLowerCase();
    if (name.includes("trading post")) return true;
    if (name.includes("shipyard") || name.includes("dockyard")) return true;
    if (
      name.includes("harbor office") ||
      name.includes("harbor master") ||
      name.includes("port authority") ||
      name.includes("fleet command")
    ) {
      return true;
    }

    if (name.includes("tavern")) return true;

    return false;
  }

  /**
   * Check if the local player is currently in a safe zone
   * Used by CombatSystem to disable screen shake in safe zones
   */
  public isPlayerInSafeZone(): boolean {
    const localShip = this.getLocalShip();
    if (!localShip) return false;

    return this.calculateSecurityTier(localShip.x, localShip.y) === "safe_zone";
  }

  /**
   * Public method to calculate security tier (used by GameCanvas undock event)
   */
  public calculateSecurityTier(
    x: number,
    y: number,
  ): "safe_zone" | "contested" | "open_waters" {
    // If in a rift, security is always 'open_waters' unless explicitly defined otherwise
    const localShip = this.getLocalShip();
    const isInsideRift =
      localShip &&
      (localShip.shipData as any)?.riftId &&
      (localShip.shipData as any).riftId !== "core";

    if (isInsideRift) {
      return "open_waters";
    }

    // Check if in safe zone (trading post, shipyard, harbor office, tavern within 8 tiles)
    if (this.isInSafeZone(x, y)) {
      return "safe_zone";
    }

    // Check if in contested zone (near player territory or Navy-protected regions)
    if (this.isInContestedZone(x, y)) {
      return "contested";
    }

    // Otherwise open waters
    return "open_waters";
  }

  /**
   * Toggle fishing minigame - only works when fishing
   */
  private toggleFishingMinigame(): void {
    // Only allow fishing minigame when actively fishing
    if (!this.isFishing) {
      console.log("🎣 Cannot activate fishing minigame - not fishing");
      return;
    }

    if (this.fishingMinigameActive) {
      this.fishingMinigameActive = false;
      this.events.emit("fishing:minigame:deactivate");
    } else {
      this.fishingMinigameActive = true;
      this.events.emit("fishing:minigame:activate", {
        spotType: this.currentFishingSpotType,
        spotId: this.currentFishingSpotId,
      });
      console.log(
        "🎣 Fishing minigame activated - press SPACE to keep bobber in zone, ESC to close",
      );
    }
  }

  // ============ SPRINT ============

  /**
   * Start sprinting - sends sprint:start to server when shift is pressed
   * Server handles SP cost and cooldown
   * Sprint only continues while shift is held down
   */
  private startSprint(): void {
    console.log(
      `🏃 [SPRINT] startSprint called - isPlayerDead: ${this.isPlayerDead}, isFishing: ${this.isFishing}, isSprinting: ${this.isSprinting}`,
    );

    if (this.isPlayerDead || this.isFishing) {
      console.log("🏃 [SPRINT] Blocked by condition");
      return;
    }

    if (!this.isSprinting) {
      // Don't set local state yet - wait for server confirmation
      // Sound will play when server confirms sprint started
      this.socket?.emit("sprint:start");
      console.log(
        "🏃 [SPRINT] Requesting sprint from server (shift held down)",
      );
    }
  }

  /**
   * Stop sprinting - sends sprint:stop to server when shift is released
   */
  private stopSprint(): void {
    console.log(
      `🏃 [SPRINT] stopSprint called - isSprinting: ${this.isSprinting}`,
    );

    if (this.isSprinting) {
      this.isSprinting = false;
      this.socket?.emit("sprint:stop");
      console.log(
        "🏃 [SPRINT] Sprint stopped - shift released, emitting sprint:stop to server",
      );
    }
  }

  /**
   * Activate signature move - enters cast mode for targeting
   * E key triggers this
   */
  private activateSignatureMove(): void {
    if (this.isPlayerDead) {
      return;
    }

    // Request wildcard power info from server to enter cast mode
    this.socket?.emit(
      "wildcard:list",
      (data: {
        moves: Array<{
          definitionId: string;
          customAlias: string;
          isEquipped: boolean;
          definition?: {
            aoeRadius: number;
            color: string;
            baseName: string;
          };
        }>;
        equipped: string | null;
      }) => {
        const equippedMove = data.moves.find((m) => m.isEquipped);
        if (!equippedMove || !equippedMove.definition) {
          this.showFloatingMessage("No signature move equipped", "#ff6666");
          return;
        }

        // Enter cast mode with the equipped move
        this.enterSignatureCastMode(
          equippedMove.definitionId,
          equippedMove.customAlias,
          equippedMove.definition.color,
          equippedMove.definition.aoeRadius,
        );

        // Change cursor to crosshair
        this.input.setDefaultCursor("crosshair");

        console.log(`⚡ Entered cast mode for: ${equippedMove.customAlias}`);
      },
    );
  }

  // ============ SHOP UI ============
  // Shop UI is now handled by ShopUI system (../systems/ShopUI.ts)

  private openShopUI(locationId: string, shopName: string): void {
    this.shopUISystem.setSocket(this.socket);
    this.shopUISystem.open(locationId, shopName);
  }

  private showShopLoadingUI(shopName: string): void {
    this.shopUISystem.showLoading(shopName);
  }

  private displayShopUI(shop: ShopData): void {
    this.shopUISystem.display(shop);
  }

  private buyShopItem(itemId: string): void {
    this.shopUISystem.buyItem(itemId);
  }

  private closeShopUI(): void {
    this.shopUISystem.close();
  }

  private showPurchaseFeedback(cost: number): void {
    this.shopUISystem.showPurchaseFeedback(cost);
  }

  // ============ NPC RENDERING ============

  private updateNPCs(npcsData: NPCData[]): void {
    // Don't render NPCs when inside a rift instance (NPCs only exist in core world)
    if (this.isInRiftInstance) {
      console.log("[NPC] Skipping render - inside rift instance");
      // Clear any existing NPCs
      this.npcs.forEach((npc) => {
        npc.destroy();
      });
      this.npcs.clear();
      return;
    }

    const existingIds = new Set(npcsData.map((n) => n.id));

    // Update or create NPCs
    // Container structure: [0] aggroRangeGraphics, [1] hull, [2] hpBar, [3] aggroIndicator
    npcsData.forEach((data) => {
      const npc = this.npcs.get(data.id);

      if (npc) {
        const oldX = npc.x;
        const oldY = npc.y;

        // Smooth interpolation for NPC movement: tween to new position
        // NPCs sync at 200ms, so use adaptive tween duration for smooth motion
        if (oldX !== data.x || oldY !== data.y) {
          const existingTween = (npc as any).movementTween as
            | Phaser.Tweens.Tween
            | undefined;
          if (existingTween) {
            existingTween.stop();
          }

          // Calculate distance for speed-based duration
          const dx = data.x - oldX; // eslint-disable-line @typescript-eslint/no-unused-vars
          const dy = data.y - oldY; // eslint-disable-line @typescript-eslint/no-unused-vars
          const distance = Math.sqrt(dx * dx + dy * dy);

          // NPCs move at ~2 tiles/sec (half player speed of 4 tiles/sec)
          const speed = 2 * TILE_SIZE; // pixels per second
          const duration = Math.max(50, (distance / speed) * 1000); // min 50ms

          (npc as any).movementTween = this.tweens.add({
            targets: npc,
            x: data.x,
            y: data.y,
            duration: duration,
            ease: "Linear",
          });
        }

        // CRITICAL: Always ensure NPC is visible - prevents invisible NPCs from attacking
        // This overrides any culling that may have hidden the NPC
        npc.setVisible(true);
        npc.setActive(true);

        // Update color based on behavior type
        const hull = npc.getAt(1) as Phaser.GameObjects.Rectangle;
        const color = NPC_BEHAVIOR_COLORS[data.behaviorType] || 0x95a5a6;
        hull.setFillStyle(color);

        // Update all visuals including aggro range
        this.updateNPCVisuals(npc, data);
      } else {
        // Create new NPC
        this.createNPCSprite(data);
      }
    });

    // Remove NPCs that no longer exist
    this.npcs.forEach((npc, id) => {
      if (!existingIds.has(id)) {
        // Check if this was our target - dismiss combat UI
        if (this.selectedTarget === id) {
          console.log(`🎯 Target NPC ${id} removed - clearing combat state`);
          this.selectedTarget = null;
          this.hideTargetInfo();

          // Dismiss cannon focus minigame
          this.events.emit("cannonFocus:deactivate");

          // Clear combat tracking line
          if (this.combatTrackingLine) {
            this.combatTrackingLine.clear();
          }
        }

        // Stop any active tween
        const existingTween = (npc as any).movementTween as
          | Phaser.Tweens.Tween
          | undefined;
        if (existingTween) {
          existingTween.stop();
        }

        npc.destroy();
        this.npcs.delete(id);
      }
    });
  }

  private createNPCSprite(data: NPCData): void {
    // Check if this is an ambient NPC - render differently
    if (data.isAmbient) {
      this.createAmbientNPCSprite(data);
      return;
    }

    const isBoss = data.isBoss || false;
    const hullSize = isBoss ? TILE_SIZE * 2 : TILE_SIZE;

    const container = this.add.container(data.x, data.y);

    // Aggro range graphics (drawn first, behind everything)
    const aggroRangeGraphics = this.add.graphics();
    aggroRangeGraphics.setDepth(0);
    container.add(aggroRangeGraphics);

    // NPC color based on behavior type
    const color = NPC_BEHAVIOR_COLORS[data.behaviorType] || 0x95a5a6;

    // NPC hull - 1 tile for normal, 2 tiles for boss
    const hull = this.add.rectangle(0, 0, hullSize, hullSize, color);
    const strokeColor = isBoss ? 0xffd700 : 0xffffff; // Gold for boss, white for normal
    const strokeWidth = isBoss ? 4 : 2;
    hull.setStrokeStyle(strokeWidth, strokeColor);
    container.add(hull);

    // Boss marker - crown shape drawn with graphics (no emoji)
    if (isBoss) {
      const crownGraphics = this.add.graphics();
      this.drawBossCrown(crownGraphics, 0, -hullSize / 2 - 8);
      container.add(crownGraphics);
    }

    // HP bar
    const hpBar = this.add.graphics();
    container.add(hpBar);

    // Aggro indicator - simple line above NPC (no emoji)
    const aggroIndicator = this.add.graphics();
    aggroIndicator.setVisible(data.isAggroed);
    container.add(aggroIndicator);

    container.setDepth(5);

    // Make interactive - enlarged hitbox for easier clicking
    const npcHitboxSize = hullSize * HITBOX_MULTIPLIER;
    const hitArea = new Phaser.Geom.Rectangle(
      -npcHitboxSize / 2,
      -npcHitboxSize / 2,
      npcHitboxSize,
      npcHitboxSize,
    );
    hull.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    hull.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        if (pointer.rightButtonDown()) return;
        event.stopPropagation(); // Prevent global click handler
        // Left click on NPC to engage
        this.combatManager.engageTarget(data.id);
      },
    );

    hull.on("pointerover", () => {
      hull.setStrokeStyle(strokeWidth + 1, 0xffff00);
      // Show NPC range on hover
      this.showNPCHoverRange(data);
    });

    hull.on("pointerout", () => {
      hull.setStrokeStyle(strokeWidth, strokeColor);
      this.combatManager.hideHoverRange();
    });

    // Store data reference on container for updates
    (container as any).npcData = data;
    (container as any).isBoss = isBoss;
    (container as any).aggroIndicator = aggroIndicator;

    // Ensure NPC is visible - critical for preventing invisible NPCs
    container.setVisible(true);

    this.npcs.set(data.id, container);
    this.updateNPCVisuals(container, data);

    // Create spawning particle effect if NPC was just spawned
    if (data.spawnedAt) {
      const timeSinceSpawn = Date.now() - data.spawnedAt;
      if (timeSinceSpawn < 5000) {
        // Show effect for 5 seconds after spawn
        this.createNPCSpawnEffect(data.x, data.y);
      }
    }
  }

  /**
   * Create a bubbling particle effect around a newly spawned NPC
   */
  private createNPCSpawnEffect(x: number, y: number): void {
    const BUBBLE_COUNT = 12;
    const EFFECT_DURATION_MS = 5000;
    const BUBBLE_RADIUS = TILE_SIZE * 1.5;

    // Create ground effect on tile below NPC (tile-based animation)
    const groundEffectY = y + TILE_SIZE;
    const groundGraphics = this.add.graphics();
    groundGraphics.setDepth(2);
    groundGraphics.fillStyle(0x4da6ff, 0.4);
    groundGraphics.fillRect(
      x - TILE_SIZE / 2,
      groundEffectY - TILE_SIZE / 2,
      TILE_SIZE,
      TILE_SIZE,
    );

    // Animate ground effect - pulse and fade
    this.tweens.add({
      targets: groundGraphics,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: EFFECT_DURATION_MS,
      ease: "Quad.easeOut",
      onComplete: () => {
        groundGraphics.destroy();
      },
    });

    // Create bubbles radiating outward from spawn point
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      const angle = (i / BUBBLE_COUNT) * Math.PI * 2;
      const distance = BUBBLE_RADIUS;
      const endX = x + Math.cos(angle) * distance;
      const endY = y + Math.sin(angle) * distance;

      // Create bubble (small rectangle)
      const bubbleSize = 4 + Math.random() * 4;
      const bubble = this.add.rectangle(
        x,
        y,
        bubbleSize,
        bubbleSize,
        0x4da6ff,
        0.6,
      );
      bubble.setDepth(4);

      // Animate bubble rising and fading
      const delay = i * 50;
      const duration = 800 + Math.random() * 400;

      this.tweens.add({
        targets: bubble,
        x: endX,
        y: endY - 40,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration,
        delay,
        ease: "Quad.easeOut",
        onComplete: () => {
          bubble.destroy();
        },
      });
    }

    // Create a pulsing ring effect at spawn center
    const ringGraphics = this.add.graphics();
    ringGraphics.setDepth(3);
    ringGraphics.lineStyle(2, 0x4da6ff, 0.8);
    ringGraphics.strokeCircle(x, y, TILE_SIZE * 0.8);

    this.tweens.add({
      targets: ringGraphics,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: EFFECT_DURATION_MS,
      ease: "Quad.easeOut",
      onComplete: () => {
        ringGraphics.destroy();
      },
    });
  }

  /**
   * Draw a boss crown marker (grid-aligned, no emoji)
   */
  private drawBossCrown(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
  ): void {
    const size = 6;
    graphics.fillStyle(0xffd700, 1); // Gold

    // Base of crown
    graphics.fillRect(x - size, y, size * 2, size / 2);

    // Three points
    graphics.fillRect(x - size / 2, y - size / 2, size / 3, size / 2);
    graphics.fillRect(x, y - size, size / 3, size);
    graphics.fillRect(x + size / 2, y - size / 2, size / 3, size / 2);
  }

  /**
   * Show boss ability telegraph (warning before attack)
   */
  private showBossTelegraph(data: {
    npcId: string;
    abilityName: string;
    shape: "aoe" | "line" | "cone";
    x: number;
    y: number;
    radius: number;
    durationMs: number;
  }): void {
    // Remove existing telegraph for this NPC
    const existing = this.bossTelegraphs.get(data.npcId);
    if (existing) {
      existing.tween.stop();
      existing.graphics.destroy();
      this.bossTelegraphs.delete(data.npcId);
    }

    // Create telegraph graphics
    const graphics = this.add.graphics();
    graphics.setDepth(10);

    // Draw telegraph based on shape
    const color = 0xff4444; // Red warning color
    const alpha = 0.6;

    if (data.shape === "aoe") {
      // Circle for AoE
      graphics.fillStyle(color, alpha);
      graphics.fillCircle(data.x, data.y, data.radius);
      graphics.lineStyle(2, color, 1);
      graphics.strokeCircleShape(
        new Phaser.Geom.Circle(data.x, data.y, data.radius),
      );
    } else if (data.shape === "line") {
      // Line for directional attack
      graphics.lineStyle(4, color, alpha);
      graphics.moveTo(data.x - data.radius, data.y);
      graphics.lineTo(data.x + data.radius, data.y);
      graphics.strokePath();
    } else if (data.shape === "cone") {
      // Cone for cone attack
      graphics.fillStyle(color, alpha);
      graphics.beginPath();
      graphics.moveTo(data.x, data.y);
      graphics.arc(data.x, data.y, data.radius, -Math.PI / 6, Math.PI / 6);
      graphics.lineTo(data.x, data.y);
      graphics.closePath();
      graphics.fillPath();
      graphics.lineStyle(2, color, 1);
      graphics.strokePath();
    }

    // Fade out animation
    const tween = this.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: data.durationMs,
      onComplete: () => {
        graphics.destroy();
        this.bossTelegraphs.delete(data.npcId);
      },
    });

    this.bossTelegraphs.set(data.npcId, { graphics, tween });
  }

  /**
   * Create ambient NPC sprite with diamond (rotated square) shape
   * Ambient NPCs: civilians, animals, traders, fishermen, sailors
   */
  private createAmbientNPCSprite(data: NPCData): void {
    const container = this.add.container(data.x, data.y);

    // Get color based on ambient category
    const rawCategory = data.ambientCategory || "civilian";
    const category = rawCategory === "trader" ? "fisherman" : rawCategory;
    const color = AMBIENT_NPC_COLORS[category] || 0x88cc88;

    const diamondSize = TILE_SIZE * 0.5;
    const diamond = this.add.graphics();
    diamond.fillStyle(color, 1);
    diamond.lineStyle(1, 0xffffff, 0.8);

    // Draw diamond (rotated square) - 4 points
    diamond.beginPath();
    diamond.moveTo(0, -diamondSize / 2); // Top
    diamond.lineTo(diamondSize / 2, 0); // Right
    diamond.lineTo(0, diamondSize / 2); // Bottom
    diamond.lineTo(-diamondSize / 2, 0); // Left
    diamond.closePath();
    diamond.fillPath();
    diamond.strokePath();

    container.add(diamond);

    // For fishermen (legacy traders), add cargo value indicator if valuable
    if (category === "fisherman" && data.cargoValue && data.cargoValue > 500) {
      const cargoIndicator = this.add.graphics();
      // Small gold square above diamond to indicate valuable cargo
      cargoIndicator.fillStyle(0xffd700, 1);
      cargoIndicator.fillRect(-2, -diamondSize / 2 - 6, 4, 4);
      container.add(cargoIndicator);
    }

    // HP bar (only show if damaged)
    const hpBar = this.add.graphics();
    container.add(hpBar);

    container.setDepth(4); // Below regular NPCs

    // Make interactive - ambient NPCs can be clicked for info or attacked
    // Enlarged hitbox for easier clicking
    const hitArea = new Phaser.Geom.Circle(0, 0, AMBIENT_NPC_HITBOX_RADIUS);
    container.setInteractive(hitArea, Phaser.Geom.Circle.Contains);

    // Store hit area so we can re-enable interaction after culling
    (container as any).ambientHitArea = hitArea;

    container.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();

        if (pointer.rightButtonDown()) {
          // Right-click: show context menu (React layer)
          this.events.emit("ambient:contextmenu", {
            ambientId: data.id,
            ambientType: data.name,
            ambientDisplayName: this.getAmbientDisplayName(data.name),
            ambientCategory: category,
            screenX: pointer.x,
            screenY: pointer.y,
          });
          return;
        }

        // Left click on ambient NPC to engage
        this.combatManager.engageTarget(data.id);
      },
    );

    container.on("pointerover", () => {
      diamond.clear();
      diamond.fillStyle(color, 1);
      diamond.lineStyle(2, 0xffff00, 1); // Yellow highlight on hover
      diamond.beginPath();
      diamond.moveTo(0, -diamondSize / 2);
      diamond.lineTo(diamondSize / 2, 0);
      diamond.lineTo(0, diamondSize / 2);
      diamond.lineTo(-diamondSize / 2, 0);
      diamond.closePath();
      diamond.fillPath();
      diamond.strokePath();

      // Show tooltip with ambient NPC info (portrait-style)
      this.showAmbientNPCTooltip(data, container);
    });

    container.on("pointerout", () => {
      diamond.clear();
      diamond.fillStyle(color, 1);
      diamond.lineStyle(1, 0xffffff, 0.8);
      diamond.beginPath();
      diamond.moveTo(0, -diamondSize / 2);
      diamond.lineTo(diamondSize / 2, 0);
      diamond.lineTo(0, diamondSize / 2);
      diamond.lineTo(-diamondSize / 2, 0);
      diamond.closePath();
      diamond.fillPath();
      diamond.strokePath();

      this.hideAmbientNPCTooltip();
    });

    // Store data reference
    (container as any).npcData = data;
    (container as any).isAmbient = true;
    (container as any).diamond = diamond;

    // Ensure ambient NPC is visible
    container.setVisible(true);

    this.ambientNPCs.set(data.id, container);
    this.updateAmbientNPCVisuals(container, data);
  }

  private updateAmbientNPCCulling(): void {
    const nowMs: number = Date.now();
    if (
      nowMs - this.lastAmbientCullingAtMs <
      this.AMBIENT_CULLING_INTERVAL_MS
    ) {
      return;
    }
    this.lastAmbientCullingAtMs = nowMs;

    const camera = this.cameras.main;
    const worldView = camera.worldView;

    // Expanded view for initial visibility check
    const expandedView = new Phaser.Geom.Rectangle(
      worldView.x - this.AMBIENT_CULLING_MARGIN_PX,
      worldView.y - this.AMBIENT_CULLING_MARGIN_PX,
      worldView.width + this.AMBIENT_CULLING_MARGIN_PX * 2,
      worldView.height + this.AMBIENT_CULLING_MARGIN_PX * 2,
    );

    // Hysteresis view - slightly larger to prevent rapid toggling
    const hysteresisView = new Phaser.Geom.Rectangle(
      worldView.x -
        this.AMBIENT_CULLING_MARGIN_PX -
        this.AMBIENT_CULLING_HYSTERESIS_PX,
      worldView.y -
        this.AMBIENT_CULLING_MARGIN_PX -
        this.AMBIENT_CULLING_HYSTERESIS_PX,
      worldView.width +
        (this.AMBIENT_CULLING_MARGIN_PX + this.AMBIENT_CULLING_HYSTERESIS_PX) *
          2,
      worldView.height +
        (this.AMBIENT_CULLING_MARGIN_PX + this.AMBIENT_CULLING_HYSTERESIS_PX) *
          2,
    );

    this.ambientNPCs.forEach((npc) => {
      const isCurrentlyVisible = npc.visible;
      const isInMainView = expandedView.contains(npc.x, npc.y);
      const isInHysteresisView = hysteresisView.contains(npc.x, npc.y);

      // Hysteresis logic: only toggle visibility if crossing hysteresis boundary
      let shouldBeVisible = isCurrentlyVisible;
      if (isCurrentlyVisible && !isInHysteresisView) {
        // Was visible, now outside hysteresis - hide it
        shouldBeVisible = false;
      } else if (!isCurrentlyVisible && isInMainView) {
        // Was hidden, now inside main view - show it
        shouldBeVisible = true;
      }

      if (npc.visible === shouldBeVisible) {
        return;
      }

      npc.setVisible(shouldBeVisible);

      // Avoid processing input for offscreen objects
      if (shouldBeVisible) {
        const hitArea = (npc as any).ambientHitArea as
          | Phaser.Geom.Circle
          | undefined;
        if (hitArea) {
          npc.setInteractive(hitArea, Phaser.Geom.Circle.Contains);
        }
      } else {
        npc.disableInteractive();
        if (this.ambientTooltip) {
          this.hideAmbientNPCTooltip();
        }
      }
    });
  }

  private updateAmbientNPCVisuals(
    container: Phaser.GameObjects.Container,
    data: NPCData,
  ): void {
    // Update HP bar only if damaged
    const hpBar = container.getAt(1) as Phaser.GameObjects.Graphics;
    if (!hpBar) return;

    hpBar.clear();

    const hpPercent = data.hp / data.maxHp;
    if (hpPercent < 1) {
      const barWidth = TILE_SIZE * 0.35;
      const barHeight = 2;
      const yOffset = TILE_SIZE * 0.2;

      // Background
      hpBar.fillStyle(0x000000, 0.5);
      hpBar.fillRect(-barWidth / 2, yOffset, barWidth, barHeight);

      // HP fill
      let hpColor = 0x27ae60;
      if (hpPercent < 0.3) hpColor = 0xe74c3c;
      else if (hpPercent < 0.6) hpColor = 0xf39c12;

      hpBar.fillStyle(hpColor, 1);
      hpBar.fillRect(-barWidth / 2, yOffset, barWidth * hpPercent, barHeight);
    }
  }

  private ambientTooltip: Phaser.GameObjects.Container | null = null;

  private ambientTradePanelOutsideClickBounds: Phaser.Geom.Rectangle | null =
    null;
  private ambientTradePanelOutsideClickHandler:
    | ((pointer: Phaser.Input.Pointer) => void)
    | null = null;
  private ambientTradePanelEscHandler: (() => void) | null = null;

  private showAmbientNPCTooltip(
    data: NPCData,
    sprite: Phaser.GameObjects.Container,
  ): void {
    this.hideAmbientNPCTooltip();

    const ambientType: string = data.name;
    const ambientDisplayName: string = this.getAmbientDisplayName(ambientType);
    const rawCategory = data.ambientCategory || "civilian";
    const category = rawCategory === "trader" ? "fisherman" : rawCategory;
    const displayCategory: string =
      ambientType === "driftwood" || ambientType === "floating_barrel"
        ? "debris"
        : category;
    const borderColor = AMBIENT_NPC_COLORS[category] || 0x88cc88;

    const camera = this.cameras.main;
    const screenX = sprite.x - camera.scrollX;
    const screenY = sprite.y - camera.scrollY;

    const tooltipWidth = 160;
    const tooltipHeight = 32;
    const portraitSize = 20;

    let tooltipX = screenX + 25;
    let tooltipY = screenY - 10;

    if (tooltipX + tooltipWidth > camera.width) {
      tooltipX = screenX - tooltipWidth - 10;
    }
    if (tooltipY + tooltipHeight > camera.height) {
      tooltipY = camera.height - tooltipHeight - 5;
    }
    if (tooltipY < 5) {
      tooltipY = 5;
    }

    this.ambientTooltip = this.add.container(tooltipX, tooltipY);
    this.ambientTooltip.setScrollFactor(0);
    this.ambientTooltip.setDepth(200);

    const bg = this.add.rectangle(
      0,
      0,
      tooltipWidth,
      tooltipHeight,
      0x000000,
      0.95,
    );
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, borderColor);
    this.ambientTooltip.add(bg);

    const portraitY = 6;
    const portraitBg = this.add.rectangle(
      4,
      portraitY,
      portraitSize,
      portraitSize,
      0x1a1a1a,
    );
    portraitBg.setOrigin(0, 0);
    portraitBg.setStrokeStyle(1, borderColor);
    this.ambientTooltip.add(portraitBg);

    const portraitKey = (() => {
      if (ambientType === "sailor" || ambientType === "fisherman") {
        const id: string = data.id || "";
        let hash: number = 0;
        for (let i = 0; i < id.length; i++) {
          hash = (hash + id.charCodeAt(i)) % 1024;
        }
        const variant: number = (hash % 4) + 1;
        return `portrait_ambient_${ambientType}_${variant}`;
      }
      return `portrait_ambient_${ambientType}`;
    })();
    if (this.textures.exists(portraitKey)) {
      const portrait = this.add.image(
        4 + portraitSize / 2,
        portraitY + portraitSize / 2,
        portraitKey,
      );
      portrait.setDisplaySize(portraitSize - 2, portraitSize - 2);
      this.ambientTooltip.add(portrait);
    } else {
      const fallbackPortraitLabel: string =
        ambientDisplayName.charAt(0).toUpperCase() || "?";
      const portraitLabel = this.add.text(
        4 + portraitSize / 2,
        portraitY + portraitSize / 2,
        fallbackPortraitLabel,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "10px",
          color: "#ffffff",
          fontStyle: "bold",
        },
      );
      portraitLabel.setOrigin(0.5);
      this.ambientTooltip.add(portraitLabel);
    }

    const textX = 4 + portraitSize + 6;

    const nameText = this.add.text(textX, 7, ambientDisplayName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#ffffff",
      fontStyle: "bold",
    });
    this.ambientTooltip.add(nameText);

    const typeText = this.add.text(textX, 19, displayCategory.toUpperCase(), {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "7px",
      color: "#a0aec0",
    });
    this.ambientTooltip.add(typeText);
  }

  private getAmbientDisplayName(ambientType: string): string {
    const overrides: Record<string, string> = {
      dolphin_pod: "Dolphin Pod",
      seagull_flock: "Seagull Flock",
      jellyfish_bloom: "Jellyfish",
      floating_barrel: "Floating Barrel",
      driftwood: "Driftwood",
      whale: "Whale",
    };

    const override = overrides[ambientType];
    if (override) {
      return override;
    }

    return ambientType
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  private hideAmbientNPCTooltip(): void {
    if (this.ambientTooltip) {
      this.ambientTooltip.destroy();
      this.ambientTooltip = null;
    }
  }

  // ============ AMBIENT TRADE PANEL ============

  private ambientTradePanel: Phaser.GameObjects.Container | null = null;
  private currentTradeAmbientId: string | null = null;

  private showAmbientTradePanel(
    ambientId: string,
    items: Array<{
      itemId: string;
      quantity: number;
      price: number;
      name: string;
    }>,
    traderName: string,
  ): void {
    this.hideAmbientTradePanel();
    this.currentTradeAmbientId = ambientId;

    const camera = this.cameras.main;
    const panelWidth = 260;
    const itemHeight = 30;
    const headerHeight = 46;
    const footerHeight = 26;
    const panelHeight =
      headerHeight + items.length * itemHeight + footerHeight + 8;

    const panelX = (camera.width - panelWidth) / 2;
    const panelY = (camera.height - panelHeight) / 2;

    this.ambientTradePanelOutsideClickBounds = new Phaser.Geom.Rectangle(
      panelX,
      panelY,
      panelWidth,
      panelHeight,
    );

    this.ambientTradePanel = this.add.container(panelX, panelY);
    this.ambientTradePanel.setScrollFactor(0);
    this.ambientTradePanel.setDepth(300);

    // Background
    const bg = this.add.rectangle(
      0,
      0,
      panelWidth,
      panelHeight,
      0x0a1628,
      0.97,
    );
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(2, 0x1f2937);
    bg.setInteractive();
    bg.on(
      "pointerdown",
      (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        bgEvent: Phaser.Types.Input.EventData,
      ) => {
        bgEvent.stopPropagation();
      },
    );
    this.ambientTradePanel.add(bg);

    // Header
    const headerText = this.add.text(
      panelWidth / 2,
      8,
      traderName.toUpperCase(),
      {
        fontFamily: '"Seurat", "Sometype Mono", monospace',
        fontSize: "12px",
        color: "#e5e7eb",
        fontStyle: "bold",
      },
    );
    headerText.setOrigin(0.5, 0);
    this.ambientTradePanel.add(headerText);

    const subText = this.add.text(panelWidth / 2, 26, "CLICK ITEM TO BUY", {
      fontFamily: '"Seurat", "Sometype Mono", monospace',
      fontSize: "9px",
      color: "#9ca3af",
    });
    subText.setOrigin(0.5, 0);
    this.ambientTradePanel.add(subText);

    // Items
    let yOffset = headerHeight;
    for (const item of items) {
      const itemBg = this.add.rectangle(
        6,
        yOffset,
        panelWidth - 12,
        itemHeight - 2,
        0x111827,
      );
      itemBg.setOrigin(0, 0);
      itemBg.setInteractive({ useHandCursor: true });
      this.ambientTradePanel.add(itemBg);

      const nameText = this.add.text(
        12,
        yOffset + 6,
        `${item.name} x${item.quantity}`,
        {
          fontFamily: '"Seurat", "Sometype Mono", monospace',
          fontSize: "10px",
          color: "#e5e7eb",
        },
      );
      this.ambientTradePanel.add(nameText);

      const priceText = this.add.text(
        panelWidth - 12,
        yOffset + 6,
        `${item.price}₱`,
        {
          fontFamily: '"Seurat", "Sometype Mono", monospace',
          fontSize: "10px",
          color: "#fbbf24",
        },
      );
      priceText.setOrigin(1, 0);
      this.ambientTradePanel.add(priceText);

      // Click handler
      itemBg.on("pointerdown", () => {
        if (this.socket) {
          this.socket.emit("ambient:buy", {
            ambientId,
            itemId: item.itemId,
            quantity: 1,
          });
        }
      });

      itemBg.on("pointerover", () => {
        itemBg.setFillStyle(0x1f2937);
      });

      itemBg.on("pointerout", () => {
        itemBg.setFillStyle(0x111827);
      });

      yOffset += itemHeight;
    }

    // Empty state
    if (items.length === 0) {
      const emptyText = this.add.text(
        panelWidth / 2,
        headerHeight + 22,
        "NOTHING TO TRADE",
        {
          fontFamily: '"Seurat", "Sometype Mono", monospace',
          fontSize: "10px",
          color: "#9ca3af",
        },
      );
      emptyText.setOrigin(0.5, 0);
      this.ambientTradePanel.add(emptyText);
    }

    // Close button
    const closeBtn = this.add.rectangle(panelWidth - 16, 10, 14, 14, 0x111827);
    closeBtn.setOrigin(0.5, 0.5);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setStrokeStyle(1, 0x374151);
    this.ambientTradePanel.add(closeBtn);

    const closeX = this.add.text(panelWidth - 16, 10, "X", {
      fontFamily: '"Seurat", "Sometype Mono", monospace',
      fontSize: "10px",
      color: "#9ca3af",
    });
    closeX.setOrigin(0.5, 0.5);
    closeX.setInteractive({ useHandCursor: true });
    this.ambientTradePanel.add(closeX);

    closeBtn.on("pointerdown", () => {
      this.hideAmbientTradePanel();
    });

    closeX.on("pointerdown", () => {
      this.hideAmbientTradePanel();
    });

    this.ambientTradePanelOutsideClickHandler = (
      pointer: Phaser.Input.Pointer,
    ): void => {
      if (!this.ambientTradePanel || !this.ambientTradePanelOutsideClickBounds)
        return;
      if (
        !this.ambientTradePanelOutsideClickBounds.contains(pointer.x, pointer.y)
      ) {
        this.hideAmbientTradePanel();
      }
    };

    this.input.on("pointerdown", this.ambientTradePanelOutsideClickHandler);

    this.ambientTradePanelEscHandler = (): void => {
      this.hideAmbientTradePanel();
    };

    this.input.keyboard?.on("keydown-ESC", this.ambientTradePanelEscHandler);
  }

  private hideAmbientTradePanel(): void {
    if (this.ambientTradePanelOutsideClickHandler) {
      this.input.off("pointerdown", this.ambientTradePanelOutsideClickHandler);
      this.ambientTradePanelOutsideClickHandler = null;
    }

    if (this.ambientTradePanelEscHandler) {
      this.input.keyboard?.off("keydown-ESC", this.ambientTradePanelEscHandler);
      this.ambientTradePanelEscHandler = null;
    }

    this.ambientTradePanelOutsideClickBounds = null;

    if (this.ambientTradePanel) {
      this.ambientTradePanel.destroy();
      this.ambientTradePanel = null;
    }
    this.currentTradeAmbientId = null;
  }

  // ============ AMBIENT NPC CONTEXT MENU ============

  private requestAmbientTrade(ambientId: string): void {
    if (!this.socket) return;
    this.socket.emit("ambient:requestTrade", { ambientId });
  }

  private attackAmbient(ambientId: string): void {
    if (!this.socket) return;
    this.socket.emit("ambient:attack", { ambientId });
  }

  /**
   * Update ambient NPCs from server data
   * NOTE: Ambient NPCs only exist in the CORE WORLD, not in rift or sanctum instances
   */
  private updateAmbientNPCs(npcsData: NPCData[]): void {
    if (this.disableAmbientNPCs) {
      if (this.ambientNPCs.size > 0) {
        this.ambientNPCs.forEach((npc) => npc.destroy());
        this.ambientNPCs.clear();
      }
      return;
    }
    // Don't render ambient NPCs when inside a rift or sanctum instance
    if (this.isInRiftInstance || this.isInSanctumInstance) {
      console.log(
        "[AMBIENT NPC] Skipping render - inside rift/sanctum instance",
      );
      // Clear any existing ambient NPCs
      this.ambientNPCs.forEach((npc) => {
        npc.destroy();
      });
      this.ambientNPCs.clear();
      return;
    }

    // If page is not visible, queue updates instead of processing them
    // This prevents rubberbanding when multiple updates arrive after the tab comes back into focus
    if (!this.isPageVisible) {
      console.log(
        `🔄 Page hidden - queuing ${npcsData.length} ambient NPC updates`,
      );
      npcsData.forEach((data) => {
        this.ambientNPCPendingUpdates.set(data.id, data);
      });
      return;
    }

    const existingIds = new Set(npcsData.map((n) => n.id));

    // Update or create ambient NPCs
    npcsData.forEach((data) => {
      const npc = this.ambientNPCs.get(data.id);

      if (npc) {
        // OPTIMIZATION: Only update position if it actually changed
        const prevData = (npc as any).npcData as NPCData | undefined;
        if (!prevData || prevData.x !== data.x || prevData.y !== data.y) {
          // Calculate adaptive duration based on distance and speed
          // Ambient NPCs move at ~2 tiles/sec (half player speed of 4 tiles/sec)
          const dx = data.x - npc.x;
          const dy = data.y - npc.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const baseSpeed = 2 * TILE_SIZE; // pixels per second (2 tiles/sec for ambient)
          const duration = Math.max(100, (distance / baseSpeed) * 1000); // min 100ms

          // Smooth interpolation: continue existing tween or create new one
          // This prevents visible jumps by updating the target mid-animation
          const existingTween = (npc as any).movementTween as
            | Phaser.Tweens.Tween
            | undefined;
          if (existingTween && existingTween.isPlaying()) {
            // Update existing tween's target position and duration
            existingTween.updateTo("x", data.x, true);
            existingTween.updateTo("y", data.y, true);
            // Update duration by recalculating remaining time
            existingTween.duration = duration;
          } else {
            // Create new tween if none exists
            (npc as any).movementTween = this.tweens.add({
              targets: npc,
              x: data.x,
              y: data.y,
              duration: duration,
              ease: "Linear",
            });
          }
        }

        // OPTIMIZATION: Only update visuals if HP changed
        if (
          !prevData ||
          prevData.hp !== data.hp ||
          prevData.maxHp !== data.maxHp
        ) {
          this.updateAmbientNPCVisuals(npc, data);
        }

        // Update stored data reference for next comparison
        (npc as any).npcData = data;
      } else {
        // Create new ambient NPC
        this.createAmbientNPCSprite(data);
      }
    });

    // Remove ambient NPCs that no longer exist
    this.ambientNPCs.forEach((npc, id) => {
      if (!existingIds.has(id)) {
        // Check if this was our target
        if (this.selectedTarget === id) {
          this.selectedTarget = null;
          this.hideTargetInfo();
        }

        // Stop any active tween
        const existingTween = (npc as any).movementTween as
          | Phaser.Tweens.Tween
          | undefined;
        if (existingTween) {
          existingTween.stop();
        }

        npc.destroy();
        this.ambientNPCs.delete(id);
      }
    });
  }

  /**
   * Update trade routes visualization (visible supply lines)
   */
  private updateTradeRoutes(routes: TradeRouteData[]): void {
    // Store routes
    this.tradeRoutes.clear();
    routes.forEach((route) => {
      this.tradeRoutes.set(route.id, route);
    });

    // Render trade route lines
    this.renderTradeRoutes();
  }

  /**
   * Render trade route lines on the map
   * Shows dashed lines connecting trading posts with active traders highlighted
   */
  private renderTradeRoutes(): void {
    // Create graphics if needed
    if (!this.tradeRouteGraphics) {
      this.tradeRouteGraphics = this.add.graphics();
      this.tradeRouteGraphics.setDepth(1); // Above ocean, below ships
    }

    this.tradeRouteGraphics.clear();

    // Draw each trade route
    this.tradeRoutes.forEach((route) => {
      if (route.waypoints.length < 2) return;

      // Color based on danger level
      let routeColor = 0x88cc88; // Safe - green
      if (route.dangerLevel > 0.6) {
        routeColor = 0xcc4444; // Dangerous - red
      } else if (route.dangerLevel > 0.3) {
        routeColor = 0xccaa44; // Moderate - yellow
      }

      // Draw route line (dashed)
      this.tradeRouteGraphics!.lineStyle(1, routeColor, 0.4);

      for (let i = 0; i < route.waypoints.length - 1; i++) {
        const start = route.waypoints[i];
        const end = route.waypoints[i + 1];

        // Draw dashed line
        this.drawDashedLine(
          this.tradeRouteGraphics!,
          start.x,
          start.y,
          end.x,
          end.y,
          8,
          4, // dash length, gap length
        );
      }

      // If route is a loop, connect last to first
      if (route.waypoints.length > 2) {
        const first = route.waypoints[0];
        const last = route.waypoints[route.waypoints.length - 1];
        this.drawDashedLine(
          this.tradeRouteGraphics!,
          last.x,
          last.y,
          first.x,
          first.y,
          8,
          4,
        );
      }

      // Draw waypoint markers (small squares)
      route.waypoints.forEach((wp) => {
        this.tradeRouteGraphics!.fillStyle(routeColor, 0.6);
        this.tradeRouteGraphics!.fillRect(wp.x - 3, wp.y - 3, 6, 6);
      });
    });
  }

  /**
   * Draw a dashed line between two points
   */
  private drawDashedLine(
    graphics: Phaser.GameObjects.Graphics,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    dashLength: number,
    gapLength: number,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const dashCount = Math.floor(distance / (dashLength + gapLength));

    const unitX = dx / distance;
    const unitY = dy / distance;

    let currentX = x1;
    let currentY = y1;

    for (let i = 0; i < dashCount; i++) {
      const endX = currentX + unitX * dashLength;
      const endY = currentY + unitY * dashLength;

      graphics.beginPath();
      graphics.moveTo(currentX, currentY);
      graphics.lineTo(endX, endY);
      graphics.strokePath();

      currentX = endX + unitX * gapLength;
      currentY = endY + unitY * gapLength;
    }

    // Draw remaining segment if any
    if (currentX !== x2 || currentY !== y2) {
      const remainingDist = Math.sqrt(
        (x2 - currentX) ** 2 + (y2 - currentY) ** 2,
      );
      if (remainingDist > 0) {
        graphics.beginPath();
        graphics.moveTo(currentX, currentY);
        graphics.lineTo(x2, y2);
        graphics.strokePath();
      }
    }
  }

  private updateNPCVisuals(
    container: Phaser.GameObjects.Container,
    data: NPCData,
  ): void {
    // Update HP bar
    this.updateNPCHPBar(container, data);

    // Update aggro range graphics (index 0 in container)
    const aggroRangeGraphics = container.getAt(
      0,
    ) as Phaser.GameObjects.Graphics;
    aggroRangeGraphics.clear();

    // Only show aggro range when NPC is aggroed
    if (data.isAggroed && data.aggroRange) {
      const rangeTiles = Math.ceil(data.aggroRange / TILE_SIZE);
      // Draw at 0,0 since container is positioned at NPC location
      drawAggroRange(aggroRangeGraphics, 0, 0, rangeTiles, true);
    }

    // Update aggro indicator visibility
    const isBoss = (container as any).isBoss || false;
    const aggroIndicator = container.getAt(isBoss ? 4 : 3) as
      | Phaser.GameObjects.Graphics
      | Phaser.GameObjects.Text;
    if (aggroIndicator) {
      aggroIndicator.setVisible(data.isAggroed);
      // For boss aggro indicator, draw a line
      if (isBoss && aggroIndicator instanceof Phaser.GameObjects.Graphics) {
        aggroIndicator.clear();
        if (data.isAggroed) {
          aggroIndicator.lineStyle(2, 0xff0000, 1);
          aggroIndicator.moveTo(-TILE_SIZE, -TILE_SIZE - 4);
          aggroIndicator.lineTo(TILE_SIZE, -TILE_SIZE - 4);
          aggroIndicator.strokePath();
        }
      }
    }
  }

  private updateNPCHPBar(
    container: Phaser.GameObjects.Container,
    data: NPCData,
  ): void {
    const isBoss = (container as any).isBoss || false;
    const hullSize = isBoss ? TILE_SIZE * 2 : TILE_SIZE;

    // Container structure: [0] aggroRangeGraphics, [1] hull, [2] hpBar, [3] aggroIndicator (or [3] crownGraphics, [4] hpBar, [5] aggroIndicator for boss)
    const hpBarIndex = isBoss ? 4 : 2;
    const hpBar = container.getAt(hpBarIndex) as Phaser.GameObjects.Graphics;
    hpBar.clear();

    const hpPercent = data.hp / data.maxHp;
    const barWidth = hullSize;
    const barHeight = isBoss ? 6 : 3;
    const yOffset = hullSize / 2 + 2;

    // Background
    hpBar.fillStyle(0x000000, 0.8);
    hpBar.fillRect(-barWidth / 2, yOffset, barWidth, barHeight);

    // HP fill - color based on percentage
    let hpColor = 0x27ae60; // Green
    if (hpPercent < 0.3)
      hpColor = 0xe74c3c; // Red
    else if (hpPercent < 0.6) hpColor = 0xf39c12; // Orange

    hpBar.fillStyle(hpColor, 1);
    hpBar.fillRect(-barWidth / 2, yOffset, barWidth * hpPercent, barHeight);

    // Border
    hpBar.lineStyle(1, 0xffffff, 0.6);
    hpBar.strokeRect(-barWidth / 2, yOffset, barWidth, barHeight);
  }

  // ============ HUD ============

  private createHUD(): void {
    // Minimal HUD - most info is in React sidebar
    this.hudContainer = this.add.container(0, 0);
    this.hudContainer.setScrollFactor(0);
    this.hudContainer.setDepth(100);
    // No in-game panels - using React sidebar instead
  }

  private updateHUD(): void {
    // HUD updates handled by React sidebar via socket events
  }

  private updateReloadIndicator(): void {
    if (!this.localShip) {
      if (this.reloadBar) {
        this.reloadBar.clear();
        this.reloadBar.setVisible(false);
      }
      return;
    }
    if (!this.reloadBar) {
      this.reloadBar = this.add.graphics();
      this.reloadBar.setDepth(60);
    }
    const shipData = this.localShip.shipData;
    const reloadUnits = shipData.reloadTime || 0;
    if (!reloadUnits || !this.lastFireTimestamp) {
      this.reloadBar.clear();
      this.reloadBar.setVisible(false);
      return;
    }
    const totalMs = reloadUnits * 200;
    const now = Date.now();
    const elapsed = now - this.lastFireTimestamp;
    const ratio = Phaser.Math.Clamp(elapsed / totalMs, 0, 1);
    this.reloadBar.setVisible(true);
    this.reloadBar.clear();
    const barWidth = TILE_SIZE;
    const barHeight = 3;
    const yOffset = -(TILE_SIZE / 2 + 4);
    const x = this.localShip.x;
    const y = this.localShip.y;
    this.reloadBar.fillStyle(0x000000, 0.6);
    this.reloadBar.fillRect(x - barWidth / 2, y + yOffset, barWidth, barHeight);
    const color = ratio >= 1 ? 0x2ecc71 : 0xf1c40f;
    this.reloadBar.fillStyle(color, 1);
    this.reloadBar.fillRect(
      x - barWidth / 2,
      y + yOffset,
      barWidth * ratio,
      barHeight,
    );
    this.reloadBar.lineStyle(1, 0xffffff, 0.5);
    this.reloadBar.strokeRect(
      x - barWidth / 2,
      y + yOffset,
      barWidth,
      barHeight,
    );
  }

  private hideTargetInfo(): void {
    if (this.targetInfo) {
      this.targetInfo.destroy();
      this.targetInfo = null;
    }
  }

  private startStormAmbient(): void {
    if (this.stormAmbientTimer) return;
    if (!this.currentRiftStormBonus) return;

    const scheduleNext = (): void => {
      if (!this.currentRiftStormBonus) {
        this.stopStormAmbient();
        return;
      }

      const delayMs: number = 3500 + Math.floor(Math.random() * 3500);
      this.stormAmbientTimer = this.time.addEvent({
        delay: delayMs,
        callback: () => {
          this.stormAmbientTimer = null;
          if (!this.currentRiftStormBonus) return;

          const candidates = [
            SFX_KEYS.STORM_1,
            SFX_KEYS.STORM_2,
            SFX_KEYS.STORM_3,
            SFX_KEYS.STORM_4,
          ];
          const key = candidates[Math.floor(Math.random() * candidates.length)];
          // Slight randomization so it's not repetitive
          const volume = 0.18 + Math.random() * 0.12;
          playSound(key, volume);
          scheduleNext();
        },
      });
    };

    scheduleNext();
  }

  private stopStormAmbient(): void {
    if (this.stormAmbientTimer) {
      this.stormAmbientTimer.remove(false);
      this.stormAmbientTimer = null;
    }
  }

  private showNPCHoverRange(data: NPCData): void {
    // Create graphics if needed
    if (!this.hoverRangeGraphics) {
      this.hoverRangeGraphics = this.add.graphics();
      this.hoverRangeGraphics.setDepth(3);
    }

    this.hoverRangeGraphics.clear();

    // Get aggro range from NPC data
    const aggroRange = data.aggroRange || 64; // Default 4 tiles
    const rangeTiles = Math.ceil(aggroRange / TILE_SIZE);

    // Draw circular range indicator (orange for NPC hover)
    drawAggroRange(this.hoverRangeGraphics, data.x, data.y, rangeTiles, false);
  }

  private showTargetInfo(data: ShipData): void {
    // Remove existing
    if (this.targetInfo) {
      this.targetInfo.destroy();
    }

    // Hide any tooltip when showing target info
    this.shipRenderer.hideShipTooltip();

    const { width } = this.cameras.main;

    this.targetInfo = this.add.container(width - 10, 10);
    this.targetInfo.setScrollFactor(0);
    this.targetInfo.setDepth(100);

    const bg = this.add.rectangle(0, 0, 200, 80, 0x000000, 0.7);
    bg.setOrigin(1, 0);
    bg.setStrokeStyle(1, 0xff0000);
    this.targetInfo.add(bg);

    const title = this.add.text(-190, 10, `TARGET: ${data.ownerName}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "11px",
      color: "#ff0000",
    });
    this.targetInfo.add(title);

    const hull = this.add.text(
      -190,
      30,
      `Hull: ${data.hullHp}/${data.maxHullHp}`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#ffffff",
      },
    );
    this.targetInfo.add(hull);

    // Distance in tiles (not pixels)
    // const distanceTiles = this.localShip
    //   ? Math.round(Phaser.Math.Distance.Between(this.localShip.x, this.localShip.y, data.x, data.y) / TILE_SIZE)
    //   : 0;
    // const inRange = distanceTiles <= 8;

    // const distText = this.add.text(-190, 50, `Distance: ${distanceTiles} tiles ${inRange ? '(in range)' : ''}`, {
    //   fontFamily: '"Sometype Mono", monospace',
    //   fontSize: '10px',
    //   color: inRange ? '#2ecc71' : '#888888',
    // });
    // this.targetInfo.add(distText);
  }

  private createInstructions(): void {
    // Instructions moved to React footer - no in-game text
  }

  // ============ FISHING UI ============

  /**
   * Show fishing status indicator
   */
  private showFishingStatus(message: string, isActive: boolean): void {
    // Clear existing UI
    if (this.fishingStatusUI) {
      this.fishingStatusUI.destroy();
      this.fishingStatusUI = null;
    }

    if (!isActive) return;

    const { width } = this.cameras.main;

    this.fishingStatusUI = this.add.container(width / 2, 80);
    this.fishingStatusUI.setScrollFactor(0);
    this.fishingStatusUI.setDepth(100);

    // Background
    const bg = this.add.rectangle(0, 0, 200, 50, 0x000000, 0.8);
    bg.setStrokeStyle(1, 0x5dade2);
    this.fishingStatusUI.add(bg);

    // Fishing icon
    const icon = this.add.text(-75, -10, "🎣", { fontSize: "14px" });
    icon.setOrigin(0.5);
    this.fishingStatusUI.add(icon);

    // Status text
    const text = this.add.text(-20, -10, "Fishing...", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "12px",
      color: "#5dade2",
    });
    text.setOrigin(0.5);
    this.fishingStatusUI.add(text);

    // SPACE prompt
    const spacePrompt = this.add.text(0, 10, "Press SPACE to engage minigame", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "10px",
      color: "#8b949e",
    });
    spacePrompt.setOrigin(0.5);
    this.fishingStatusUI.add(spacePrompt);
  }

  /**
   * Show a fishing message (no catch, etc.)
   */
  private showFishingMessage(message: string, color: string): void {
    if (!this.localShip) return;

    const text = this.add.text(
      this.localShip.x,
      this.localShip.y - 40,
      message,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: color,
        backgroundColor: "#00000088",
        padding: { x: 4, y: 2 },
      },
    );
    text.setOrigin(0.5);
    text.setDepth(150);

    // Fade out and destroy
    this.tweens.add({
      targets: text,
      alpha: 0,
      y: text.y - 20,
      duration: 2000,
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Show a fish catch notification
   */
  private showFishingCatch(
    fish: { id: string; name: string; category: string; value: number },
    _message: string,
    color: string,
  ): void {
    if (!this.localShip) return;

    // Simple toast notification (same style as no-catch message)
    const text = this.add.text(
      this.localShip.x,
      this.localShip.y - 40,
      `🐟 ${fish.name}`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: color,
        backgroundColor: "#00000088",
        padding: { x: 4, y: 2 },
      },
    );
    text.setOrigin(0.5);
    text.setDepth(150);

    // Fade out and destroy
    this.tweens.add({
      targets: text,
      alpha: 0,
      y: text.y - 20,
      duration: 2000,
      onComplete: () => text.destroy(),
    });

    // Play sound effect based on rarity
    if (fish.category === "legendary") {
      console.log("🌟 LEGENDARY CATCH!");
    }
  }

  /**
   * Show a fishing error with prominent visual feedback
   */
  private showFishingError(message: string): void {
    // Clear any fishing status UI
    if (this.fishingStatusUI) {
      this.fishingStatusUI.destroy();
      this.fishingStatusUI = null;
    }
    this.isFishing = false;

    // Show error at top of screen (more visible than floating text)
    const { width } = this.cameras.main;

    const errorContainer = this.add.container(width / 2, 100);
    errorContainer.setScrollFactor(0);
    errorContainer.setDepth(200);

    // Background with red border
    const bg = this.add.rectangle(0, 0, 300, 50, 0x1a0a0a, 0.95);
    bg.setStrokeStyle(2, 0xe74c3c);
    errorContainer.add(bg);

    // Error icon
    const icon = this.add.text(-130, 0, "❌", { fontSize: "18px" });
    icon.setOrigin(0.5);
    errorContainer.add(icon);

    // Error message
    const text = this.add.text(10, 0, message, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "11px",
      color: "#e74c3c",
      wordWrap: { width: 220 },
    });
    text.setOrigin(0.5);
    errorContainer.add(text);

    // Shake animation for attention
    this.tweens.add({
      targets: errorContainer,
      x: { from: width / 2 - 5, to: width / 2 + 5 },
      duration: 50,
      yoyo: true,
      repeat: 3,
    });

    // Fade out after delay
    this.tweens.add({
      targets: errorContainer,
      alpha: 0,
      y: 80,
      duration: 500,
      delay: 3000,
      onComplete: () => errorContainer.destroy(),
    });
  }

  /**
   * Stop fishing when player moves
   */
  private stopFishingOnMove(): void {
    if (this.isFishing) {
      this.socket?.emit("fishing:stop");
      this.isFishing = false;
      if (this.fishingStatusUI) {
        this.fishingStatusUI.destroy();
        this.fishingStatusUI = null;
      }
      // Also deactivate the minigame UI to prevent stuck state
      this.fishingManager.getFishingMinigameUI().deactivate();
    }
  }

  // ============ TREASURE SYSTEM ============

  /**
   * Show a treasure marker on the map (2x2 tile square visual)
   */
  private showTreasureMarker(
    id: string,
    x: number,
    y: number,
    reward: number,
    expiresIn: number,
  ): void {
    // Remove existing marker if any
    this.removeTreasureMarker(id);

    const container = this.add.container(x, y);
    container.setDepth(50);

    // 2x2 tile square background (32x32 pixels)
    const squareBg = this.add.graphics();
    squareBg.fillStyle(0xffd700, 0.15);
    squareBg.fillRect(-TILE_SIZE, -TILE_SIZE, TILE_SIZE * 2, TILE_SIZE * 2);
    squareBg.lineStyle(2, 0xffd700, 0.8);
    squareBg.strokeRect(-TILE_SIZE, -TILE_SIZE, TILE_SIZE * 2, TILE_SIZE * 2);
    container.add(squareBg);

    // Inner X marker
    const xMark = this.add.graphics();
    xMark.lineStyle(2, 0xffd700, 1);
    xMark.moveTo(-8, -8);
    xMark.lineTo(8, 8);
    xMark.moveTo(8, -8);
    xMark.lineTo(-8, 8);
    xMark.strokePath();
    container.add(xMark);

    // Label above
    const label = this.add.text(0, -TILE_SIZE - 12, `[TREASURE]`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#ffd700",
      backgroundColor: "#000000aa",
      padding: { x: 4, y: 2 },
    });
    label.setOrigin(0.5);
    container.add(label);

    // Reward text below
    const rewardText = this.add.text(0, TILE_SIZE + 12, `${reward}₱`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#ffd700",
      backgroundColor: "#000000aa",
      padding: { x: 4, y: 2 },
    });
    rewardText.setOrigin(0.5);
    container.add(rewardText);

    // Pulsing animation on square
    this.tweens.add({
      targets: squareBg,
      alpha: { from: 0.3, to: 0.6 },
      duration: 600,
      yoyo: true,
      repeat: -1,
    });

    // Make clickable to collect - left click
    const hitArea = this.add.rectangle(
      0,
      0,
      TILE_SIZE * 2.5,
      TILE_SIZE * 2.5,
      0x000000,
      0,
    );
    hitArea.setInteractive();
    hitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) return; // Handle right-click separately
      if (this.localShip) {
        const dist = Phaser.Math.Distance.Between(
          this.localShip.x,
          this.localShip.y,
          x,
          y,
        );
        if (dist < TILE_SIZE * 3) {
          this.socket?.emit("treasure:collect", { id });
        } else {
          this.showFloatingMessage(
            "Too far away! Sail closer to collect.",
            "#ffaa00",
          );
        }
      }
    });

    // Right click for context menu
    hitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!pointer.rightButtonDown()) return;
      this.game.events.emit("debris:contextmenu", {
        type: "treasure",
        id,
        x: pointer.x,
        y: pointer.y,
        worldX: x,
        worldY: y,
      });
    });

    // Visual feedback on hover
    hitArea.on("pointerover", () => {
      container.setScale(1.1);
    });
    hitArea.on("pointerout", () => {
      container.setScale(1.0);
    });

    container.add(hitArea);

    this.treasureMarkers.set(id, container);

    // Store active treasure info
    this.activeTreasure = {
      id,
      x,
      y,
      reward,
      expiresAt: Date.now() + expiresIn,
    };

    // Show notification
    this.showFloatingMessage(
      "Treasure location revealed! Sail to the square to collect.",
      "#ffd700",
    );
  }

  /**
   * Remove a treasure marker
   */
  private removeTreasureMarker(id: string): void {
    const marker = this.treasureMarkers.get(id);
    if (marker) {
      marker.destroy();
      this.treasureMarkers.delete(id);
    }
    if (this.activeTreasure?.id === id) {
      this.activeTreasure = null;
    }
  }

  /**
   * Show treasure collected notification
   */
  private showTreasureCollectedNotification(
    reward: number,
    loot: Array<{ itemId: string; name: string; quantity: number }>,
  ): void {
    const { width, height } = this.cameras.main;

    const container = this.add.container(width / 2, height / 2);
    container.setScrollFactor(0);
    container.setDepth(200);

    // Background
    const bg = this.add.rectangle(
      0,
      0,
      250,
      120 + loot.length * 20,
      0x1a1a2e,
      0.95,
    );
    bg.setStrokeStyle(2, 0xffd700);
    container.add(bg);

    // Title
    const title = this.add.text(0, -40 - loot.length * 5, "[TREASURE FOUND]", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "14px",
      color: "#ffd700",
    });
    title.setOrigin(0.5);
    container.add(title);

    // Pieces reward
    const piecesText = this.add.text(0, -15, `+${reward} pieces`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "12px",
      color: "#2ecc71",
    });
    piecesText.setOrigin(0.5);
    container.add(piecesText);

    // Loot items
    loot.forEach((item, i) => {
      const itemText = this.add.text(
        0,
        10 + i * 18,
        `+${item.quantity}x ${item.name}`,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "10px",
          color: "#5dade2",
        },
      );
      itemText.setOrigin(0.5);
      container.add(itemText);
    });

    // Scale in animation
    container.setScale(0);
    this.tweens.add({
      targets: container,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: "Back.easeOut",
    });

    // Fade out after delay
    this.tweens.add({
      targets: container,
      alpha: 0,
      y: height / 2 - 50,
      duration: 500,
      delay: 3000,
      onComplete: () => container.destroy(),
    });

    playSound(SFX_KEYS.FISHING_RARE_CATCH);
  }

  /**
   * Show visual effect for signature move activation - element-specific grid-style effects
   */
  private showWildcardPowerVisual(
    casterX: number,
    casterY: number,
    targetX: number,
    targetY: number,
    color: string,
    aoeRadius: number,
    element: string = "flame",
  ): void {
    // Snap to tile grid (16px = 1 tile)
    const snappedCasterX = Math.round(casterX / TILE_SIZE) * TILE_SIZE;
    const snappedCasterY = Math.round(casterY / TILE_SIZE) * TILE_SIZE;
    const snappedTargetX = Math.round(targetX / TILE_SIZE) * TILE_SIZE;
    const snappedTargetY = Math.round(targetY / TILE_SIZE) * TILE_SIZE;

    const colorNum = parseInt(color.replace("#", ""), 16);

    // Use the new SignatureMoveEffectsRenderer
    this.signatureMoveEffectsRenderer.showSignatureMoveEffect({
      casterX: snappedCasterX,
      casterY: snappedCasterY,
      targetX: snappedTargetX,
      targetY: snappedTargetY,
      aoeRadiusTiles: Math.max(aoeRadius, 2),
      color: colorNum,
      element,
      moveId: `${Date.now()}_${Math.random()}`,
    });
  }

  /**
   * FLAME: Rising square embers + flicker effect
   */
  private showFlameEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Create rising ember squares
    const emberCount = 12;
    for (let i = 0; i < emberCount; i++) {
      const ember = this.add.graphics();
      const size = 4 + Math.random() * 8;
      const offsetX = (Math.random() - 0.5) * radius * 1.5;
      const offsetY = (Math.random() - 0.5) * radius;

      ember.fillStyle(color, 0.8 + Math.random() * 0.2);
      ember.fillRect(-size / 2, -size / 2, size, size);
      ember.setPosition(offsetX, offsetY);
      container.add(ember);

      // Rising + flickering animation
      this.tweens.add({
        targets: ember,
        y: offsetY - 40 - Math.random() * 30,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 600 + Math.random() * 400,
        ease: "Power1",
        delay: Math.random() * 200,
      });
    }

    // Central burst square
    const burst = this.add.graphics();
    burst.fillStyle(color, 0.5);
    burst.fillRect(-radius * 0.4, -radius * 0.4, radius * 0.8, radius * 0.8);
    burst.lineStyle(3, color, 1);
    burst.strokeRect(-radius * 0.5, -radius * 0.5, radius, radius);
    container.add(burst);

    // Flicker effect on burst
    this.tweens.add({
      targets: burst,
      alpha: 0,
      scaleX: 1.8,
      scaleY: 1.8,
      duration: 500,
      ease: "Power2",
      yoyo: false,
    });

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * STORM: Jagged line bolts + strobing rectangles
   */
  private showStormEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Create lightning bolt lines (jagged)
    const boltCount = 4;
    for (let b = 0; b < boltCount; b++) {
      const bolt = this.add.graphics();
      bolt.lineStyle(2 + Math.random() * 2, color, 1);

      const angle = (b / boltCount) * Math.PI * 2 + Math.random() * 0.5;
      const segments = 4 + Math.floor(Math.random() * 3);
      let px = 0,
        py = 0;

      bolt.beginPath();
      bolt.moveTo(0, 0);

      for (let s = 1; s <= segments; s++) {
        const dist = (s / segments) * radius;
        const jitter = (Math.random() - 0.5) * 20;
        px = Math.cos(angle) * dist + jitter;
        py = Math.sin(angle) * dist + jitter;
        bolt.lineTo(px, py);
      }
      bolt.strokePath();
      container.add(bolt);

      // Flash and fade
      this.tweens.add({
        targets: bolt,
        alpha: 0,
        duration: 150 + Math.random() * 100,
        delay: b * 50,
        yoyo: true,
        repeat: 2,
      });
    }

    // Strobing rectangles
    const strobeCount = 6;
    for (let i = 0; i < strobeCount; i++) {
      const strobe = this.add.graphics();
      const size = 8 + Math.random() * 12;
      const sx = (Math.random() - 0.5) * radius * 1.2;
      const sy = (Math.random() - 0.5) * radius * 1.2;

      strobe.fillStyle(color, 0.9);
      strobe.fillRect(-size / 2, -size / 2, size, size);
      strobe.setPosition(sx, sy);
      strobe.setAlpha(0);
      container.add(strobe);

      // Strobe flash
      this.tweens.add({
        targets: strobe,
        alpha: 1,
        duration: 50,
        delay: i * 80 + Math.random() * 50,
        yoyo: true,
        repeat: 1,
      });
    }

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * TIDE: Expanding ring of tile-squares + spray dots
   */
  private showTideEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Create expanding ring of squares
    const ringSquares = 16;
    for (let i = 0; i < ringSquares; i++) {
      const square = this.add.graphics();
      const angle = (i / ringSquares) * Math.PI * 2;
      const size = 6;

      square.fillStyle(color, 0.7);
      square.fillRect(-size / 2, -size / 2, size, size);
      square.setPosition(Math.cos(angle) * 10, Math.sin(angle) * 10);
      container.add(square);

      // Expand outward
      this.tweens.add({
        targets: square,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        alpha: 0,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 600,
        ease: "Power1",
      });
    }

    // Spray dots (small squares)
    const sprayCount = 20;
    for (let i = 0; i < sprayCount; i++) {
      const spray = this.add.graphics();
      const size = 2 + Math.random() * 4;
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.5;

      spray.fillStyle(color, 0.5 + Math.random() * 0.3);
      spray.fillRect(-size / 2, -size / 2, size, size);
      spray.setPosition(Math.cos(angle) * dist, Math.sin(angle) * dist);
      container.add(spray);

      // Scatter outward
      this.tweens.add({
        targets: spray,
        x: Math.cos(angle) * (radius + Math.random() * 20),
        y: Math.sin(angle) * (radius + Math.random() * 20) - 10,
        alpha: 0,
        duration: 400 + Math.random() * 300,
        delay: Math.random() * 150,
        ease: "Power1",
      });
    }

    // Central wave square
    const wave = this.add.graphics();
    wave.lineStyle(3, color, 0.8);
    wave.strokeRect(-radius * 0.3, -radius * 0.3, radius * 0.6, radius * 0.6);
    container.add(wave);

    this.tweens.add({
      targets: wave,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 700,
      ease: "Power2",
    });

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * SHADOW: Pulsing dark squares + expanding rings
   */
  private showShadowEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Dark pulse (expanding dark square)
    const pulse = this.add.graphics();
    pulse.fillStyle(0x000000, 0.6);
    pulse.fillRect(-radius * 0.2, -radius * 0.2, radius * 0.4, radius * 0.4);
    pulse.lineStyle(2, color, 0.8);
    pulse.strokeRect(
      -radius * 0.25,
      -radius * 0.25,
      radius * 0.5,
      radius * 0.5,
    );
    container.add(pulse);

    this.tweens.add({
      targets: pulse,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 500,
      ease: "Power3",
    });

    // Afterimage squares (trailing shadows)
    const afterimageCount = 8;
    for (let i = 0; i < afterimageCount; i++) {
      const afterimage = this.add.graphics();
      const size = 10 + Math.random() * 15;
      const angle = (i / afterimageCount) * Math.PI * 2;
      const dist = radius * 0.4;

      afterimage.fillStyle(color, 0.4);
      afterimage.fillRect(-size / 2, -size / 2, size, size);
      afterimage.setPosition(Math.cos(angle) * dist, Math.sin(angle) * dist);
      afterimage.setAlpha(0);
      container.add(afterimage);

      // Fade in then out (afterimage effect)
      this.tweens.add({
        targets: afterimage,
        alpha: 0.7,
        duration: 100,
        delay: i * 40,
        yoyo: true,
        hold: 100,
        onComplete: () => {
          this.tweens.add({
            targets: afterimage,
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius,
            alpha: 0,
            duration: 300,
          });
        },
      });
    }

    // Screen darken effect (brief)
    const darken = this.add.graphics();
    darken.fillStyle(0x000000, 0.3);
    darken.fillRect(-400, -300, 800, 600);
    darken.setScrollFactor(0);
    darken.setDepth(199);

    this.tweens.add({
      targets: darken,
      alpha: 0,
      duration: 400,
      onComplete: () => darken.destroy(),
    });

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * IRON: Metallic squares + sliding blocks
   */
  private showIronEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Central impact square
    const impact = this.add.graphics();
    impact.fillStyle(color, 0.6);
    impact.fillRect(-radius * 0.3, -radius * 0.3, radius * 0.6, radius * 0.6);
    impact.lineStyle(4, color, 1);
    impact.strokeRect(
      -radius * 0.35,
      -radius * 0.35,
      radius * 0.7,
      radius * 0.7,
    );
    container.add(impact);

    this.tweens.add({
      targets: impact,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 300,
      ease: "Power2",
    });

    // Chunky shrapnel squares flying outward
    const shrapnelCount = 10;
    for (let i = 0; i < shrapnelCount; i++) {
      const shrapnel = this.add.graphics();
      const size = 8 + Math.random() * 12;
      const angle = (i / shrapnelCount) * Math.PI * 2 + Math.random() * 0.3;

      // Metallic look - darker fill with bright edge
      shrapnel.fillStyle(0x444444, 0.9);
      shrapnel.fillRect(-size / 2, -size / 2, size, size);
      shrapnel.lineStyle(1, color, 1);
      shrapnel.strokeRect(-size / 2, -size / 2, size, size);
      shrapnel.setPosition(0, 0);
      shrapnel.setRotation(Math.random() * Math.PI);
      container.add(shrapnel);

      // Fly outward with rotation
      const targetX = Math.cos(angle) * (radius + Math.random() * 30);
      const targetY = Math.sin(angle) * (radius + Math.random() * 30);

      this.tweens.add({
        targets: shrapnel,
        x: targetX,
        y: targetY,
        rotation: shrapnel.rotation + Math.PI * 2,
        alpha: 0,
        duration: 400 + Math.random() * 200,
        delay: Math.random() * 100,
        ease: "Power1",
      });
    }

    // Ground impact lines (cracks)
    const crackCount = 4;
    for (let i = 0; i < crackCount; i++) {
      const crack = this.add.graphics();
      crack.lineStyle(2, color, 0.6);

      const angle = (i / crackCount) * Math.PI * 2 + Math.PI / 4;
      crack.beginPath();
      crack.moveTo(0, 0);
      crack.lineTo(
        Math.cos(angle) * radius * 0.8,
        Math.sin(angle) * radius * 0.8,
      );
      crack.strokePath();
      container.add(crack);

      this.tweens.add({
        targets: crack,
        alpha: 0,
        duration: 600,
        delay: 200,
      });
    }

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * SPIRIT: Glowing orbs + wave rings
   */
  private showSpiritEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Concentric expanding squares (shock waves)
    const waveCount = 4;
    for (let i = 0; i < waveCount; i++) {
      const wave = this.add.graphics();
      const startSize = radius * 0.2;

      wave.lineStyle(3 - i * 0.5, color, 0.9 - i * 0.15);
      wave.strokeRect(-startSize / 2, -startSize / 2, startSize, startSize);
      wave.setScale(0.5);
      container.add(wave);

      this.tweens.add({
        targets: wave,
        scaleX: 3 + i * 0.5,
        scaleY: 3 + i * 0.5,
        alpha: 0,
        duration: 600,
        delay: i * 100,
        ease: "Power2",
      });
    }

    // Central glow square
    const glow = this.add.graphics();
    glow.fillStyle(color, 0.4);
    glow.fillRect(-radius * 0.15, -radius * 0.15, radius * 0.3, radius * 0.3);
    container.add(glow);

    this.tweens.add({
      targets: glow,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 400,
    });

    // Screen tint effect (purple/spirit overlay)
    const tint = this.add.graphics();
    tint.fillStyle(color, 0.15);
    tint.fillRect(-400, -300, 800, 600);
    tint.setScrollFactor(0);
    tint.setDepth(199);
    tint.setAlpha(0);

    this.tweens.add({
      targets: tint,
      alpha: 1,
      duration: 150,
      yoyo: true,
      hold: 100,
      onComplete: () => tint.destroy(),
    });

    // Radiating lines from center
    const lineCount = 8;
    for (let i = 0; i < lineCount; i++) {
      const line = this.add.graphics();
      const angle = (i / lineCount) * Math.PI * 2;

      line.lineStyle(1, color, 0.6);
      line.beginPath();
      line.moveTo(0, 0);
      line.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
      line.strokePath();
      line.setAlpha(0);
      container.add(line);

      this.tweens.add({
        targets: line,
        alpha: 0.8,
        duration: 100,
        delay: 150 + i * 30,
        yoyo: true,
        hold: 50,
      });
    }

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * DEFAULT: Generic colored squares
   */
  private showDefaultEffect(
    x: number,
    y: number,
    color: number,
    radius: number,
  ): void {
    const container = this.add.container(x, y);
    container.setDepth(200);

    const graphics = this.add.graphics();
    graphics.lineStyle(3, color, 1);
    graphics.strokeRect(-radius, -radius, radius * 2, radius * 2);

    graphics.lineStyle(2, color, 0.7);
    graphics.strokeRect(
      -radius * 0.6,
      -radius * 0.6,
      radius * 1.2,
      radius * 1.2,
    );

    graphics.fillStyle(color, 0.3);
    graphics.fillRect(-radius * 0.3, -radius * 0.3, radius * 0.6, radius * 0.6);
    container.add(graphics);

    this.tweens.add({
      targets: graphics,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 800,
      ease: "Power2",
    });

    // Cleanup after 3 seconds
    this.time.delayedCall(3000, () => container.destroy());
  }

  /**
   * Render escape smoke effect - tile-based square grid lasting 30+ seconds
   */
  private renderEscapeSmokeEffect(x: number, y: number): void {
    const TILE_SIZE = 16;
    const SMOKE_RADIUS_TILES = 5; // 5 tiles radius = 80px
    const SMOKE_DURATION = 32000; // 32 seconds
    const FADE_START = 28000; // Start fading at 28s

    const container = this.add.container(x, y);
    container.setDepth(150);

    // Create tile-based smoke grid (square tiles)
    const smokeGridTiles: Phaser.GameObjects.Rectangle[] = [];

    for (
      let tileX = -SMOKE_RADIUS_TILES;
      tileX <= SMOKE_RADIUS_TILES;
      tileX++
    ) {
      for (
        let tileY = -SMOKE_RADIUS_TILES;
        tileY <= SMOKE_RADIUS_TILES;
        tileY++
      ) {
        // Only fill tiles within circular radius for natural look
        const distFromCenter = Math.sqrt(tileX * tileX + tileY * tileY);
        if (distFromCenter <= SMOKE_RADIUS_TILES) {
          const pixelX = tileX * TILE_SIZE;
          const pixelY = tileY * TILE_SIZE;

          const smokeTile = this.add.rectangle(
            pixelX,
            pixelY,
            TILE_SIZE - 2,
            TILE_SIZE - 2,
            0x888888,
            0.6,
          );
          smokeTile.setStrokeStyle(1, 0x666666, 0.4);
          container.add(smokeTile);
          smokeGridTiles.push(smokeTile);
        }
      }
    }

    // Animate fade out starting at 28 seconds
    this.tweens.add({
      targets: smokeGridTiles,
      alpha: 0,
      duration: SMOKE_DURATION - FADE_START,
      delay: FADE_START,
      ease: "Linear",
      onComplete: () => {
        container.destroy();
      },
    });
  }

  /**
   * Show a floating message above the player (or center of screen if no ship)
   */
  public showFloatingMessage(message: string, color: string): void {
    console.log(`💬 Floating message: ${message} (${color})`);

    // If no local ship, show at center of camera
    const x = this.localShip?.x || this.cameras.main.centerX;
    const y = (this.localShip?.y || this.cameras.main.centerY) - 50;

    const nowMs: number = Date.now();
    const key: string = `${message}|${color}`;
    if (
      this.lastFloatingMessageKey === key &&
      nowMs - this.lastFloatingMessageAtMs < 400
    ) {
      return;
    }
    this.lastFloatingMessageAtMs = nowMs;
    this.lastFloatingMessageKey = key;

    if (this.floatingMessageTween) {
      this.floatingMessageTween.stop();
      this.floatingMessageTween = null;
    }

    if (!this.floatingMessageText || !this.floatingMessageBg) {
      this.floatingMessageText = this.add.text(x, y, message, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: color,
        padding: { x: 0, y: 0 },
      });
      this.floatingMessageText.setOrigin(0.5);
      this.floatingMessageText.setDepth(150);

      this.floatingMessageBg = this.add.rectangle(
        this.floatingMessageText.x,
        this.floatingMessageText.y,
        this.floatingMessageText.width + 12,
        this.floatingMessageText.height + 6,
        0x000000,
        0.55,
      );
      this.floatingMessageBg.setOrigin(0.5);
      this.floatingMessageBg.setDepth(149);
    } else {
      this.floatingMessageText.setAlpha(1);
      this.floatingMessageBg.setAlpha(1);
      this.floatingMessageText.setPosition(x, y);
      this.floatingMessageText.setText(message);
      this.floatingMessageText.setColor(color);

      this.floatingMessageBg.setPosition(
        this.floatingMessageText.x,
        this.floatingMessageText.y,
      );
      this.floatingMessageBg.setSize(
        this.floatingMessageText.width + 12,
        this.floatingMessageText.height + 6,
      );
    }

    const startY: number = this.floatingMessageText.y;
    this.floatingMessageTween = this.tweens.add({
      targets: [this.floatingMessageText, this.floatingMessageBg],
      alpha: 0,
      y: startY - 30,
      duration: 3000,
      onComplete: () => {
        if (this.floatingMessageText) {
          this.floatingMessageText.destroy();
          this.floatingMessageText = null;
        }
        if (this.floatingMessageBg) {
          this.floatingMessageBg.destroy();
          this.floatingMessageBg = null;
        }
        this.floatingMessageTween = null;
      },
    });
  }

  /**
   * Show a crafting ready toast notification (when craft completes while offline)
   */
  private showCraftingReadyToast(recipeName: string, quantity: number): void {
    const { width, height } = this.cameras.main;

    const container = this.add.container(width / 2, height - 100);
    container.setScrollFactor(0);
    container.setDepth(200);

    const panelWidth = 280;
    const panelHeight = 60;

    // Background
    const bg = this.add.rectangle(
      0,
      0,
      panelWidth,
      panelHeight,
      0x0d1117,
      0.95,
    );
    bg.setStrokeStyle(2, 0x2ecc71);
    container.add(bg);

    // Icon
    const icon = this.add.text(-panelWidth / 2 + 30, 0, "⚒️", {
      fontSize: "24px",
    });
    icon.setOrigin(0.5);
    container.add(icon);

    // Title
    const title = this.add.text(
      -panelWidth / 2 + 60,
      -12,
      "CRAFTING COMPLETE",
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "11px",
        fontStyle: "bold",
        color: "#2ecc71",
      },
    );
    title.setOrigin(0, 0.5);
    container.add(title);

    // Item name
    const itemText = this.add.text(
      -panelWidth / 2 + 60,
      8,
      `${quantity > 1 ? quantity + "x " : ""}${recipeName}`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "12px",
        color: "#ffffff",
      },
    );
    itemText.setOrigin(0, 0.5);
    container.add(itemText);

    // Hint
    const hint = this.add.text(panelWidth / 2 - 10, 0, "Press C to collect", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#8b949e",
    });
    hint.setOrigin(1, 0.5);
    container.add(hint);

    // Slide in animation
    container.setY(height + 50);
    this.tweens.add({
      targets: container,
      y: height - 100,
      duration: 400,
      ease: "Back.easeOut",
    });

    // Fade out after delay
    this.tweens.add({
      targets: container,
      alpha: 0,
      y: height - 140,
      duration: 500,
      delay: 6000,
      onComplete: () => container.destroy(),
    });
  }

  /**
   * Show a chat message above a ship
   * Messages float above the sender's ship and fade out over a longer duration
   * Name text and border use the ship's color; message text stays white.
   */
  private showChatMessage(
    senderId: string,
    senderName: string,
    shipColor: string | number | undefined,
    message: string,
    position?: { x: number; y: number },
  ): void {
    // Find the ship sprite for this sender
    const senderShip = this.ships.get(senderId);

    // Fallback to world center if position is undefined
    const fallbackPosition = { x: 4000, y: 4000 };
    const pos = position || fallbackPosition;

    console.log(
      `💬 Chat from ${senderName} (${senderId}), ship found: ${!!senderShip}, position: (${pos.x}, ${pos.y})`,
    );

    // Use ship position if available, otherwise use provided position
    const x = senderShip ? senderShip.x : pos.x;
    const y = senderShip ? senderShip.y - 40 : pos.y - 40;

    // Create chat bubble container
    const container = this.add.container(x, y);
    container.setDepth(200);

    // Resolve ship color (fallback to light blue)
    let nameColorHex: string = "#5dade2";
    let borderColor: number = 0x3a3a3a;
    if (typeof shipColor === "string") {
      nameColorHex = shipColor;
      borderColor = Phaser.Display.Color.HexStringToColor(shipColor).color;
    } else if (typeof shipColor === "number") {
      borderColor = shipColor;
      const c = Phaser.Display.Color.IntegerToColor(shipColor);
      nameColorHex = `#${c.color32.toString(16).slice(2).padStart(6, "0")}`;
    }

    // Two-part inline text: name (colored) + message (white)
    const nameLabel = `${senderName}:`;
    const nameText = this.add.text(0, 0, nameLabel, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "10px",
      color: nameColorHex,
    });
    nameText.setOrigin(0, 0.5);

    const messageText = this.add.text(nameText.width + 4, 0, ` ${message}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "10px",
      color: "#ffffff",
    });
    messageText.setOrigin(0, 0.5);

    const totalWidth = messageText.x + messageText.width;
    const lineHeight = Math.max(nameText.height, messageText.height);

    // Wrap texts in a sub-container so we can center them easily
    const textContainer = this.add.container(-totalWidth / 2, 0, [
      nameText,
      messageText,
    ]);

    // Background rectangle sized to total text width
    const padding = 6;
    const bgWidth = totalWidth + padding * 2;
    const bgHeight = lineHeight + padding * 2;

    const bg = this.add.rectangle(0, 0, bgWidth, bgHeight, 0x000000, 0.8);
    bg.setStrokeStyle(1, borderColor);

    // Add elements to container (bg first so it's behind text)
    container.add([bg, textContainer]);

    // Animate: float up and fade out (longer duration ~20s)
    this.tweens.add({
      targets: container,
      y: container.y - 30,
      alpha: 0,
      duration: 60000,
      ease: "Cubic.easeOut",
      onComplete: () => container.destroy(),
    });
  }

  /**
   * Show a global broadcast message (admin announcement, event, etc.)
   * Displays as a centered toast at the top of the screen
   */
  private showGlobalBroadcast(
    message: string,
    type: string,
    duration: number,
  ): void {
    const { width } = this.cameras.main;

    // Monospace, boxy aesthetic for all broadcast types
    const colors: Record<string, { border: number; text: string }> = {
      info: { border: 0x5dade2, text: "#c9d1d9" },
      warning: { border: 0xf39c12, text: "#f1c40f" },
      success: { border: 0x27ae60, text: "#2ecc71" },
      event: { border: 0x9b59b6, text: "#d2b4de" },
    };
    const style = colors[type] || colors.info;

    // Create container at top center of screen
    const container = this.add.container(width / 2, 52);
    container.setScrollFactor(0);
    container.setDepth(1000);
    container.setAlpha(0);

    // Message text (monospace, no emoji)
    const text = this.add.text(0, 0, message, {
      fontFamily: "var(--font-mono)",
      fontSize: "12px",
      color: style.text,
      wordWrap: { width: 360 },
      align: "center",
    });
    text.setOrigin(0.5);

    // Background sized to text
    const paddingX = 16;
    const paddingY = 8;
    const bgWidth = Math.max(260, text.width + paddingX * 2);
    const bgHeight = text.height + paddingY * 2;
    const bg = this.add.rectangle(0, 0, bgWidth, bgHeight, 0x0d1117, 0.96);
    bg.setStrokeStyle(1, style.border);

    container.add([bg, text]);

    // Animate in
    this.tweens.add({
      targets: container,
      alpha: 1,
      y: 80,
      duration: 300,
      ease: "Back.easeOut",
    });

    // Animate out after duration
    this.time.delayedCall(duration, () => {
      this.tweens.add({
        targets: container,
        alpha: 0,
        y: 40,
        duration: 300,
        ease: "Power2",
        onComplete: () => container.destroy(),
      });
    });
  }

  // ============ MESSAGE IN A BOTTLE SYSTEM ============

  /**
   * Spawn a floating bottle in the ocean
   */
  private spawnFloatingBottle(id: string, x: number, y: number): void {
    // Don't spawn bottles in sanctums
    if (this.isInSanctumInstance) return;

    // Don't spawn if already exists
    if (this.floatingBottles.has(id)) return;

    const container = this.add.container(x, y);
    container.setDepth(25);

    // Grid cell background (faded) - 1 tile, grid-aligned
    const gridBg = this.add.graphics();
    gridBg.fillStyle(0x4a7c8c, 0.3);
    gridBg.fillRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE);
    gridBg.lineStyle(2, 0x6b9dac, 0.5);
    gridBg.strokeRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE);
    container.add(gridBg);

    // Bottle sprite (centered)
    const bottleSprite = this.add.image(0, 0, "message_in_a_bottle");
    bottleSprite.setDisplaySize(TILE_SIZE * 0.8, TILE_SIZE * 0.8);
    container.add(bottleSprite);

    // Sparkle effect (grid-aligned 4px block)
    const sparkle = this.add.graphics();
    sparkle.fillStyle(0xffffff, 0.8);
    sparkle.fillRect(0, -4, 4, 4);
    sparkle.lineStyle(1, 0xffff99, 0.6);
    sparkle.strokeRect(0, -4, 4, 4);
    container.add(sparkle);

    // Floating animation
    this.tweens.add({
      targets: container,
      y: { from: y - 3, to: y + 3 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    // Sparkle twinkle
    this.tweens.add({
      targets: sparkle,
      alpha: { from: 0.3, to: 1 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    });

    // Make clickable - enlarged hitbox for easier clicking
    const hitArea = this.add.rectangle(
      0,
      0,
      DEBRIS_HITBOX_SIZE,
      DEBRIS_HITBOX_SIZE,
      0x000000,
      0,
    );
    hitArea.setInteractive();

    // Handle both left and right clicks
    hitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        // Right click for context menu
        this.game.events.emit("debris:contextmenu", {
          type: "debris",
          id,
          x: pointer.x,
          y: pointer.y,
          worldX: x,
          worldY: y,
        });
      } else {
        // Left click to collect
        if (this.localShip) {
          const dist = Phaser.Math.Distance.Between(
            this.localShip.x,
            this.localShip.y,
            x,
            y,
          );
          if (dist < TILE_SIZE * 5) {
            this.socket?.emit("bottle:collect", { id });
          } else {
            this.showFloatingMessage(
              "Sail closer to pick up the bottle.",
              "#aaaaaa",
            );
          }
        }
      }
    });

    // Visual feedback on hover
    hitArea.on("pointerover", () => {
      container.setScale(1.2);
    });
    hitArea.on("pointerout", () => {
      container.setScale(1.0);
    });

    container.add(hitArea);

    // Store ID on container for context menu
    (container as any).bottleId = id;

    this.floatingBottles.set(id, container);
  }

  /**
   * Remove a floating bottle
   */
  private removeFloatingBottle(id: string): void {
    const bottle = this.floatingBottles.get(id);
    if (bottle) {
      // Pop animation before destroy
      this.tweens.add({
        targets: bottle,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          bottle.destroy();
          this.floatingBottles.delete(id);
        },
      });
    }
  }

  // ============ WEATHER SYSTEM ============

  /**
   * Set the current weather with visual effects
   */
  private setWeather(
    weather: "clear" | "fog" | "storm" | "calm",
    effects: {
      visibility?: number;
      speedModifier?: number;
      fishingBonus?: number;
    },
  ): void {
    this.currentWeather = weather;

    // Clear existing weather effects
    if (this.weatherOverlay) {
      this.weatherOverlay.destroy();
      this.weatherOverlay = null;
    }

    // Clear existing rain renderer
    if (this.pixelRainRenderer) {
      this.pixelRainRenderer.destroy();
      this.pixelRainRenderer = null;
    }

    // Create weather overlay (if not clear weather)
    if (weather !== "clear") {
      this.weatherOverlay = this.add.graphics();
      this.weatherOverlay.setScrollFactor(0);
      this.weatherOverlay.setDepth(120);
      this.updateWeatherOverlaySize();
    }

    // DISABLED: Visibility limits were causing NPCs to disappear based on weather
    // let visibilityLimitTiles: number | null = null;

    switch (weather) {
      case "fog":
        this.stopWeatherTimer();
        // Fog notification
        this.showWeatherNotification(
          "FOG",
          "Visibility reduced. NPCs harder to spot.",
          "#aaaaaa",
        );
        break;

      case "storm":
        // Initialize pixel rain renderer for storms
        if (!this.pixelRainRenderer) {
          this.pixelRainRenderer = new PixelRainRenderer(this);
          this.pixelRainRenderer.setSpawnRate(0.4);
          this.pixelRainRenderer.setMaxDrops(200);
        }

        if (!this.weatherTimer) {
          this.weatherTimer = this.time.addEvent({
            delay: 120,
            loop: true,
            callback: () => this.updateWeatherOverlaySize(),
          });
        }
        this.showWeatherNotification(
          "STORM",
          "Dangerous waters! Movement slowed.",
          "#5dade2",
        );
        break;

      case "calm":
        this.stopWeatherTimer();
        this.showWeatherNotification(
          "CALM SEAS",
          "Perfect fishing conditions! +50% catch rate.",
          "#2ecc71",
        );
        break;

      case "clear":
      default:
        // No overlay for clear weather
        this.stopWeatherTimer();
        break;
    }

    // DISABLED: Never apply visibility limits - NPCs should always be visible
    // if (this.shipRenderer) {
    //   this.shipRenderer.setVisibilityLimit(visibilityLimitTiles);
    // }
  }

  /**
   * Update weather overlay size when camera/screen is resized
   */
  private updateWeatherOverlaySize(): void {
    if (!this.weatherOverlay) return;

    const { width, height } = this.cameras.main;

    // Clear and redraw the overlay
    this.weatherOverlay.clear();

    switch (this.currentWeather) {
      case "fog":
        // Dense fog overlay
        this.weatherOverlay.fillStyle(0x9ea7b3, 0.55);
        this.weatherOverlay.fillRect(0, 0, width, height);
        // Subtle banded haze
        this.weatherOverlay.fillStyle(0x7f8794, 0.08);
        for (let y = 0; y < height; y += 32) {
          this.weatherOverlay.fillRect(0, y, width, 12);
        }
        break;

      case "storm":
        this.drawStormOverlay(width, height);
        break;

      case "calm":
        // Slight golden tint for calm seas
        this.weatherOverlay.fillStyle(0xffd700, 0.05);
        this.weatherOverlay.fillRect(0, 0, width, height);
        break;

      case "clear":
      default:
        // No overlay for clear weather
        break;
    }
  }

  /**
   * Show weather change notification
   */
  private showWeatherNotification(
    title: string,
    description: string,
    color: string,
  ): void {
    const { width } = this.cameras.main;

    const container = this.add.container(width / 2, 60);
    container.setScrollFactor(0);
    container.setDepth(100);

    // Background
    const bg = this.add.rectangle(0, 0, 280, 50, 0x000000, 0.8);
    bg.setStrokeStyle(1, Phaser.Display.Color.HexStringToColor(color).color);
    container.add(bg);

    // Title
    const titleText = this.add.text(0, -10, `[${title}]`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "12px",
      color: color,
    });
    titleText.setOrigin(0.5);
    container.add(titleText);

    // Description
    const descText = this.add.text(0, 10, description, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#cccccc",
    });
    descText.setOrigin(0.5);
    container.add(descText);

    // Slide in from top
    container.setY(-50);
    this.tweens.add({
      targets: container,
      y: 60,
      duration: 500,
      ease: "Back.easeOut",
    });

    // Fade out after delay
    this.tweens.add({
      targets: container,
      alpha: 0,
      y: 40,
      duration: 500,
      delay: 5000,
      onComplete: () => container.destroy(),
    });
  }

  // ============ RIFT STORM SYSTEM ============

  /**
   * Draw a jagged rotating spiral for rift storm VFX
   */
  private drawRiftStormSpiral(
    graphics: Phaser.GameObjects.Graphics,
    radius: number,
    colorNum: number,
    rotation: number,
    wobbleTime: number,
  ): void {
    graphics.clear();

    // Draw multiple spiral arms
    const numArms = 3;
    for (let arm = 0; arm < numArms; arm++) {
      const armOffset = (arm / numArms) * Math.PI * 2;
      const alpha = 0.5 + Math.sin(wobbleTime * 0.002 + arm) * 0.2;
      graphics.lineStyle(3, colorNum, alpha);
      graphics.beginPath();

      // Jagged spiral with few joints
      const joints = 12;
      let firstPoint = true;

      for (let i = 0; i < joints; i++) {
        const t = i / joints;
        const angle = rotation + armOffset + t * Math.PI * 3; // 1.5 rotations
        const dist = t * radius * 0.85;

        // Add wobble/jitter to each joint
        const wobbleX = Math.sin(wobbleTime * 0.003 + i * 1.5 + arm) * 8;
        const wobbleY = Math.cos(wobbleTime * 0.0025 + i * 1.3 + arm) * 8;
        const jitterX = (((i * 17 + arm * 31) % 11) - 5) * 2;
        const jitterY = (((i * 23 + arm * 41) % 11) - 5) * 2;

        const px = Math.cos(angle) * dist + wobbleX + jitterX;
        const py = Math.sin(angle) * dist + wobbleY + jitterY;

        if (firstPoint) {
          graphics.moveTo(px, py);
          firstPoint = false;
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.strokePath();
    }

    // Inner glow spiral
    graphics.lineStyle(6, colorNum, 0.15);
    graphics.beginPath();
    let first = true;
    for (let i = 0; i < 10; i++) {
      const t = i / 10;
      const angle = rotation + t * Math.PI * 2.5;
      const dist = t * radius * 0.6;
      const wobble = Math.sin(wobbleTime * 0.002 + i) * 5;
      const px = Math.cos(angle) * dist + wobble;
      const py = Math.sin(angle) * dist + wobble;
      if (first) {
        graphics.moveTo(px, py);
        first = false;
      } else {
        graphics.lineTo(px, py);
      }
    }
    graphics.strokePath();
  }

  /**
   * Draw dense cloud particles for rift storm
   */
  private drawRiftStormClouds(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    colorNum: number,
    time: number,
  ): void {
    graphics.clear();

    // Draw many small cloud blobs that drift
    const numClouds = 40;
    const seed = x * 1000 + y;

    for (let i = 0; i < numClouds; i++) {
      const angle = (i / numClouds) * Math.PI * 2 + time * 0.0003;
      const dist = (0.3 + ((seed + i * 137) % 100) / 140) * radius;
      const cloudX = x + Math.cos(angle) * dist;
      const cloudY = y + Math.sin(angle) * dist;
      const cloudSize = 8 + ((seed + i * 53) % 12);
      const alpha = 0.15 + ((seed + i * 31) % 10) / 50;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(
        cloudX - cloudSize / 2,
        cloudY - cloudSize / 2,
        cloudSize,
        cloudSize,
      );
    }

    // Inner dense clouds
    for (let i = 0; i < 20; i++) {
      const angle = (i / 20) * Math.PI * 2 - time * 0.0005;
      const dist = (0.1 + ((seed + i * 89) % 100) / 500) * radius;
      const cloudX = x + Math.cos(angle) * dist;
      const cloudY = y + Math.sin(angle) * dist;
      const cloudSize = 12 + ((seed + i * 41) % 16);
      const alpha = 0.2 + ((seed + i * 17) % 10) / 40;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(
        cloudX - cloudSize / 2,
        cloudY - cloudSize / 2,
        cloudSize,
        cloudSize,
      );
    }
  }

  /**
   * Draw lightning fork for rift storm
   */
  private drawRiftStormLightning(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
  ): void {
    graphics.clear();

    // Draw 2-3 lightning forks
    const numForks = 2 + Math.floor(Math.random() * 2);

    for (let f = 0; f < numForks; f++) {
      const startAngle = Math.random() * Math.PI * 2;
      const startDist = radius * 0.2;
      let lx = x + Math.cos(startAngle) * startDist;
      let ly = y + Math.sin(startAngle) * startDist;

      const endAngle = startAngle + (Math.random() - 0.5) * Math.PI;
      const endDist = radius * (0.6 + Math.random() * 0.3);
      const endX = x + Math.cos(endAngle) * endDist;
      const endY = y + Math.sin(endAngle) * endDist;

      graphics.lineStyle(3, 0xffffff, 0.9);
      graphics.beginPath();
      graphics.moveTo(lx, ly);

      // Jagged path to end
      const segments = 4 + Math.floor(Math.random() * 3);
      for (let s = 1; s <= segments; s++) {
        const t = s / segments;
        const targetX = lx + (endX - lx) * t;
        const targetY = ly + (endY - ly) * t;
        const jitterX = (Math.random() - 0.5) * 20;
        const jitterY = (Math.random() - 0.5) * 20;
        lx = targetX + (s < segments ? jitterX : 0);
        ly = targetY + (s < segments ? jitterY : 0);
        graphics.lineTo(lx, ly);
      }
      graphics.strokePath();

      // Glow effect
      graphics.lineStyle(6, 0x9b59b6, 0.3);
      graphics.beginPath();
      graphics.moveTo(
        x + Math.cos(startAngle) * startDist,
        y + Math.sin(startAngle) * startDist,
      );
      graphics.lineTo(endX, endY);
      graphics.strokePath();
    }
  }

  /**
   * Draw wavy portal overlay (Minecraft nether portal style)
   */
  private drawRiftPortalOverlay(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    colorNum: number,
    time: number,
  ): void {
    graphics.clear();

    // Wavy vertical stripes like nether portal
    const stripeCount = Math.floor(radius / 8);
    const stripeWidth = (radius * 2) / stripeCount;

    for (let i = 0; i < stripeCount; i++) {
      const stripeX = x - radius + i * stripeWidth;
      const waveOffset = Math.sin(time * 0.003 + i * 0.5) * 8;
      const waveOffset2 = Math.sin(time * 0.002 + i * 0.3) * 4;
      const alpha = 0.08 + Math.sin(time * 0.004 + i * 0.7) * 0.04;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(
        stripeX + waveOffset,
        y - radius + waveOffset2,
        stripeWidth * 0.8,
        radius * 2,
      );
    }

    // Horizontal wave bands
    const bandCount = Math.floor(radius / 12);
    for (let i = 0; i < bandCount; i++) {
      const bandY = y - radius + i * ((radius * 2) / bandCount);
      const waveOffset = Math.sin(time * 0.0025 + i * 0.4) * 6;
      const alpha = 0.06 + Math.sin(time * 0.003 + i * 0.5) * 0.03;

      graphics.fillStyle(colorNum, alpha);
      graphics.fillRect(
        x - radius + waveOffset,
        bandY,
        radius * 2,
        ((radius * 2) / bandCount) * 0.6,
      );
    }
  }

  /**
   * Draw jagged wobbling outer rings for rift storm
   */
  private drawRiftStormRings(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    colorNum: number,
    time: number,
  ): void {
    graphics.clear();

    const numRings = 5;
    for (let ring = 0; ring < numRings; ring++) {
      const ringRadius = radius * (1 - ring * 0.15);
      const alpha = 0.15 + ring * 0.05;
      const jointCount = 20 + ring * 4; // More joints for outer rings

      // Draw jagged ring with wobble
      graphics.lineStyle(2 + ring * 0.5, colorNum, alpha + 0.2);
      graphics.beginPath();

      for (let i = 0; i <= jointCount; i++) {
        const t = i / jointCount;
        const baseAngle = t * Math.PI * 2;

        // Wobble effect
        const wobbleX =
          Math.sin(time * 0.002 + i * 0.5 + ring) * (4 + ring * 2);
        const wobbleY =
          Math.cos(time * 0.0018 + i * 0.4 + ring) * (4 + ring * 2);

        // Jitter for jagged look
        const jitterX = (((i * 17 + ring * 31) % 7) - 3) * 3;
        const jitterY = (((i * 23 + ring * 41) % 7) - 3) * 3;

        const px = x + Math.cos(baseAngle) * ringRadius + wobbleX + jitterX;
        const py = y + Math.sin(baseAngle) * ringRadius + wobbleY + jitterY;

        if (i === 0) {
          graphics.moveTo(px, py);
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.strokePath();

      // Fill with low alpha
      graphics.fillStyle(colorNum, alpha * 0.4);
      graphics.beginPath();
      for (let i = 0; i <= jointCount; i++) {
        const t = i / jointCount;
        const baseAngle = t * Math.PI * 2;
        const wobbleX =
          Math.sin(time * 0.002 + i * 0.5 + ring) * (4 + ring * 2);
        const wobbleY =
          Math.cos(time * 0.0018 + i * 0.4 + ring) * (4 + ring * 2);
        const jitterX = (((i * 17 + ring * 31) % 7) - 3) * 3;
        const jitterY = (((i * 23 + ring * 41) % 7) - 3) * 3;
        const px = x + Math.cos(baseAngle) * ringRadius + wobbleX + jitterX;
        const py = y + Math.sin(baseAngle) * ringRadius + wobbleY + jitterY;
        if (i === 0) {
          graphics.moveTo(px, py);
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.fillPath();
    }
  }

  /**
   * Draw energy particles swirling in the storm
   */
  private drawRiftEnergyParticles(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    colorNum: number,
    time: number,
  ): void {
    graphics.clear();

    // Fast-moving energy sparks
    const numSparks = 30;
    const seed = Math.floor(x * 100 + y);

    for (let i = 0; i < numSparks; i++) {
      const sparkSeed = seed + i * 137;
      const baseAngle = ((sparkSeed % 360) * Math.PI) / 180;
      const speed = 0.001 + (sparkSeed % 100) / 50000;
      const angle = baseAngle + time * speed;
      const dist = (0.2 + (sparkSeed % 100) / 150) * radius;

      const sparkX = x + Math.cos(angle) * dist;
      const sparkY = y + Math.sin(angle) * dist;

      // Pulsing alpha
      const alpha = 0.4 + Math.sin(time * 0.005 + i) * 0.3;
      const size = 3 + (sparkSeed % 5);

      graphics.fillStyle(0xffffff, alpha * 0.8);
      graphics.fillRect(sparkX - size / 2, sparkY - size / 2, size, size);

      // Purple glow around spark
      graphics.fillStyle(colorNum, alpha * 0.4);
      graphics.fillRect(sparkX - size, sparkY - size, size * 2, size * 2);
    }

    // Larger energy orbs moving slower
    const numOrbs = 8;
    for (let i = 0; i < numOrbs; i++) {
      const orbSeed = seed + i * 89;
      const baseAngle = ((orbSeed % 360) * Math.PI) / 180;
      const speed = 0.0003 + (orbSeed % 50) / 100000;
      const angle = baseAngle + time * speed;
      const dist = (0.4 + (orbSeed % 100) / 250) * radius;

      const orbX = x + Math.cos(angle) * dist;
      const orbY = y + Math.sin(angle) * dist;

      const alpha = 0.3 + Math.sin(time * 0.003 + i * 2) * 0.2;
      const size = 8 + (orbSeed % 8);

      // Core
      graphics.fillStyle(0xffffff, alpha);
      graphics.fillRect(orbX - size / 4, orbY - size / 4, size / 2, size / 2);

      // Glow
      graphics.fillStyle(colorNum, alpha * 0.5);
      graphics.fillRect(orbX - size / 2, orbY - size / 2, size, size);
    }
  }

  /**
   * Spawn a rift storm visual at the given position
   * NOTE: Rift storms only render in the CORE WORLD, not in rift instances or sanctums
   */
  private spawnRiftStorm(data: {
    id: string;
    x: number;
    y: number;
    radius: number;
    tier: string;
    stage?: "active" | "dissipating";
    expiresAt: number;
    dissipatesAt?: number | null;
    color: string;
  }): void {
    // Don't render rift storms when inside a rift instance or sanctum
    if (this.isInRiftInstance) {
      console.log(`⚡ [RIFT STORM] Skipping render - inside rift instance`);
      return;
    }

    if (this.isInSanctumInstance) {
      console.log(`⚡ [RIFT STORM] Skipping render - inside sanctum`);
      return;
    }

    // Remove existing if present
    this.removeRiftStorm(data.id);

    // Parse color - use purple for all storms for consistency
    const purpleColor = 0x9b59b6;
    const colorNum = purpleColor;

    // Animated jagged outer rings
    const graphics = this.add.graphics();
    graphics.setDepth(4);
    let ringTime = 0;
    this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        ringTime = tween.getValue() ?? 0;
        this.drawRiftStormRings(
          graphics,
          data.x,
          data.y,
          data.radius,
          colorNum,
          ringTime,
        );
      },
    });

    // Portal overlay (wavy Minecraft-style effect)
    const portalOverlay = this.add.graphics();
    portalOverlay.setDepth(5);
    let portalTime = 0;
    const portalTween = this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        portalTime = tween.getValue() ?? 0;
        this.drawRiftPortalOverlay(
          portalOverlay,
          data.x,
          data.y,
          data.radius,
          colorNum,
          portalTime,
        );
      },
    });

    // Dense clouds layer
    const clouds = this.add.graphics();
    clouds.setDepth(6);
    let cloudTime = 0;
    this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        cloudTime = tween.getValue() ?? 0;
        this.drawRiftStormClouds(
          clouds,
          data.x,
          data.y,
          data.radius,
          colorNum,
          cloudTime,
        );
      },
    });

    // Energy particles layer
    const particles = this.add.graphics();
    particles.setDepth(9);
    let particleTime = 0;
    this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        particleTime = tween.getValue() ?? 0;
        this.drawRiftEnergyParticles(
          particles,
          data.x,
          data.y,
          data.radius,
          colorNum,
          particleTime,
        );
      },
    });

    // Rotating spiral
    const spiralGraphics = this.add.graphics();
    spiralGraphics.setDepth(7);
    spiralGraphics.setPosition(data.x, data.y);
    let spiralRotation = 0;
    let spiralWobbleTime = 0;
    const spiralTween = this.tweens.addCounter({
      from: 0,
      to: 100000,
      duration: 100000,
      repeat: -1,
      onUpdate: (tween) => {
        spiralWobbleTime = tween.getValue() ?? 0;
        spiralRotation = (spiralWobbleTime / 100000) * Math.PI * 2 * 8; // 8 full rotations over duration
        this.drawRiftStormSpiral(
          spiralGraphics,
          data.radius * 0.7,
          colorNum,
          spiralRotation,
          spiralWobbleTime,
        );
      },
    });

    // Lightning layer
    const lightning = this.add.graphics();
    lightning.setDepth(8);

    // Schedule lightning flashes
    const scheduleLightning = (): Phaser.Time.TimerEvent => {
      return this.time.addEvent({
        delay: Phaser.Math.Between(800, 2500),
        loop: false,
        callback: () => {
          this.drawRiftStormLightning(lightning, data.x, data.y, data.radius);
          // Flash then fade
          this.tweens.add({
            targets: lightning,
            alpha: { from: 1, to: 0 },
            duration: 150,
            onComplete: () => {
              lightning.clear();
              const storm = this.riftStorms.get(data.id);
              if (storm) {
                storm.lightningTimer = scheduleLightning();
              }
            },
          });
        },
      });
    };
    const lightningTimer = scheduleLightning();

    // Label
    const tierLabel = data.tier.toUpperCase();
    const label = this.add.text(
      data.x,
      data.y - data.radius - 20,
      `[RIFT STORM - ${tierLabel}]`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "11px",
        color: "#9b59b6",
        backgroundColor: "#000000cc",
        padding: { x: 6, y: 3 },
      },
    );
    label.setOrigin(0.5);
    label.setDepth(9);

    // Store reference
    this.riftStorms.set(data.id, {
      id: data.id,
      x: data.x,
      y: data.y,
      radius: data.radius,
      tier: data.tier,
      stage: data.stage || "active",
      expiresAt: data.expiresAt,
      dissipatesAt: data.dissipatesAt ?? null,
      color: data.color,
      graphics,
      label,
      clouds,
      lightning,
      portalOverlay,
      spiralGraphics,
      particles,
      lightningTimer,
      portalTween,
      spiralTween,
    });

    // Emit to React for world map marker
    this.events.emit("riftStorm:spawned", data);
  }

  /**
   * Remove a rift storm visual
   */
  private removeRiftStorm(id: string): void {
    const storm = this.riftStorms.get(id);
    if (storm) {
      storm.dissipationTween?.destroy();
      storm.graphics.destroy();
      storm.label.destroy();
      storm.clouds.destroy();
      storm.lightning.destroy();
      storm.portalOverlay.destroy();
      storm.spiralGraphics.destroy();
      storm.particles.destroy();
      storm.lightningTimer.destroy();
      storm.portalTween.destroy();
      storm.spiralTween.destroy();
      this.riftStorms.delete(id);
    }
  }

  private setRiftStormDissipating(id: string, endsAt: number): void {
    const storm = this.riftStorms.get(id);
    if (!storm) return;

    storm.stage = "dissipating";
    storm.dissipatesAt = endsAt;

    const now: number = Date.now();
    const remainingMs: number = Math.max(0, endsAt - now);

    // Stop lightning flashes during dissipation so it reads like a fade-out
    try {
      storm.lightningTimer.destroy();
    } catch {
      // ignore
    }

    // Fade all storm layers out smoothly; despawn event will still remove it at the end.
    storm.dissipationTween?.destroy();
    storm.dissipationTween = this.tweens.add({
      targets: [
        storm.graphics,
        storm.clouds,
        storm.lightning,
        storm.portalOverlay,
        storm.spiralGraphics,
        storm.particles,
        storm.label,
      ],
      alpha: { from: 1, to: 0 },
      duration: remainingMs,
      ease: "Sine.easeInOut",
    });
  }

  /**
   * Get the local player's ship
   */
  public getLocalShip(): ShipSprite | null {
    return this.localShip;
  }

  /**
   * Get all active rift storms (for world map)
   */
  public getRiftStorms(): Array<{
    id: string;
    x: number;
    y: number;
    radius: number;
    tier: string;
    color: string;
  }> {
    const storms: Array<{
      id: string;
      x: number;
      y: number;
      radius: number;
      tier: string;
      color: string;
    }> = [];
    const stormValues = Array.from(this.riftStorms.values());
    for (let i = 0; i < stormValues.length; i++) {
      const storm = stormValues[i];
      storms.push({
        id: storm.id,
        x: storm.x,
        y: storm.y,
        radius: storm.radius,
        tier: storm.tier,
        color: storm.color,
      });
    }
    return storms;
  }

  // ============ SHIPWRECK SYSTEM ============

  /**
   * Spawn a shipwreck at the given position
   */
  private spawnShipwreck(
    id: string,
    x: number,
    y: number,
    size: string,
    rotation: number,
    lootQuality: string,
  ): void {
    if (this.shipwrecks.has(id)) return;

    const container = this.add.container(x, y);
    container.setDepth(20);

    // Size dimensions - grid-aligned to tile multiples (no half-tiles)
    const sizes: Record<string, { w: number; h: number }> = {
      "1x2": { w: TILE_SIZE, h: TILE_SIZE * 2 },
      "2x1": { w: TILE_SIZE * 2, h: TILE_SIZE },
      "2x2": { w: TILE_SIZE * 2, h: TILE_SIZE * 2 },
    };
    const dim = sizes[size] || sizes["2x2"];

    // Quality colors
    const qualityColors: Record<string, number> = {
      common: 0x8b4513,
      uncommon: 0x2e8b57,
      rare: 0x4169e1,
      legendary: 0xffd700,
    };
    const color = qualityColors[lootQuality] || 0x8b4513;

    // Grid-aligned wreck: square frame + square/rect interior (no circles, no organic silhouette)
    const hull = this.add.graphics();
    const baseWood = 0x4a3728;
    const darkWood = 0x352417;
    const lightWood = 0x5a4331;

    const halfW = dim.w / 2;
    const halfH = dim.h / 2;

    // Surrounding square frame (grid-aligned)
    const framePadding: number = TILE_SIZE;
    const frameW: number = dim.w + framePadding * 2;
    const frameH: number = dim.h + framePadding * 2;
    hull.fillStyle(0x0b1220, 0.55);
    hull.fillRect(-frameW / 2, -frameH / 2, frameW, frameH);
    hull.lineStyle(2, color, 1);
    hull.strokeRect(-frameW / 2, -frameH / 2, frameW, frameH);

    // Main wreck body (rect, tile-aligned)
    hull.fillStyle(baseWood, 1);
    hull.fillRect(-halfW, -halfH, dim.w, dim.h);
    hull.lineStyle(2, darkWood, 0.85);
    hull.strokeRect(-halfW, -halfH, dim.w, dim.h);

    // Inner shadow to add depth
    hull.lineStyle(2, darkWood, 0.55);
    hull.strokeRect(-halfW + 4, -halfH + 4, dim.w - 8, dim.h - 8);

    // Plank lines (grid-aligned spacing)
    hull.lineStyle(2, darkWood, 0.55);
    const plankCount = Math.max(2, Math.floor(dim.h / TILE_SIZE));
    for (let i = 1; i <= plankCount; i++) {
      const ly = -halfH + 8 + (i * (dim.h - 16)) / (plankCount + 1);
      hull.lineBetween(-halfW + 8, ly, halfW - 8, ly);
    }

    // Broken ribs (short strokes, keep inside the box)
    hull.lineStyle(3, darkWood, 0.8);
    hull.lineBetween(-halfW + 6, -halfH + 6, halfW - 6, -halfH + 6);
    hull.lineBetween(-halfW + 6, 0, halfW - 10, 0);

    // Highlight chips (grid-aligned 4px blocks)
    hull.fillStyle(lightWood, 0.9);
    hull.fillRect(-halfW + 8, -halfH + 8, 4, 4);
    hull.fillRect(halfW - 12, -2, 4, 4);
    hull.fillRect(-2, halfH - 12, 4, 4);
    container.add(hull);

    // Broken mast (no sail fragment bar)
    const mast = this.add.graphics();
    mast.lineStyle(4, 0x3d2817, 1);
    mast.beginPath();
    mast.moveTo(-4, -halfH + 4);
    mast.lineTo(halfW - 8, -halfH - 20);
    mast.strokePath();
    container.add(mast);

    // Scattered debris blocks around the wreck (grid-aligned 4px blocks)
    const debrisGraphics = this.add.graphics();
    debrisGraphics.fillStyle(darkWood, 0.85);
    debrisGraphics.fillRect(-halfW - 12, -halfH + 8, 4, 4);
    debrisGraphics.fillRect(halfW + 8, -halfH + 12, 4, 4);
    debrisGraphics.fillRect(-halfW - 8, halfH - 8, 4, 4);
    debrisGraphics.fillRect(halfW + 4, halfH - 4, 4, 4);
    debrisGraphics.fillStyle(lightWood, 0.65);
    debrisGraphics.fillRect(-halfW - 4, -halfH - 4, 4, 4);
    debrisGraphics.fillRect(halfW + 2, halfH + 4, 4, 4);
    container.add(debrisGraphics);

    // Keep grid-aligned: no rotation, no circular glow
    container.setRotation(0);

    // Subtle bob animation
    this.tweens.add({
      targets: container,
      y: { from: y - 2, to: y + 2 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    // Make clickable - use enlarged hitbox for easier clicking
    const hitArea = this.add.rectangle(
      0,
      0,
      (dim.w + 16) * HITBOX_MULTIPLIER,
      (dim.h + 16) * HITBOX_MULTIPLIER,
      0x000000,
      0,
    );
    hitArea.setInteractive();

    // Left click to salvage
    hitArea.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        if (!pointer.leftButtonDown()) return;
        event.stopPropagation(); // Prevent global click handler from moving player
        if (this.localShip) {
          const dist = Phaser.Math.Distance.Between(
            this.localShip.x,
            this.localShip.y,
            x,
            y,
          );
          if (dist < TILE_SIZE * 4) {
            // In range - start salvaging
            this.socket?.emit("shipwreck:startSalvage", { wreckId: id });
          } else {
            // Out of range - move toward it
            this.moveToPosition(x, y);
          }
        }
      },
    );

    // Right click for context menu
    hitArea.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        if (!pointer.rightButtonDown()) return;
        event.stopPropagation();
        this.game.events.emit("shipwreck:contextmenu", {
          wreckId: id,
          worldX: x,
          worldY: y,
          screenX: pointer.x,
          screenY: pointer.y,
          lootQuality,
        });
      },
    );

    container.add(hitArea);

    this.shipwrecks.set(id, container);
  }

  /**
   * Mark a shipwreck as depleted (grayed out)
   */
  private markShipwreckDepleted(wreckId: string): void {
    const wreck = this.shipwrecks.get(wreckId);
    if (wreck) {
      wreck.setAlpha(0.4);
      // Remove interactivity
      wreck.list.forEach((child) => {
        if (child instanceof Phaser.GameObjects.Rectangle) {
          child.disableInteractive();
        }
      });
    }
  }

  /**
   * Remove a shipwreck
   */
  private removeShipwreck(wreckId: string): void {
    const wreck = this.shipwrecks.get(wreckId);
    if (wreck) {
      // Prevent any further interactions immediately
      wreck.list.forEach((child) => {
        if (child instanceof Phaser.GameObjects.Rectangle) {
          child.disableInteractive();
        }
      });
      this.shipwrecks.delete(wreckId);

      this.tweens.add({
        targets: wreck,
        alpha: 0,
        y: wreck.y + 20,
        duration: 500,
        onComplete: () => {
          wreck.destroy();
        },
      });
    }
  }

  /**
   * Show salvage progress UI
   */
  private showSalvageUI(quality: string): void {
    this.hideSalvageUI();

    const { width } = this.cameras.main;

    this.salvageUI = this.add.container(width / 2, 80);
    this.salvageUI.setScrollFactor(0);
    this.salvageUI.setDepth(100);

    const containerWidth = 220;
    const containerHeight = 24;

    // ACTION LABEL CONTAINER (top) - wreck/inventory style
    const actionBg = this.add.rectangle(
      0,
      -26,
      containerWidth,
      containerHeight,
      0x161b22,
      1,
    );
    actionBg.setStrokeStyle(1, 0x30363d);
    this.salvageUI.add(actionBg);

    const qualityCapitalized =
      quality.charAt(0).toUpperCase() + quality.slice(1).toLowerCase();
    const actionLabel = this.add.text(
      0,
      -26,
      `SALVAGING ${qualityCapitalized.toUpperCase()} WRECK`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#e6edf3",
      },
    );
    actionLabel.setOrigin(0.5);
    this.salvageUI.add(actionLabel);

    // PROGRESS BAR CONTAINER (bottom)
    const barBg = this.add.rectangle(
      0,
      0,
      containerWidth,
      containerHeight,
      0x0d1117,
      0.96,
    );
    barBg.setStrokeStyle(1, 0x30363d);
    this.salvageUI.add(barBg);

    // Progress bar fill
    const barFill = this.add.rectangle(
      -containerWidth / 2 + 2,
      0,
      0,
      containerHeight - 6,
      0x2ea043,
    );
    barFill.setOrigin(0, 0.5);
    barFill.setName("progressFill");
    this.salvageUI.add(barFill);

    // Progress text
    const progressText = this.add.text(0, 0, "0%", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#ffffff",
    });
    progressText.setOrigin(0.5);
    progressText.setName("progressText");
    this.salvageUI.add(progressText);
  }

  /**
   * Update salvage progress UI
   */
  private updateSalvageUI(progress: number): void {
    if (!this.salvageUI) return;

    const fill = this.salvageUI.getByName(
      "progressFill",
    ) as Phaser.GameObjects.Rectangle;
    const text = this.salvageUI.getByName(
      "progressText",
    ) as Phaser.GameObjects.Text;

    if (fill) {
      fill.width = (progress / 100) * (200 - 4);
    }
    if (text) {
      text.setText(`${Math.round(progress)}%`);
    }
  }

  /**
   * Hide salvage UI
   */
  private hideSalvageUI(): void {
    if (this.salvageUI) {
      this.salvageUI.destroy();
      this.salvageUI = null;
    }
  }

  /**
   * Show salvage reward notification
   */
  private showSalvageReward(
    pieces: number,
    loot: Array<{ itemId: string; name: string; quantity: number }>,
    quality: string,
  ): void {
    const { width, height } = this.cameras.main;

    const safePieces: number =
      typeof pieces === "number" && !Number.isNaN(pieces) ? pieces : 0;
    const safeLoot = Array.isArray(loot)
      ? loot.filter((i) => (i?.quantity || 0) > 0)
      : [];

    const hasPieces: boolean = safePieces > 0;
    const totalRows = (hasPieces ? 1 : 0) + safeLoot.length;
    if (totalRows === 0) return;

    const qualityStroke: Record<string, number> = {
      common: 0x9e9e9e,
      uncommon: 0x2ecc71,
      rare: 0x58a6ff,
      legendary: 0xf1c40f,
    };
    const strokeColor: number = qualityStroke[quality] ?? 0x9e9e9e;

    const rowHeight = 36;
    const slotSize = 28;
    const panelWidth = 240;
    const panelHeight = 40 + totalRows * rowHeight;

    const container = this.add.container(width / 2, height - 150);
    container.setScrollFactor(0);
    container.setDepth(200);

    // Background - matching wreck/inventory style
    const bg = this.add.rectangle(
      0,
      0,
      panelWidth,
      panelHeight,
      0x0d1117,
      0.96,
    );
    bg.setStrokeStyle(2, strokeColor);
    container.add(bg);

    // Title bar
    const titleBar = this.add.rectangle(
      0,
      -panelHeight / 2 + 14,
      panelWidth - 4,
      24,
      0x161b22,
      1,
    );
    titleBar.setStrokeStyle(1, 0x30363d);
    container.add(titleBar);

    const title = this.add.text(
      0,
      -panelHeight / 2 + 14,
      `${quality.toUpperCase()} SALVAGE`,
      {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#e6edf3",
      },
    );
    title.setOrigin(0.5);
    container.add(title);

    const startY = -panelHeight / 2 + 40;
    const leftX = -panelWidth / 2 + 16;

    let rowIndex = 0;

    if (hasPieces) {
      const y = startY + rowIndex * rowHeight;
      rowIndex++;

      const rowBg = this.add.rectangle(
        0,
        y,
        panelWidth - 12,
        rowHeight - 4,
        0x161b22,
        0.35,
      );
      container.add(rowBg);

      const slotBg = this.add.rectangle(
        leftX + slotSize / 2,
        y,
        slotSize,
        slotSize,
        0x161b22,
        1,
      );
      slotBg.setStrokeStyle(1, 0x30363d);
      container.add(slotBg);

      const pieceIcon = this.add.text(leftX + slotSize / 2, y, "₱", {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "14px",
        fontStyle: "bold",
        color: "#f1c40f",
      });
      pieceIcon.setOrigin(0.5);
      container.add(pieceIcon);

      const qtyText = this.add.text(
        leftX + slotSize - 2,
        y + slotSize / 2 - 4,
        `${safePieces}`,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "9px",
          color: "#f1c40f",
        },
      );
      qtyText.setOrigin(1, 1);
      qtyText.setStroke("#000000", 2);
      container.add(qtyText);

      const nameText = this.add.text(leftX + slotSize + 10, y, "Pieces", {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#c9d1d9",
      });
      nameText.setOrigin(0, 0.5);
      container.add(nameText);
    }

    safeLoot.forEach((item, i) => {
      const y = startY + (rowIndex + i) * rowHeight;

      const rowBg = this.add.rectangle(
        0,
        y,
        panelWidth - 12,
        rowHeight - 4,
        0x161b22,
        (rowIndex + i) % 2 === 0 ? 0.4 : 0.2,
      );
      container.add(rowBg);

      const slotBg = this.add.rectangle(
        leftX + slotSize / 2,
        y,
        slotSize,
        slotSize,
        0x161b22,
        1,
      );
      slotBg.setStrokeStyle(1, 0x30363d);
      container.add(slotBg);

      this.drawItemIcon(
        container,
        item.itemId,
        leftX + slotSize / 2,
        y,
        slotSize - 4,
      );

      const qtyText = this.add.text(
        leftX + slotSize - 2,
        y + slotSize / 2 - 4,
        `${item.quantity}`,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "9px",
          color: "#ffffff",
        },
      );
      qtyText.setOrigin(1, 1);
      qtyText.setStroke("#000000", 2);
      container.add(qtyText);

      const nameText = this.add.text(leftX + slotSize + 10, y, item.name, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#c9d1d9",
      });
      nameText.setOrigin(0, 0.5);
      container.add(nameText);
    });

    container.setScale(0);
    this.tweens.add({
      targets: container,
      scaleX: 1,
      scaleY: 1,
      duration: 220,
      ease: "Back.easeOut",
    });

    this.tweens.add({
      targets: container,
      alpha: 0,
      y: height - 190,
      duration: 400,
      delay: 2800,
      onComplete: () => container.destroy(),
    });

    playSound(SFX_KEYS.FISHING_CATCH);
  }

  // ============ RESOURCE NODE SYSTEM ============

  // Animation phase for resource nodes (like fishing spots)
  private resourceNodeAnimPhase: number = 0;

  /**
   * Spawn a resource node in the world (sprite-based rendering)
   */
  private spawnResourceNode(node: {
    id: string;
    type: string;
    name: string;
    x: number;
    y: number;
    tileX?: number;
    tileY?: number;
    harvestsRemaining: number;
    maxHarvests: number;
    harvestTime: number;
    requiredLevel?: number;
    requiredSkill?: string;
  }): void {
    if (this.resourceNodes.has(node.id)) return;

    // Calculate tile position from world position if not provided
    const tileX = node.tileX ?? Math.floor(node.x / TILE_SIZE);
    const tileY = node.tileY ?? Math.floor(node.y / TILE_SIZE);
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;

    // Create sprite-based node visual (use as the main container)
    if (!this.textures.exists(node.type)) {
      console.warn(`⚠️ Resource node texture missing at spawn: ${node.type}`);
    }
    const nodeContainer = ResourceNodeRenderer.createNodeSprite(
      this,
      node.type,
      tileX,
      tileY,
      false,
    );
    nodeContainer.setDepth(10); // Above islands (depth 1) and fishing spots (depth 5)
    nodeContainer.setPosition(worldX, worldY);

    // Create invisible hitbox for interaction - enlarged for easier clicking
    const hitArea = this.add.rectangle(
      0,
      0,
      RESOURCE_NODE_HITBOX_SIZE,
      RESOURCE_NODE_HITBOX_SIZE,
      0x000000,
      0,
    );
    hitArea.setInteractive();
    hitArea.setDepth(11);

    // Store node data on hitArea for reference
    (hitArea as any).nodeId = node.id;
    (hitArea as any).nodeType = node.type;
    (hitArea as any).nodeName = node.name;
    (hitArea as any).tileX = tileX;
    (hitArea as any).tileY = tileY;
    (hitArea as any).isDepleted = false;
    (hitArea as any).requiredLevel = node.requiredLevel || 1;
    (hitArea as any).requiredSkill = node.requiredSkill || "salvaging";

    hitArea.on("pointerover", (pointer: Phaser.Input.Pointer) => {
      // Emit event to React layer for tooltip rendering
      const skillName =
        (node.requiredSkill || "salvaging").charAt(0).toUpperCase() +
        (node.requiredSkill || "salvaging").slice(1);
      const levelReq = node.requiredLevel || 1;
      const requirement =
        levelReq > 1 ? `[Requires ${skillName} Lv.${levelReq}]` : undefined;

      // Convert canvas coordinates to window coordinates
      const canvas = this.game.canvas;
      const rect = canvas.getBoundingClientRect();
      const screenX = rect.left + pointer.x;
      const screenY = rect.top + pointer.y;

      this.events.emit("resource:tooltip:show", {
        name: node.name,
        requirement,
        x: screenX,
        y: screenY,
      });
    });

    hitArea.on("pointerout", () => {
      this.events.emit("resource:tooltip:hide");
    });

    // Helper function to attempt harvest
    const attemptHarvest = (): void => {
      if (this.localShip) {
        const dist = Phaser.Math.Distance.Between(
          this.localShip.x,
          this.localShip.y,
          worldX,
          worldY,
        );
        if (dist < TILE_SIZE * 5) {
          // 5 tiles range like fishing
          if (!(hitArea as any).isDepleted) {
            this.socket?.emit("resource:startHarvest", { nodeId: node.id });
          } else {
            this.showFloatingMessage("This resource is depleted.", "#aaaaaa");
          }
        } else {
          // Out of range - move toward it
          this.moveToPosition(worldX, worldY);
        }
      }
    };

    // Left-click to harvest immediately
    hitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Only handle left clicks - let right clicks pass through to islands
      if (pointer.leftButtonDown()) {
        if (
          pointer.event &&
          typeof pointer.event.stopPropagation === "function"
        ) {
          pointer.event.stopPropagation();
        }
        attemptHarvest();
      }
    });

    // Right-click to open resource node context menu (handled in React layer)
    hitArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!pointer.rightButtonDown()) return;

      // Stop propagation so island doesn't also get the click
      if (
        pointer.event &&
        typeof pointer.event.stopPropagation === "function"
      ) {
        pointer.event.stopPropagation();
      }
      if (pointer.event && typeof pointer.event.preventDefault === "function") {
        pointer.event.preventDefault();
      }

      this.events.emit("resource:contextmenu", {
        nodeId: node.id,
        nodeType: node.type,
        nodeName: node.name,
        worldX,
        worldY,
        screenX: pointer.x,
        screenY: pointer.y,
      });
    });

    // Add hit area to node container
    nodeContainer.add(hitArea);

    // Store metadata on container
    (nodeContainer as any).hitArea = hitArea;
    (nodeContainer as any).nodeId = node.id;
    (nodeContainer as any).nodeType = node.type;
    (nodeContainer as any).tileX = tileX;
    (nodeContainer as any).tileY = tileY;

    this.resourceNodes.set(node.id, nodeContainer);

    // Start animation timer if not already running
    if (!this.resourceNodeAnimTimer) {
      this.startResourceNodeAnimation();
    }
  }

  private resourceNodeAnimTimer: Phaser.Time.TimerEvent | null = null;

  /**
   * Harvest a resource node (public method for context menu)
   */
  public harvestResourceNode(nodeId: string): void {
    if (!this.localShip || !this.socket) return;

    const nodeContainer = this.resourceNodes.get(nodeId);
    if (!nodeContainer) return;

    const hitArea = (nodeContainer as any).hitArea;
    if (!hitArea) return;

    const tileX = (nodeContainer as any).tileX;
    const tileY = (nodeContainer as any).tileY;
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;

    // Check if player is in range (5 tiles like fishing)
    const dist = Phaser.Math.Distance.Between(
      this.localShip.x,
      this.localShip.y,
      worldX,
      worldY,
    );
    if (dist < TILE_SIZE * 5) {
      // In range - check if depleted
      if ((hitArea as any).isDepleted) {
        this.showFloatingMessage("This resource is depleted.", "#aaaaaa");
        return;
      }
      // Start harvesting
      this.socket.emit("resource:startHarvest", { nodeId });
    } else {
      // Out of range - move toward it
      this.moveToPosition(worldX, worldY);
    }
  }

  /**
   * Remove a resource node from the scene (fully despawn)
   */
  private removeResourceNode(nodeId: string): void {
    const nodeContainer = this.resourceNodes.get(nodeId);
    if (nodeContainer) {
      // Disable interactions immediately
      const hitArea = (nodeContainer as any).hitArea;
      if (hitArea && hitArea instanceof Phaser.GameObjects.Rectangle) {
        hitArea.disableInteractive();
      }

      // Hide tooltip if it's for this node
      this.events.emit("resource:tooltip:hide");

      // Remove from map immediately
      this.resourceNodes.delete(nodeId);

      // Fade out and destroy
      this.tweens.add({
        targets: nodeContainer,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          nodeContainer.destroy();
        },
      });
    }
  }

  /**
   * Start resource node animation (sprite-based, minimal updates needed)
   */
  private startResourceNodeAnimation(): void {
    this.resourceNodeAnimTimer = this.time.addEvent({
      delay: 600, // Update every 600ms
      callback: () => {
        this.resourceNodeAnimPhase = (this.resourceNodeAnimPhase + 1) % 4;
        // Sprite-based rendering doesn't need animation updates like graphics did
        // Sprites are static; only active state border changes if needed
      },
      loop: true,
    });
  }

  /**
   * Show harvest progress UI
   */
  private showHarvestUI(harvestTime: number): void {
    // Clear any existing UI/tween first
    this.hideHarvestUI();

    const { width, height } = this.cameras.main;
    this.harvestUI = this.add.container(width / 2, height - 120);
    this.harvestUI.setScrollFactor(0);
    this.harvestUI.setDepth(150);

    const nodeName = (() => {
      if (!this.currentHarvestNodeId) return "Resource";
      const nodeContainer = this.resourceNodes.get(this.currentHarvestNodeId);
      const hitArea = nodeContainer
        ? ((nodeContainer as any).hitArea as any)
        : null;
      if (
        hitArea &&
        typeof hitArea.nodeName === "string" &&
        hitArea.nodeName.length > 0
      ) {
        return String(hitArea.nodeName);
      }
      if (
        hitArea &&
        typeof hitArea.nodeType === "string" &&
        hitArea.nodeType.length > 0
      ) {
        return String(hitArea.nodeType).replace(/_/g, " ");
      }
      return "Resource";
    })();

    // Background - increased height to fit time text
    const bg = this.add.rectangle(0, 0, 240, 80, 0x0d1117, 0.95);
    bg.setStrokeStyle(2, 0x2ecc71);
    this.harvestUI.add(bg);

    const innerBorder = this.add.rectangle(0, 0, 234, 74, 0x1a1a2e, 0);
    innerBorder.setStrokeStyle(1, 0x30363d);
    this.harvestUI.add(innerBorder);

    // Title
    const title = this.add.text(0, -26, "HARVESTING", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "11px",
      color: "#2ecc71",
    });
    title.setOrigin(0.5);
    this.harvestUI.add(title);

    const subtitle = this.add.text(0, -10, nodeName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#c9d1d9",
    });
    subtitle.setOrigin(0.5);
    this.harvestUI.add(subtitle);

    // Progress bar background
    const barBg = this.add.rectangle(0, 10, 188, 12, 0x161b22);
    barBg.setStrokeStyle(1, 0x30363d);
    this.harvestUI.add(barBg);

    // Progress bar fill
    const barFill = this.add.rectangle(-94, 10, 0, 10, 0x2ecc71);
    barFill.setOrigin(0, 0.5);
    barFill.setName("harvestFill");
    this.harvestUI.add(barFill);

    // Progress text
    const progressText = this.add.text(0, 10, "0%", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "9px",
      color: "#ffffff",
    });
    progressText.setOrigin(0.5);
    progressText.setName("harvestText");
    this.harvestUI.add(progressText);

    const timeText = this.add.text(0, 28, `${harvestTime.toFixed(1)}s`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "8px",
      color: "#8b949e",
    });
    timeText.setOrigin(0.5);
    timeText.setName("harvestTime");
    this.harvestUI.add(timeText);

    this.harvestUI.setScale(0.92);
    this.harvestUI.setAlpha(0);
    this.tweens.add({
      targets: this.harvestUI,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 140,
      ease: "Quad.easeOut",
    });

    // Animate progress bar
    const startTime = Date.now();
    const durationMs = harvestTime * 1000;
    this.harvestTween = this.tweens.add({
      targets: barFill,
      width: 188,
      duration: durationMs,
      onUpdate: () => {
        // Guard in case UI was cleared mid-tween
        if (!progressText.scene) {
          return;
        }
        const progress = (barFill.width / 188) * 100;
        progressText.setText(`${Math.round(progress)}%`);

        if (timeText.scene) {
          const elapsed = Date.now() - startTime;
          const remainingMs = Math.max(0, durationMs - elapsed);
          timeText.setText(`${(remainingMs / 1000).toFixed(1)}s`);
        }
      },
      onComplete: () => {
        this.harvestTween = null;
        // Auto-complete harvest when bar fills
        if (this.currentHarvestNodeId) {
          this.socket?.emit("resource:completeHarvest", {
            nodeId: this.currentHarvestNodeId,
          });
        }
      },
    });
  }

  /**
   * Hide harvest UI
   */
  private hideHarvestUI(): void {
    // Stop and remove tween first so it doesn't try to draw on destroyed objects
    if (this.harvestTween) {
      this.harvestTween.stop();
      this.harvestTween.remove();
      this.harvestTween = null;
    }

    if (this.harvestUI) {
      this.harvestUI.destroy();
      this.harvestUI = null;
    }
  }

  /**
   * Show harvest reward notification with clean row-based layout
   */
  private showHarvestReward(
    drops: Array<{ itemId: string; quantity: number }>,
    xpGained?: number,
  ): void {
    if (drops.length === 0 && !xpGained) {
      this.showFloatingMessage("No resources gathered.", "#aaaaaa");
      return;
    }

    const { width, height } = this.cameras.main;

    const formatItemName = (itemId: string): string => {
      const raw = itemId.replace(/_/g, " ");
      return raw
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    // Row-based layout like wreck loot panel
    const hasXp = xpGained && xpGained > 0;
    const rowHeight = 36;
    const slotSize = 28;
    const totalRows = drops.length + (hasXp ? 1 : 0);
    const panelWidth = 200;
    const panelHeight = 40 + totalRows * rowHeight;

    const container = this.add.container(width / 2, height - 120);
    container.setScrollFactor(0);
    container.setDepth(200);

    // Background - matching wreck loot panel style
    const bg = this.add.rectangle(
      0,
      0,
      panelWidth,
      panelHeight,
      0x0d1117,
      0.96,
    );
    bg.setStrokeStyle(2, 0x30363d);
    container.add(bg);

    // Title bar
    const titleBar = this.add.rectangle(
      0,
      -panelHeight / 2 + 14,
      panelWidth - 4,
      24,
      0x161b22,
      1,
    );
    titleBar.setStrokeStyle(1, 0x30363d);
    container.add(titleBar);

    const title = this.add.text(0, -panelHeight / 2 + 14, "GATHERED", {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: "10px",
      color: "#2ecc71",
    });
    title.setOrigin(0.5);
    container.add(title);

    // Draw item rows
    const startY = -panelHeight / 2 + 40;
    const leftX = -panelWidth / 2 + 16;

    drops.forEach((drop, i) => {
      const y = startY + i * rowHeight;

      // Row background
      const rowBg = this.add.rectangle(
        0,
        y,
        panelWidth - 12,
        rowHeight - 4,
        0x161b22,
        i % 2 === 0 ? 0.4 : 0.2,
      );
      container.add(rowBg);

      // Item slot background
      const slotBg = this.add.rectangle(
        leftX + slotSize / 2,
        y,
        slotSize,
        slotSize,
        0x161b22,
        1,
      );
      slotBg.setStrokeStyle(1, 0x30363d);
      container.add(slotBg);

      // Draw pixel art icon for the item
      this.drawItemIcon(
        container,
        drop.itemId,
        leftX + slotSize / 2,
        y,
        slotSize - 4,
      );

      // Quantity badge (inventory style - bottom right of slot)
      const qtyText = this.add.text(
        leftX + slotSize - 2,
        y + slotSize / 2 - 4,
        `${drop.quantity}`,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "9px",
          color: "#ffffff",
        },
      );
      qtyText.setOrigin(1, 1);
      qtyText.setStroke("#000000", 2);
      container.add(qtyText);

      // Item name
      const itemName = formatItemName(drop.itemId);
      const nameText = this.add.text(leftX + slotSize + 10, y, itemName, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#c9d1d9",
      });
      nameText.setOrigin(0, 0.5);
      container.add(nameText);
    });

    // XP row if present
    if (hasXp) {
      const y = startY + drops.length * rowHeight;

      // Row background with gold tint
      const rowBg = this.add.rectangle(
        0,
        y,
        panelWidth - 12,
        rowHeight - 4,
        0x1a1a0d,
        0.5,
      );
      container.add(rowBg);

      // XP slot background
      const slotBg = this.add.rectangle(
        leftX + slotSize / 2,
        y,
        slotSize,
        slotSize,
        0x1a1a0d,
        1,
      );
      slotBg.setStrokeStyle(1, 0xffd700);
      container.add(slotBg);

      // XP text icon instead of star
      const xpIcon = this.add.text(leftX + slotSize / 2, y, "XP", {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        fontStyle: "bold",
        color: "#ffd700",
      });
      xpIcon.setOrigin(0.5);
      container.add(xpIcon);

      // XP amount badge (inventory style)
      const qtyText = this.add.text(
        leftX + slotSize - 2,
        y + slotSize / 2 - 4,
        `${xpGained}`,
        {
          fontFamily: '"Sometype Mono", monospace',
          fontSize: "9px",
          color: "#ffd700",
        },
      );
      qtyText.setOrigin(1, 1);
      qtyText.setStroke("#000000", 2);
      container.add(qtyText);

      // XP label
      const nameText = this.add.text(leftX + slotSize + 10, y, "Experience", {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "10px",
        color: "#ffd700",
      });
      nameText.setOrigin(0, 0.5);
      container.add(nameText);
    }

    // Scale in animation
    container.setScale(0);
    this.tweens.add({
      targets: container,
      scaleX: 1,
      scaleY: 1,
      duration: 220,
      ease: "Back.easeOut",
    });

    // Fade out after delay
    this.tweens.add({
      targets: container,
      alpha: 0,
      y: height - 160,
      duration: 400,
      delay: 2800,
      onComplete: () => container.destroy(),
    });

    playSound(SFX_KEYS.FISHING_CATCH);
  }

  /**
   * Draw pixel art icon for an item in a container
   */
  private drawItemIcon(
    container: Phaser.GameObjects.Container,
    itemId: string,
    x: number,
    y: number,
    size: number,
  ): void {
    const graphics = this.add.graphics();

    // Item-specific pixel art icons
    const halfSize = size / 2;

    switch (itemId) {
      case "wood_planks":
        // Brown wooden planks
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(x - halfSize + 4, y - 6, size - 8, 4);
        graphics.fillRect(x - halfSize + 4, y + 2, size - 8, 4);
        graphics.fillStyle(0xa0522d, 1);
        graphics.fillRect(x - halfSize + 6, y - 4, size - 12, 2);
        graphics.fillStyle(0x654321, 1);
        graphics.fillRect(x - halfSize + 8, y - 5, 2, 2);
        graphics.fillRect(x + halfSize - 12, y + 3, 2, 2);
        break;

      case "iron_ore":
        // Gray ore chunk with metallic flecks
        graphics.fillStyle(0x696969, 1);
        graphics.fillRect(x - 8, y - 6, 16, 12);
        graphics.fillStyle(0x808080, 1);
        graphics.fillRect(x - 6, y - 4, 12, 8);
        graphics.fillStyle(0xc0c0c0, 1);
        graphics.fillRect(x - 2, y - 2, 4, 4);
        graphics.fillRect(x + 4, y + 2, 2, 2);
        break;

      case "copper_ore":
        // Orange-brown ore
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(x - 8, y - 6, 16, 12);
        graphics.fillStyle(0xcd7f32, 1);
        graphics.fillRect(x - 6, y - 4, 12, 8);
        graphics.fillStyle(0xdaa520, 1);
        graphics.fillRect(x - 2, y - 2, 4, 4);
        break;

      case "rope":
        // Coiled rope
        graphics.fillStyle(0xdeb887, 1);
        graphics.fillRect(x - 6, y - 8, 4, 16);
        graphics.fillRect(x - 2, y - 6, 4, 12);
        graphics.fillRect(x + 2, y - 4, 4, 8);
        graphics.fillStyle(0xd2b48c, 1);
        graphics.fillRect(x - 4, y - 6, 2, 12);
        break;

      case "cloth":
        // Folded fabric
        graphics.fillStyle(0xf5f5dc, 1);
        graphics.fillRect(x - 10, y - 4, 20, 8);
        graphics.fillStyle(0xfaf0e6, 1);
        graphics.fillRect(x - 8, y - 2, 16, 4);
        graphics.fillStyle(0xe8e4d9, 1);
        graphics.fillRect(x - 6, y, 12, 2);
        break;

      case "seaweed":
        // Green wavy seaweed
        graphics.fillStyle(0x228b22, 1);
        graphics.fillRect(x - 6, y - 10, 3, 20);
        graphics.fillRect(x - 1, y - 8, 3, 16);
        graphics.fillRect(x + 4, y - 10, 3, 20);
        graphics.fillStyle(0x32cd32, 1);
        graphics.fillRect(x - 5, y - 8, 2, 14);
        break;

      case "driftwood":
        // Brown log
        graphics.fillStyle(0x8b7355, 1);
        graphics.fillRect(x - 12, y - 3, 24, 6);
        graphics.fillStyle(0xa0522d, 1);
        graphics.fillRect(x - 10, y - 2, 20, 4);
        graphics.fillStyle(0x654321, 1);
        graphics.fillRect(x - 8, y - 1, 2, 2);
        graphics.fillRect(x + 6, y - 1, 2, 2);
        break;

      default:
        // Generic material icon
        graphics.fillStyle(0x666666, 1);
        graphics.fillRect(x - 8, y - 8, 16, 16);
        graphics.fillStyle(0x888888, 1);
        graphics.fillRect(x - 6, y - 6, 12, 12);
        graphics.fillStyle(0xaaaaaa, 1);
        graphics.fillRect(x - 4, y - 4, 4, 4);
    }

    container.add(graphics);
  }

  // ============ CONSTRUCTION MODE ============

  /**
   * Enter construction mode with a selected kit
   */
  public enterConstructionMode(kitItemId: string, kitName: string): void {
    this.constructionMode.active = true;
    this.constructionMode.selectedKitId = kitItemId;
    this.constructionMode.selectedKitName = kitName;

    // Create preview graphics if not exists
    if (!this.constructionMode.previewGraphics) {
      if (!this.add) {
        console.error("🔨 [Construction] Scene graphics system not ready");
        return;
      }
      this.constructionMode.previewGraphics = this.add.graphics();
      this.constructionMode.previewGraphics.setDepth(1000);
    }

    console.log(
      `🔨 [Construction] Entered construction mode with kit: ${kitItemId}`,
    );

    // Emit event for UI to show construction mode indicator
    this.events.emit("construction:mode_changed", {
      active: true,
      kitId: kitItemId,
      kitName: kitName,
    });
  }

  /**
   * Exit construction mode
   */
  public exitConstructionMode(): void {
    this.constructionMode.active = false;
    this.constructionMode.selectedKitId = null;
    this.constructionMode.selectedKitName = null;

    // Clear preview graphics
    if (this.constructionMode.previewGraphics) {
      this.constructionMode.previewGraphics.clear();
    }

    console.log(`🔨 [Construction] Exited construction mode`);

    // Emit event for UI to hide construction mode indicator
    this.events.emit("construction:mode_changed", { active: false });
  }

  /**
   * Check if construction mode is active
   */
  public isConstructionModeActive(): boolean {
    return this.constructionMode.active;
  }

  /**
   * Get current construction kit info
   */
  public getConstructionKitInfo(): {
    kitId: string | null;
    kitName: string | null;
  } {
    return {
      kitId: this.constructionMode.selectedKitId,
      kitName: this.constructionMode.selectedKitName,
    };
  }

  /**
   * Update construction preview at mouse position
   */
  private updateConstructionPreview(worldX: number, worldY: number): void {
    if (!this.constructionMode.active || !this.constructionMode.previewGraphics)
      return;

    // Snap to tile grid (use TILE_SIZE for grid alignment)
    const tileX = Math.floor(worldX / TILE_SIZE);
    const tileY = Math.floor(worldY / TILE_SIZE);
    const snappedX = tileX * TILE_SIZE;
    const snappedY = tileY * TILE_SIZE;

    // Only update if position changed
    if (
      snappedX === this.constructionMode.lastPreviewX &&
      snappedY === this.constructionMode.lastPreviewY
    ) {
      return;
    }

    this.constructionMode.lastPreviewX = snappedX;
    this.constructionMode.lastPreviewY = snappedY;

    // Check if placement is valid
    const isValid = this.isConstructionPlacementValid(snappedX, snappedY);
    this.constructionMode.isValidPosition = isValid;

    // Draw preview - match the actual rendered tile appearance
    const graphics = this.constructionMode.previewGraphics;
    graphics.clear();

    // Get material color based on kit type
    let materialColor = 0xd2691e; // Default wood color
    if (this.constructionMode.selectedKitId) {
      const materialType = this.constructionMode.selectedKitId.replace(
        "construction_kit_",
        "",
      );
      if (materialType === "stone") {
        materialColor = 0x808080; // Gray
      } else if (materialType === "metal") {
        materialColor = 0xa9a9a9; // Dark gray
      }
    }

    // Draw filled rectangle - very faded preview
    const previewAlpha = isValid ? 0.25 : 0.15;
    graphics.fillStyle(materialColor, previewAlpha);
    graphics.fillRect(snappedX, snappedY, TILE_SIZE, TILE_SIZE);

    // Draw subtle border - only show on valid placement, very faint
    if (isValid) {
      graphics.lineStyle(0.5, 0x888888, 0.2);
      graphics.strokeRect(
        snappedX + 0.5,
        snappedY + 0.5,
        TILE_SIZE - 1,
        TILE_SIZE - 1,
      );
    } else {
      // Invalid placement - very subtle red tint instead of red border
      graphics.lineStyle(0.5, 0xff6b6b, 0.15);
      graphics.strokeRect(
        snappedX + 0.5,
        snappedY + 0.5,
        TILE_SIZE - 1,
        TILE_SIZE - 1,
      );
    }
  }

  /**
   * Draw construction preview icon based on material type
   */
  private drawConstructionPreviewIcon(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    materialType: string,
    isValid: boolean,
  ): void {
    const iconAlpha = isValid ? 0.8 : 0.5;

    switch (materialType) {
      case "wood":
        graphics.fillStyle(0x8b4513, iconAlpha);
        graphics.fillRect(x - 12, y - 12, 24, 24);
        graphics.fillStyle(0xa0522d, iconAlpha);
        graphics.fillRect(x - 10, y - 10, 20, 20);
        // Wood grain lines
        graphics.lineStyle(1, 0x654321, iconAlpha);
        graphics.lineBetween(x - 8, y - 6, x + 8, y - 6);
        graphics.lineBetween(x - 8, y, x + 8, y);
        graphics.lineBetween(x - 8, y + 6, x + 8, y + 6);
        break;

      case "stone":
        graphics.fillStyle(0x808080, iconAlpha);
        graphics.fillRect(x - 12, y - 12, 24, 24);
        graphics.fillStyle(0x696969, iconAlpha);
        graphics.fillRect(x - 10, y - 10, 10, 10);
        graphics.fillRect(x, y, 10, 10);
        graphics.fillStyle(0xa9a9a9, iconAlpha);
        graphics.fillRect(x, y - 10, 10, 10);
        graphics.fillRect(x - 10, y, 10, 10);
        break;

      case "metal":
        graphics.fillStyle(0x4a4a4a, iconAlpha);
        graphics.fillRect(x - 12, y - 12, 24, 24);
        graphics.fillStyle(0x6a6a6a, iconAlpha);
        graphics.fillRect(x - 10, y - 10, 20, 20);
        // Metal rivets
        graphics.fillStyle(0x8a8a8a, iconAlpha);
        graphics.fillCircle(x - 6, y - 6, 2);
        graphics.fillCircle(x + 6, y - 6, 2);
        graphics.fillCircle(x - 6, y + 6, 2);
        graphics.fillCircle(x + 6, y + 6, 2);
        break;

      default:
        graphics.fillStyle(0x666666, iconAlpha);
        graphics.fillRect(x - 10, y - 10, 20, 20);
    }
  }

  /**
   * Check if a position is valid for construction placement
   */
  private isConstructionPlacementValid(
    worldX: number,
    worldY: number,
  ): boolean {
    const tileX = Math.floor(worldX / TILE_SIZE);
    const tileY = Math.floor(worldY / TILE_SIZE);

    // Check if tile is in world bounds
    if (
      tileX < 0 ||
      tileY < 0 ||
      tileX >= WORLD_WIDTH / TILE_SIZE ||
      tileY >= WORLD_HEIGHT / TILE_SIZE
    ) {
      return false;
    }

    // Check if tile is blocked by islands
    if (this.isTileBlockedByLoadedIslands(tileX, tileY)) {
      return false;
    }

    // Server will do final collision validation
    // Client-side check is just for visual feedback

    return true;
  }

  /**
   * Handle construction click to place a tile
   */
  private handleConstructionClick(worldX: number, worldY: number): void {
    if (!this.constructionMode.active || !this.constructionMode.selectedKitId)
      return;

    // Check if placement is valid at the raw world position
    // Server will handle snapping to grid
    if (!this.isConstructionPlacementValid(worldX, worldY)) {
      console.log(
        `🔨 [Construction] Invalid placement at (${worldX}, ${worldY})`,
      );
      this.showFloatingMessage("Cannot place here!", "#ff6666");
      return;
    }

    // Emit construction placement event to server with RAW coordinates
    // Server will snap to grid to ensure consistency
    console.log(
      `🔨 [Construction] Emitting place_tile with raw coords (${worldX}, ${worldY})`,
    );
    if (!this.socket) {
      console.error(`🔨 [Construction] ERROR: Socket is null!`);
      this.showFloatingMessage("Socket not connected!", "#ff6666");
      return;
    }

    // Get material type for optimistic rendering
    const materialType = this.constructionMode.selectedKitId.replace(
      "construction_kit_",
      "",
    );
    const snappedX = Math.floor(worldX / TILE_SIZE) * TILE_SIZE;
    const snappedY = Math.floor(worldY / TILE_SIZE) * TILE_SIZE;
    const tempConstructionId = `temp_${Date.now()}`;

    // Optimistically render the tile immediately
    console.log(
      `🔨 [Construction] Optimistically rendering ${materialType} tile at (${snappedX}, ${snappedY})`,
    );
    const placedTileRenderer = this.placedTileRenderer;
    if (
      placedTileRenderer &&
      (placedTileRenderer as any).renderSingleConstructionTile
    ) {
      (placedTileRenderer as any).renderSingleConstructionTile(
        snappedX,
        snappedY,
        materialType,
        tempConstructionId,
      );
    }

    this.socket.emit(
      "construction:place_tile",
      {
        kitItemId: this.constructionMode.selectedKitId,
        worldX: snappedX,
        worldY: snappedY,
      },
      (response: {
        success: boolean;
        message: string;
        constructionId?: string;
        worldX?: number;
        worldY?: number;
        materialType?: string;
      }) => {
        console.log(`🔨 [Construction] Placement result:`, response);

        if (response.success) {
          this.showFloatingMessage("Tile placed!", "#66ff66");

          // Replace temp tile with real one if coordinates match
          if (
            response.worldX === snappedX &&
            response.worldY === snappedY &&
            placedTileRenderer &&
            (placedTileRenderer as any).updateConstructionTileId
          ) {
            (placedTileRenderer as any).updateConstructionTileId(
              snappedX,
              snappedY,
              tempConstructionId,
              response.constructionId!,
            );
            console.log(
              `🔨 [Construction] Updated temp tile to real ID: ${response.constructionId}`,
            );
          }

          // Stay in construction mode so player can place more tiles
          // Player can press ESC to exit when done
        } else {
          // Rollback optimistic rendering on failure
          this.showFloatingMessage(
            response.message || "Placement failed",
            "#ff6666",
          );
          if (
            placedTileRenderer &&
            (placedTileRenderer as any).removeSingleConstructionTile
          ) {
            (placedTileRenderer as any).removeSingleConstructionTile(
              snappedX,
              snappedY,
              tempConstructionId,
            );
            console.log(
              `🔨 [Construction] Rolled back optimistic tile at (${snappedX}, ${snappedY})`,
            );
          }
        }
      },
    );
  }

  // ============ SIGNATURE MOVE CAST MODE ============

  /**
   * Enter signature move cast mode
   */
  public enterSignatureCastMode(
    moveId: string,
    moveName: string,
    moveColor: string,
    aoeRadius: number,
  ): void {
    this.signatureCastMode.active = true;
    this.signatureCastMode.moveId = moveId;
    this.signatureCastMode.moveName = moveName;
    this.signatureCastMode.moveColor = moveColor;
    this.signatureCastMode.aoeRadius = aoeRadius;

    // Create preview graphics if not exists
    if (!this.signatureCastMode.previewGraphics) {
      if (!this.add) {
        console.error("⚡ [Signature] Scene graphics system not ready");
        return;
      }
      this.signatureCastMode.previewGraphics = this.add.graphics();
      this.signatureCastMode.previewGraphics.setDepth(1000);
    }

    console.log(`⚡ [Wildcard] Entered cast mode for: ${moveName} (${moveId})`);

    // Emit event for UI to show cast mode indicator
    this.events.emit("wildcard:cast_mode_changed", {
      active: true,
      moveId: moveId,
      moveName: moveName,
      moveColor: moveColor,
      aoeRadius: aoeRadius,
    });
  }

  /**
   * Exit signature move cast mode
   */
  public exitSignatureCastMode(): void {
    this.signatureCastMode.active = false;
    this.signatureCastMode.moveId = null;
    this.signatureCastMode.moveName = null;
    this.signatureCastMode.moveColor = null;
    this.signatureCastMode.aoeRadius = 0;

    // Clear preview graphics
    if (this.signatureCastMode.previewGraphics) {
      this.signatureCastMode.previewGraphics.clear();
    }

    // Restore default cursor
    this.input.setDefaultCursor("auto");

    console.log(`⚡ [Wildcard] Exited cast mode`);

    // Emit event for UI to hide cast mode indicator
    this.events.emit("wildcard:cast_mode_changed", { active: false });
  }

  /**
   * Check if signature cast mode is active
   */
  public isSignatureCastModeActive(): boolean {
    return this.signatureCastMode.active;
  }

  /**
   * Get current signature move info
   */
  public getSignatureCastMoveInfo(): {
    moveId: string | null;
    moveName: string | null;
    moveColor: string | null;
    aoeRadius: number;
  } {
    return {
      moveId: this.signatureCastMode.moveId,
      moveName: this.signatureCastMode.moveName,
      moveColor: this.signatureCastMode.moveColor,
      aoeRadius: this.signatureCastMode.aoeRadius,
    };
  }

  /**
   * Set cannon focus active state (called by GameCanvas when minigame is opened/closed)
   */
  public setCannonFocusActive(active: boolean): void {
    this.cannonFocusActive = active;
  }

  /**
   * Update signature move preview at mouse position
   */
  private updateSignatureCastPreview(worldX: number, worldY: number): void {
    if (
      !this.signatureCastMode.active ||
      !this.signatureCastMode.previewGraphics
    )
      return;

    // Only update if position changed significantly
    if (
      Math.abs(worldX - this.signatureCastMode.lastPreviewX) < 8 &&
      Math.abs(worldY - this.signatureCastMode.lastPreviewY) < 8
    ) {
      return;
    }

    this.signatureCastMode.lastPreviewX = worldX;
    this.signatureCastMode.lastPreviewY = worldY;

    const graphics = this.signatureCastMode.previewGraphics;
    graphics.clear();

    // Draw AOE circle preview
    const aoeRadiusPx = this.signatureCastMode.aoeRadius * 64; // Convert tiles to pixels

    // Outer circle (semi-transparent)
    graphics.lineStyle(
      2,
      this.hexToNumber(this.signatureCastMode.moveColor || "#4fc3f7"),
      0.6,
    );
    graphics.strokeCircle(worldX, worldY, aoeRadiusPx);

    // Inner circle (more opaque)
    graphics.lineStyle(
      1,
      this.hexToNumber(this.signatureCastMode.moveColor || "#4fc3f7"),
      0.3,
    );
    graphics.strokeCircle(worldX, worldY, aoeRadiusPx - 4);

    // Center point
    graphics.fillStyle(
      this.hexToNumber(this.signatureCastMode.moveColor || "#4fc3f7"),
      0.8,
    );
    graphics.fillCircle(worldX, worldY, 4);

    // Crosshair
    graphics.lineStyle(
      1,
      this.hexToNumber(this.signatureCastMode.moveColor || "#4fc3f7"),
      0.5,
    );
    graphics.lineBetween(worldX - 12, worldY, worldX + 12, worldY);
    graphics.lineBetween(worldX, worldY - 12, worldX, worldY + 12);
  }

  /**
   * Handle signature move cast click
   */
  private handleSignatureCastClick(worldX: number, worldY: number): void {
    if (!this.signatureCastMode.active || !this.signatureCastMode.moveId)
      return;

    console.log(
      `⚡ [Signature] Cast ${this.signatureCastMode.moveName} at (${worldX}, ${worldY})`,
    );

    if (!this.socket) {
      console.error(`⚡ [Signature] ERROR: Socket is null!`);
      this.showFloatingMessage("Socket not connected!", "#ff6666");
      return;
    }

    // Save values before exiting cast mode (callback will use these)
    const moveId = this.signatureCastMode.moveId;
    const moveName = this.signatureCastMode.moveName || "Signature Move";
    const moveColor = this.signatureCastMode.moveColor || "#4fc3f7";

    // Exit cast mode BEFORE emitting to server (prevents double-casting)
    this.exitSignatureCastMode();

    // Emit wildcard cast event to server
    this.socket.emit(
      "wildcard:cast",
      {
        moveId: moveId,
        targetX: worldX,
        targetY: worldY,
      },
      (response: { success: boolean; message?: string }) => {
        if (response.success) {
          this.showFloatingMessage(`${moveName} cast!`, moveColor);
          console.log(`⚡ [Signature] Cast successful`);
        } else {
          this.showFloatingMessage(
            response.message || "Cast failed",
            "#ff6666",
          );
          console.log(`⚡ [Signature] Cast failed:`, response.message);
        }
      },
    );
  }

  /**
   * Helper to convert hex color to number
   */
  private hexToNumber(hex: string): number {
    return parseInt(hex.replace("#", ""), 16);
  }

  // Loot container handlers
  private spawnLootContainerMarker(
    id: string,
    x: number,
    y: number,
    isPrivate: boolean,
  ): void {
    // If in sanctum, don't render here - sanctum containers are handled by SanctumRenderer
    // They get rendered when the sanctum state updates
    if (this.isInSanctumInstance) {
      console.log(
        `🏛️ Loot container ${id} created in sanctum at (${x}, ${y}) - will be rendered by SanctumRenderer`,
      );
      return;
    }

    // Ocean loot container - render as wreck marker
    // Create a container for the loot marker
    const container = this.add.container(x, y);

    // Draw a treasure chest icon (simple square with border)
    const graphics = this.add.graphics();
    graphics.fillStyle(0xffd700, 1); // Gold fill
    graphics.fillRect(-4, -4, 8, 8);
    graphics.lineStyle(1, 0xffaa00, 1); // Orange border
    graphics.strokeRect(-4, -4, 8, 8);
    graphics.setPosition(0, 0);

    container.add(graphics);

    // Add a glow effect
    const glow = this.add.graphics();
    glow.lineStyle(1, 0xffd700, 0.5);
    glow.strokeRect(-6, -6, 12, 12);
    glow.setPosition(0, 0);
    container.add(glow);

    // Make it interactive - position relative to container at (0, 0)
    const hitArea = this.add.zone(0, 0, 32, 32);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();

        if (pointer.leftButtonDown()) {
          // Left-click: Open loot panel (same as context menu "Open Loot")
          console.log(
            `📦 Left-click on loot container: ${id}, opening loot panel`,
          );
          // Trigger the standard loot container open flow
          this.game.events.emit("lootcontainer:open", { id: id });
          // Request the container data from server
          if (this.socket) {
            this.socket.emit("lootcontainer:open", { id: id });
          }
        } else if (pointer.rightButtonDown()) {
          // Right-click: Show context menu
          console.log(
            `📦 Right-click on loot container: ${id}, showing context menu`,
          );
          const camera = this.cameras.main;
          const screenX = (x - camera.scrollX) * camera.zoom;
          const screenY = (y - camera.scrollY) * camera.zoom;

          this.events.emit("shipwreck:contextmenu", {
            wreckId: id,
            worldX: x,
            worldY: y,
            screenX: screenX,
            screenY: screenY,
            lootQuality: isPrivate ? "private" : "public",
          });
        }
      },
    );

    // Store reference
    this.lootContainerMarkers.set(id, container);
    container.setDepth(5);
  }

  private removeLootContainerMarker(id: string): void {
    const marker = this.lootContainerMarkers.get(id);
    if (marker) {
      marker.destroy();
      this.lootContainerMarkers.delete(id);
    }
  }
}
